import "./index.css";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import { Copy, Zap, Gem, Rocket, Package, Crown, Check, Lock, LayoutDashboard } from "lucide-react";
import { cn, tableTheme as T, analysisTone } from "./components/ui/analysisTableTheme.js";
import { useQueryClient } from '@tanstack/react-query'; // ✅ FIX: React Query Cache-Invalidierung
import { apiUrl } from './utils/apiBase';
import { fetchDatasetSnapshotWire } from './utils/snapshotWire.js';
import {
  fetchManualOverrideSummary,
  resetManualOverrides,
} from "./api.js";
import { saveNftTransformFmvOverrides } from "./utils/nftTransformFmvOverrides.js";
import {
  confirmResetManualOverrides,
  manualOverrideActiveLabel,
  manualOverridePreservedToast,
  manualOverrideResetSuccessToast,
} from "./utils/manualOverrideUi.js";
import {
  missingAnalysisContextMessage,
  resolveDatasetAnalysisContext,
} from "./utils/analysisContext.js";
import {
  readAdminReplayLaunch,
  isAdminReplayUrl,
  clearAdminReplayLaunch,
  getAdminReplayAutorunMode,
  readPreviewExchangeImports,
  stripAdminReplayAutorunParams,
} from "./utils/adminReplayLaunch.js";
import {
  buildTxOverrideKey,
  buildManualOverridePayload,
  buildManualClassificationMapFromResult,
  pickManualClassification,
} from "./utils/txOverrideKey.js";
import { validateManualOverrideClassificationForUi } from "./utils/manualOverrideUi.js";
import BalanceVerifierMulti from "./components/BalanceVerifierMulti";
import LanguageSwitcher from "./components/LanguageSwitcher";
import LoginComponent from "./components/LoginComponent";
import { useLanguage } from "./contexts/LanguageContext.jsx";
import { useAuth } from "./contexts/AuthContext.jsx";
import { t } from "./utils/translations";
import { APP_VERSION_LABEL } from "./appVersion";
import desktopLogoCopy from "./assets/brand/desktop-logo-copy.png";
import analysisHeader from "./assets/brand/analysis-header.png";
import { V3TAX_MOBILE_LOGO_PATH } from "./constants/v3taxBranding.js";
import { shouldUseMobileBrandHeroLogo } from "./utils/heroBrandLogo.js";

// 🔧 ERWEITERTE TOKEN-UNTERSTÜTZUNG: Alle unterstützten Token für Swap-Klassifizierung und NFT Mints
const supportedTokensForSwaps = [
  "VET", "VTHO", "USDGLO", "HAI", "B3TR", "VOT3", "SHA", "VNS", "BVET", "YEET", 
  "VEB3TR", "WOV", "COJ", "VVET", "SHT", "BTC", "ETH", "SOL", "USDC", "USDT", 
  "WAN", "XRP", "VEED", "MVA", "VEUSD", "WVET", "VSEA", "OCE", "VPU", "GOLD", 
  "JUR", "MVG", "VFA", "FCWS", "VEX", "LGCT", "DHN", "PLA", "PPR", "UNION", 
  "DRAGON", "BANANA", "VST", "DBET", "EHRT", "SNK", "TIC", "GEMS", "AQD", "MDN", 
  "BAG", "LION", "SASS", "BANGZ", "BVC", "PLG", "SQUAD", "MILK", "RAIN", "VSC", 
  "UNO", "DWVET", "MVC", "3DT", "USDV", "LLAMA", "RATV"
];

// import VetVthoTransactionTable from "./components/VetVthoTransactionTable"; // ✅ ENTFERNT: Ersetzt durch RawTransfersTable
// import Vip180TokenTable from "./components/Vip180TokenTable"; // ✅ ENTFERNT: Ersetzt durch RawTransfersTable
import RawTransfersTable from "./components/RawTransfersTable";
import { shouldDeferExportPrep } from "./utils/deferredExportPrep.js";
import { uiPerfLog } from "./utils/uiPerfLog.js";
import { installScrollPerfProbe } from "./utils/scrollPerfProbe.js";
import VetTaxTable from "./components/VetTaxTable";
import VthoTaxTable from "./components/VthoTaxTable";
import B3trTaxTable from "./components/B3trTaxTable";
import LiquidityTaxTable from "./components/LiquidityTaxTable.jsx";
import NftTaxTable from "./components/NftTaxTable";
import TotalIncomeSummary from "./components/TotalIncomeSummary";
import ExportAllSection from "./components/ExportAllSection";
import MultiVthoOverview from "./components/MultiVthoOverview";
import Dashboard from "./components/Dashboard";
import Footer from "./components/Footer";
import MyWalletsDashboard from "./components/MyWalletsDashboard";
import VeWorldLoadingOverlay from "./components/VeWorldLoadingOverlay";
import AnalysisCrashBoundary from "./components/AnalysisCrashBoundary";
import AnalysisFailureModal, {
  buildAnalysisSupportDescription,
} from "./components/AnalysisFailureModal";
import ExchangeImportsTable from "./components/ExchangeImportsTable";
import LegalNoticesModal from "./components/LegalNoticesModal";
import TaxReportPrint from "./print/TaxReportPrint";
import HelpSupportModal from "./components/HelpSupportModal";
import MaskedEurCell from "./components/MaskedEurCell";
import {
  pickSectionTotals,
  resolveAnalysisCountry,
  resolveAnalysisDataset,
} from "./utils/fiatFields";
import {
  translateBackendJobDetailToGerman,
  translateBackendJobDetailToEnglish,
} from "./utils/analysisJobDetailI18n";

// 🚀 ENTFERNT: Alle Client-Analyse/FIFO Imports - wurden ins Backend transferiert
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { snapshotAPI } from "./api.js";
import { switchAccount, disconnectEverywhere, getSignClient, findExistingSession } from "./utils/walletconnectClient";
import { getPlanInfo } from "./utils/planUtils"; // 🆕 NEU: Plan-Utilities importieren
import { classifyStargateDeposit } from './utils/classifyStargateDeposits';
import { classifyStargateStakingRewardsInList } from './utils/classifyStargateStakingRewards';
import { parseDateToTimestamp } from './utils/fifoUtils';
import {
  extractEventIdentityFields,
  formatCsvDateTimeUtc,
} from "./utils/exportFormatting.js";
import { buildCanonicalEventKeyFromRow } from "../shared/canonicalEventKey.js";

const analysisShell =
  "relative w-full overflow-visible " +
  "before:pointer-events-none before:absolute before:inset-0 " +
  "before:bg-[radial-gradient(ellipse_120%_80%_at_100%_-20%,rgba(99,102,241,0.06),transparent_52%)] " +
  "after:pointer-events-none after:absolute after:inset-0 " +
  "after:bg-[radial-gradient(ellipse_80%_60%_at_0%_100%,rgba(16,185,129,0.05),transparent_46%)]";

const analysisContent =
  "v3-shell analysis-content-shell relative z-10 pt-8 pb-8 sm:pt-10 lg:pt-12 lg:pb-10";

const analysisStack = "w-full min-w-0 space-y-5 lg:space-y-6 analysis-live-sections";

/** Ergebnisansicht: großer Logo-/Slogan-Hero ausblenden (Fokus auf TotalIncomeSummary + Tabellen). */
const showAnalysisHero = false;

// 🚀 BEHALTEN: Nur noch UI-relevante Imports
// - preloadPriceMapFromBackend (falls Preise noch im Frontend gebraucht werden)
// - getTokenPriceEUR (falls Preise noch im Frontend gebraucht werden)
// - sanitizeTokenName (falls noch in der UI gebraucht)

// 🚫 EINFACHE ERSATZ-FUNKTION für sanitizeTokenName
const sanitizeTokenName = (token) => {
  return (token || "").toString().toUpperCase();
};

function toRawExportTimestamp(value) {
  const n = Number(value);

  if (!Number.isFinite(n) || n <= 0) {
    return 0;
  }

  return n > 1e12
    ? Math.floor(n / 1000)
    : Math.floor(n);
}

/**
 * Bewahrt die vollständige On-Chain-Event-Identität vor dem
 * Classified-Ledger-Export-Prep.
 *
 * Unterschiedliche Events derselben Transaktion dürfen nicht nur wegen
 * identischem Hash, Token, Betrag und Typ zusammengeführt werden.
 */
function buildRawLedgerExportRow(
  tx = {},
  manualClassifications = {},
  seq = 0
) {
  const identity = extractEventIdentityFields(tx);

  const timestamp = toRawExportTimestamp(
    tx.timestamp ??
      tx.block_timestamp ??
      tx.blockTimestamp ??
      tx.ts ??
      0
  );

  const type =
    pickManualClassification(
      manualClassifications,
      tx
    ) ||
    tx.classification ||
    tx.type ||
    "";

  const token =
    tx.token ||
    tx.token_symbol ||
    tx.tokenSymbol ||
    tx.asset ||
    "";

  const hash =
    tx.hash ||
    tx.txid ||
    tx.transactionHash ||
    "";

  const canonicalEventKey =
    identity.canonical_event_key ||
    buildCanonicalEventKeyFromRow(
      {
        ...tx,
        hash,
        classification: type,
        token,
      },
      {
        seq,
        kind: type || "raw_transfer",
      }
    );

  const hasStructuredIdentity =
    identity.clause_index != null ||
    identity.event_index != null ||
    identity.log_index != null;

  const raw = tx.raw_json ?? tx.rawJson ?? {};

  const legacyEventRef =
    tx.eventRef ??
    tx.event_id ??
    tx.eventId ??
    tx.logIndex ??
    tx.log_index ??
    tx.transferIndex ??
    tx.transfer_index ??
    tx.clauseIndex ??
    tx.clause_index ??
    tx.eventIndex ??
    tx.event_index ??
    tx.subIndex ??
    tx.sub_index ??
    tx.idx ??
    tx.index ??
    raw.event_index ??
    raw.eventIndex ??
    raw.log_index ??
    raw.logIndex ??
    raw.clause_index ??
    raw.clauseIndex ??
    "";

  return {
    type,
    classification: type,

    token,

    amount:
      tx.amount ??
      tx.value ??
      tx.quantity ??
      tx.qty ??
      "",

    recipient:
      tx.to ||
      tx.recipient ||
      tx.receiver ||
      "",

    sender:
      tx.from ||
      tx.sender ||
      "",

    hash,

    /*
     * Maschinenlesbaren Timestamp erhalten.
     * ExportAllSection formatiert daraus später ein stabiles UTC-Datum.
     */
    timestamp,
    block_timestamp: timestamp,

    /*
     * Vollständige Event-Identität für CSV-Spalten und Dedupe.
     */
    clause_index: identity.clause_index,
    event_index: identity.event_index,
    log_index: identity.log_index,
    token_contract: identity.token_contract,

    canonical_event_key:
      canonicalEventKey || null,

    disposal_event_key:
      identity.disposal_event_key,

    fifo_leg_index:
      identity.fifo_leg_index,

    /*
     * Custody-Metadaten für den Classified-Ledger-Export erhalten.
     *
     * Diese Felder werden nicht zur Steuerberechnung im Frontend verwendet.
     * Sie verhindern lediglich, dass der Export später die Custody aus
     * Sender/Empfänger erraten muss.
     */
    custodyLocationId:
      tx.custodyLocationId ??
      tx.custody_location_id ??
      null,

    sourceCustodyLocationId:
      tx.sourceCustodyLocationId ??
      tx.source_custody_location_id ??
      null,

    targetCustodyLocationId:
      tx.targetCustodyLocationId ??
      tx.target_custody_location_id ??
      null,

    wallet:
      tx.wallet ??
      tx.walletAddress ??
      tx.wallet_address ??
      null,

    mainWallet:
      tx.mainWallet ??
      tx.main_wallet ??
      null,

    from:
      tx.from ||
      tx.sender ||
      "",

    to:
      tx.to ||
      tx.recipient ||
      tx.receiver ||
      "",

    raw_json:
      tx.raw_json ??
      tx.rawJson ??
      null,

    /*
     * Bei strukturierten Chain-Events den kanonischen Key als Event-Referenz
     * verwenden. Für alte Daten bleibt die bisherige Fallback-Kette bestehen.
     */
    eventRef:
      tx.eventRef ??
      (
        hasStructuredIdentity
          ? canonicalEventKey
          : legacyEventRef
      ),

    date: timestamp
      ? formatCsvDateTimeUtc(timestamp)
      : String(tx.date || ""),
  };
}

// 🔎 Normalisiert Plan-Strings ("Ultimate", "ultimate", "Ultimate Plan" etc.) → stabile IDs
const normalizePlanId = (raw) => {
  const s = (raw ?? "").toString().trim().toLowerCase();
  if (!s) return "free";
  if (s === "dev" || s.includes("dev") || s.includes("admin")) return "dev";
  if (s.includes("ultimate")) return "ultimate";
  if (s.includes("elite")) return "elite";
  if (s.includes("pro")) return "pro";
  if (s.includes("standard")) return "standard";
  if (s.includes("free")) return "free";
  return s;
};

function normalizeUiCountryCode(raw) {
  const c = String(raw || "").trim().toUpperCase().replace(/\s+/g, "_");
  if (!c) return "DE";
  if (c === "GB" || c === "UNITED_KINGDOM" || c === "UNITED KINGDOM") return "UK";
  return c;
}

function resolveExplicitRouteCountry(location) {
  const q = new URLSearchParams(location?.search || "").get("country");
  const raw = location?.state?.selectedCountry ?? q;
  if (raw == null || raw === "") return null;
  return normalizeUiCountryCode(raw);
}

// 🧪 LockedSection Component für Preview-Mode
function LockedSection({
  title,
  subtitle,
  maskedValue,
  language,
  currencySymbol = "€",
  currencyCode = "EUR",
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
          </div>
          <MaskedEurCell
            value={maskedValue || `${currencySymbol} **.**`}
            dense
            currencySymbol={currencySymbol}
            currencyCode={currencyCode}
            language={language}
          />
        </div>

        {/* Fake table blur */}
        <div className="mt-4 space-y-2">
          <div className="h-8 rounded-lg bg-slate-100" />
          <div className="h-8 rounded-lg bg-slate-100" />
          <div className="h-8 rounded-lg bg-slate-100" />
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 backdrop-blur-[2px]" />
      <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-sky-400 via-indigo-400 to-violet-400" />
    </div>
  );
}

// 🧪 Preview: Wenn eine TaxTable im Preview crasht (weil EUR-Felder fehlen), zeigen wir Fallback statt "white screen"
class PreviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
      }
  render() {
    if (this.state.hasError) return this.props.fallback || null;
    return this.props.children;
  }
}

function PreviewSectionHeader({
  title,
  subtitle,
  maskedValue,
  metaRight,
  language = "de",
  currencySymbol = "€",
  currencyCode = "EUR",
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        {subtitle ? <div className="mt-1 text-xs text-slate-500">{subtitle}</div> : null}
      </div>

      <div className="flex flex-col items-end gap-1">
        <MaskedEurCell
          value={maskedValue || `${currencySymbol} **.**`}
          dense
          currencySymbol={currencySymbol}
          currencyCode={currencyCode}
          language={language}
        />
        {metaRight ? <div className="text-[11px] text-slate-500">{metaRight}</div> : null}
      </div>
    </div>
  );
}

function PreviewRowsFallback({ rows = [], language }) {
  const list = Array.isArray(rows) ? rows : [];
  const shown = list.slice(0, 50);

  const fmtDate = (x) => {
    const ts = x?.timestamp ?? x?.block_timestamp ?? x?.ts ?? 0;
    const n = Number(ts);
    if (!Number.isFinite(n) || n <= 0) return "—";
    const ms = n > 1e12 ? n : n * 1000;
    return new Date(ms).toLocaleString();
  };

  const short = (s) => {
    const v = String(s || "");
    if (!v) return "—";
    return v.length > 14 ? `${v.slice(0, 10)}…${v.slice(-4)}` : v;
  };

  return (
    <div className={T.shell}>
      <div className={cn(T.header, T.headerRail, analysisTone.indigo.rail)}>
        <div className="pl-4">
          <span className={T.eyebrow}>
            <LayoutDashboard className="h-3.5 w-3.5 text-indigo-600" />
            {language === "en" ? "Preview fallback" : "Preview-Fallback"}
          </span>
          <h3 className={T.title}>
            {language === "en" ? "Limited preview table" : "Eingeschränkte Vorschau-Tabelle"}
          </h3>
          <p className={T.subtitle}>
            {language === "en"
              ? "EUR and FIFO details are hidden in preview mode."
              : "EUR- und FIFO-Details sind im Vorschau-Modus ausgeblendet."}
          </p>
        </div>
      </div>

      <div className={T.tableBlock}>
        <div className={T.tableWrap}>
          <table className={T.table}>
            <thead className={cn(T.thead, "v3-primary-table-head v3-primary-table-head--indigo")}>
              <tr>
                <th className={T.th}>{language === "en" ? "Date" : "Datum"}</th>
                <th className={T.th}>{language === "en" ? "Type" : "Typ"}</th>
                <th className={T.th}>Token</th>
                <th className={T.th}>{language === "en" ? "Amount" : "Menge"}</th>
                <th className={T.th}>Hash</th>
              </tr>
            </thead>
            <tbody className={T.tbody}>
              {shown.map((r, i) => (
                <tr key={r.hash || r.txid || i} className={T.tr}>
                  <td className={T.td}>{fmtDate(r)}</td>
                  <td className={T.td}>{r.classification || r.type || "—"}</td>
                  <td className={T.td}>{String(r.token || "—")}</td>
                  <td className={cn(T.td, T.mono)}>{String(r.amount ?? r.value ?? "—")}</td>
                  <td className={cn(T.td, "font-mono text-slate-600")}>{short(r.hash || r.txid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {list.length > 50 ? (
        <div className="px-6 py-3 border-t border-slate-200/80 text-xs text-slate-500">
          {language === "en"
            ? `Showing 50 of ${list.length} rows in preview fallback.`
            : `Zeige 50 von ${list.length} Zeilen im Preview-Fallback.`}
        </div>
      ) : null}
    </div>
  );
}

// ✅ Re-usable Plan Badge Icon (Free/Standard/Pro/Elite/Ultimate) – konsistent & "premium"
const PlanBadge = ({ planId, size = "sm", variant = "hero", className = "", title }) => {
  const id = normalizePlanId(planId);

  const styles = {
    // Free = ✅ grüner Haken
    free: {
      Icon: Check,
      bg: "from-emerald-500 via-green-600 to-lime-500",
      ring: "ring-emerald-200/70",
      glow: "from-emerald-200/60 via-lime-200/50 to-green-300/60",
    },

    // Standard = Karton
    standard: {
      Icon: Package,
      bg: "from-slate-500 via-slate-600 to-sky-600",
      ring: "ring-slate-200/60",
      glow: "from-slate-300/40 via-sky-300/40 to-blue-400/40",
    },

    // Pro = Blitz (lila)
    pro: {
      Icon: Zap,
      // VeChain-ish: blau/purple, dunkler, kein Pink
      bg: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
      ring: "ring-indigo-200/60",
      glow: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
    },

    // Elite = Diamant (gelb)
    elite: {
      Icon: Gem,
      bg: "from-amber-400 via-yellow-500 to-orange-500",
      ring: "ring-amber-200/70",
      glow: "from-amber-200/60 via-yellow-200/55 to-orange-200/55",
    },

    // Ultimate = Rakete (rot)
    ultimate: {
      Icon: Rocket,
      bg: "from-red-500 via-rose-600 to-orange-500",
      ring: "ring-rose-200/60",
      glow: "from-red-300/55 via-rose-300/50 to-orange-300/50",
    },

    // Dev
    dev: {
      Icon: Crown,
      bg: "from-blue-600 via-indigo-600 to-violet-700",
      ring: "ring-blue-200/60",
      glow: "from-blue-300/50 via-indigo-300/45 to-violet-400/50",
    },
  };

  const cfg = styles[id] || styles.free;

  const sizes = {
    // 🔥 für Header: kleiner & cleaner
    xxs: { wrap: "w-4 h-4 rounded-md", icon: "w-2.5 h-2.5", sparkle: false },
    xs:  { wrap: "w-5 h-5 rounded-lg", icon: "w-3 h-3", sparkle: false },

    // Standard UI
    sm:  { wrap: "w-8 h-8 rounded-xl", icon: "w-4 h-4", sparkle: true,  sparklePos: "top-1.5 right-1.5", sparkleClass: "w-2 h-2" },
    lg:  { wrap: "w-16 h-16 rounded-2xl", icon: "w-8 h-8", sparkle: true, sparklePos: "top-2 right-2", sparkleClass: "w-2.5 h-2.5" },
  };

  const s = sizes[size] || sizes.sm;
  const Icon = cfg.Icon;

  const compact = variant === "compact";

  return (
    <div
      title={title}
      className={[
        "relative inline-flex items-center justify-center overflow-hidden group",
        s.wrap,
        "bg-gradient-to-br",
        cfg.bg,

        // compact: dezent im Header / hero: fett im Dropdown
        compact ? "border border-white/20 shadow-sm" : `ring-1 ${cfg.ring} shadow-lg`,
        "transform-gpu transition-all duration-300",
        compact ? "" : "hover:shadow-xl hover:scale-[1.03]",
        className,
      ].join(" ")}
    >
      {/* Outer glow (nur hero) */}
      {!compact && (
        <div className={`absolute inset-0 bg-gradient-to-br ${cfg.glow} opacity-40 blur-lg`} />
      )}

      {/* Inner highlight + Depth */}
      <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/35 to-transparent opacity-40" />
      <div className="absolute inset-0 rounded-[inherit] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.25)]" />

      {/* Shine sweep (nur hero) */}
      {!compact && (
        <div className="absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/20 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />
      )}

      {/* Icon */}
      <Icon className={`${s.icon} text-white drop-shadow-sm relative z-10`} />
    </div>
  );
};

const MATCH_TOLERANCE_SECONDS = 10;

// 🚀 ENTFERNT: debugUnmatchedNfts Funktion - wurde ins Backend transferiert

function assertUsableAnalysisResult(result) {
  if (!result || typeof result !== "object") {
    throw new Error("Analysis result is empty or invalid");
  }

  if (result.success === false || result.ok === false || result.error) {
    throw new Error(
      result.error?.message ||
        result.error ||
        "Backend returned an analysis error"
    );
  }

  const ds =
    result?.analysisResult?.dataset ??
    result?.dataset ??
    result?.processedData ??
    null;

  if (!ds || typeof ds !== "object") {
    throw new Error("Analysis result does not contain a valid dataset payload");
  }

  return ds;
}

function App() {
  // 🌐 Sprachverwaltung
  const { language, changeLanguage } = useLanguage();
  
  // 🔐 Auth-Verwaltung
  const { user, isAuthenticated, isLoading: authLoading, checkPlanAccess, logout, checkAuthStatus, source, setSource, latestPlan } = useAuth(); // 🆕 NEU: latestPlan hinzugefügt
  
  // 🧭 Navigation
  const navigate = useNavigate();
  const { datasetId: actualDatasetId } = useParams();
  
  // 🚨 KORRIGIERT: Verhindere doppelte Ausführung durch useEffect/Strict-Mode
  const analysisStartedRef = useRef(false);
  const autorunConsumedRef = useRef(false);
  const analysisFailedRef = useRef(false);
  const lastAnalysisTimeRef = useRef(0);
  const backendDataRef = useRef(null);
  
  // 🚀 NEU: Notification für Dashboard nach erfolgreicher Berechnung
  const calcNotifiedRef = useRef(false);

  // Helfer zum Notifizieren
  const notifyCalculationDone = (datasetId) => {
    const ts = new Date().toISOString();
    // 1) lokales Signal (triggert das Dashboard im selben Tab)
    window.dispatchEvent(new CustomEvent('analysis:calculationCompleted', {
      detail: { datasetId, ts }
    }));
    // 2) optional: localStorage für andere Tabs/Fenster
    localStorage.setItem(`v3tax:lastCalculatedAt:${datasetId}`, ts);
    
      };
  

  const [vetTxs, setVetTxs] = useState([]);
  const [vthoTxs, setVthoTxs] = useState([]);
  const [b3trTxs, setB3trTxs] = useState([]);
  const [nftTxs, setNftTxs] = useState([]);

  // 🎯 VEREINFACHT: Nur ein Edit-Modus State
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
const [showExportHint, setShowExportHint] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isTaxReportPrintMounted, setIsTaxReportPrintMounted] = useState(false);
  const showScrollToTopRef = useRef(false);
  const scrollRafRef = useRef(null);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpActiveSection, setHelpActiveSection] = useState('overview');
  const [showMyWalletsDashboard, setShowMyWalletsDashboard] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [supportForm, setSupportForm] = useState({ category: '' });

  const handleHelpModalClose = useCallback(() => {
    const closeHandler =
      typeof window !== 'undefined' ? window._supportModalCloseHandler : null;

    if (typeof closeHandler === 'function') {
      closeHandler();
      return;
    }

    setShowHelpModal(false);
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        window._supportModalCloseHandler = null;
      }
    };
  }, []);

  useEffect(() => {
    return installScrollPerfProbe("AnalysisApp");
  }, []);

  // 🚀 NEU: Multi-Wallet State für VTHO Overview
  const [walletSet, setWalletSet] = useState({
    addresses: [],
    mainAddresses: [],
    delegateAddresses: []
  });

  // 📋 Copy helper with toast
  const copyAddress = async (text) => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      toast.success(t('toast.addressCopied', language));
    } catch (err) {
      toast.error(t('toast.copyFailed', language));
    }
  };
  
// Close dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (event) => {
    if (showDropdown && !event.target.closest('.dropdown-container')) {
      setShowDropdown(false);
    }

    if (showLegalModal && !event.target.closest('.legal-modal-container')) {
      setShowLegalModal(false);
    }

    if (isLanguageDropdownOpen && !event.target.closest('.language-dropdown-container')) {
      setIsLanguageDropdownOpen(false);
    }
    if (showHelpModal && !event.target.closest('.help-modal-container')) {
      handleHelpModalClose();
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [showDropdown, showLegalModal, showHelpModal, isLanguageDropdownOpen, handleHelpModalClose]);

// Show scroll-to-top button when user scrolls down
useEffect(() => {
  const handleScroll = () => {
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const next = scrollTop > 300;
      if (showScrollToTopRef.current !== next) {
        showScrollToTopRef.current = next;
        setShowScrollToTop(next);
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => {
    window.removeEventListener('scroll', handleScroll);
    if (scrollRafRef.current !== null) {
      window.cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  };
}, []);

// 🚀 Event-Listener für WalletConnect Account-Wechsel
useEffect(() => {
  const handleAccountsChanged = (event) => {
        
    // Hier könnte die dApp ihren State aktualisieren
    // z.B. neuen Account setzen, UI aktualisieren
    if (event.detail.accounts && event.detail.accounts.vechain) {
      const newAccounts = event.detail.accounts.vechain.accounts;
      if (newAccounts && newAccounts.length > 0) {
        const newWalletAddress = newAccounts[0].split(':').pop(); // Extrahiere Adresse aus "vechain:chain:address"
                
        // Toast-Benachrichtigung
        toast.success(language === 'en' ? `Account switched to: ${newWalletAddress.slice(0, 6)}...${newWalletAddress.slice(-4)}` : `Account gewechselt zu: ${newWalletAddress.slice(0, 6)}...${newWalletAddress.slice(-4)}`);
        
        // TODO: Hier könnte der neue Account in der dApp gesetzt werden
        // z.B. navigate mit neuem walletAddress
      }
    }
  };

  window.addEventListener('walletconnect:accountsChanged', handleAccountsChanged);
  
  return () => {
    window.removeEventListener('walletconnect:accountsChanged', handleAccountsChanged);
  };
}, [language]);

// Print-Renderer nur temporär im DOM halten
useEffect(() => {
  const handleAfterPrint = () => {
    setIsTaxReportPrintMounted(false);
  };
  window.addEventListener("afterprint", handleAfterPrint);
  return () => {
    window.removeEventListener("afterprint", handleAfterPrint);
  };
}, []);
  
  // 🎯 VEREINFACHT: Nur manuelle Klassifizierungen speichern
  const [manualClassifications, setManualClassifications] = useState({});
  const [pendingManualOverrides, setPendingManualOverrides] = useState({});
  const [isSavingManualOverrides, setIsSavingManualOverrides] = useState(false);
  const [isResettingManualOverrides, setIsResettingManualOverrides] = useState(false);
  const [savedManualOverrideCount, setSavedManualOverrideCount] = useState(0);
  const savedManualClassificationsRef = useRef({});
  const preservedOverrideCountAtRunStartRef = useRef(0);

  const hasPendingManualOverrides = Object.keys(pendingManualOverrides || {}).length > 0;
  
  // 🔧 NEU: State für nicht-steuerpflichtige Inflows und Sales
  const [nonTaxableInflows, setNonTaxableInflows] = useState({});
  const [nonTaxableSales, setNonTaxableSales] = useState({});
  
  // 🔧 NEU: State für manuelle NFT-Daten
  const [manualNftData, setManualNftData] = useState({});
  


  // 🎯 VEREINFACHT: Keine separaten edited States mehr!
  // const [editedVetVthoTxs, setEditedVetVthoTxs] = useState([]);
  // const [editedB3trTxs, setEditedB3trTxs] = useState([]);
  // const [editedNftTxs, setEditedNftTxs] = useState([]);

  const [fifoResultsState, setFifoResultsState] = useState({
    vet: [],
    vtho: [],
    b3tr: [],
    nfts: [],
  });

  const location = useLocation();
  
const locationState = location.state || {};
const {
  wallet: walletAddress = "",
  year = "",
  plan = "",
  delegateWallet: veDelegateWallet = null,
  mainWalletTxCount = 0,
  delegateWalletTxCount = 0,
  selectedCountry: selectedCountryFromRoute = 'DE', // 🎯 NEU: Land für Steuerberechnung
  isPurchased = false, // 🎯 NEU: Flag für gekaufte Wallet
  fromViewAnalysis = false, // 🎯 NEU: Flag für View Analysis
  loadFromSnapshot = false, // 🎯 NEU: Flag für Snapshot-Laden
  autoAnalyze = false, // 🚀 NEU: Flag für automatische Analyse
  isPreview = false // 🧪 Preview-Mode: sanitized payload ohne EUR/FIFO-Daten
} = locationState;

// ✅ selectedCountry: Route/Query hat Vorrang vor Dataset/Snapshot (Admin UK darf nicht zu DE werden)
const explicitSelectedCountry = useMemo(
  () => resolveExplicitRouteCountry(location),
  [location.state?.selectedCountry, location.search]
);

const [selectedCountry, setSelectedCountry] = useState(() =>
  normalizeUiCountryCode(
    explicitSelectedCountry ||
      selectedCountryFromRoute ||
      new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("country") ||
      "DE"
  )
);

// 🧪 Preview-Mode: Optional auch per Query-Parameter
const isPreviewFromQuery = new URLSearchParams(location.search).get("preview") === "1";
const isPreviewFromLS = localStorage.getItem("v3tax_preview_mode") === "true";

// ✅ Paid/Dataset-Kontext erkennen: sobald datasetId existiert -> niemals Preview aus LS übernehmen
const hasDatasetContext = useMemo(() => !!(actualDatasetId || location.state?.datasetId || isPurchased), [actualDatasetId, location.state?.datasetId, isPurchased]);

// finaler initialer Preview-Mode
const initialPreviewMode = useMemo(() =>
  !!isPreview ||
  isPreviewFromQuery ||
  (!hasDatasetContext && isPreviewFromLS),
  [isPreview, isPreviewFromQuery, hasDatasetContext, isPreviewFromLS]);

// 🚨 KORRIGIERT: Setze Standard-Jahr wenn über datasetId navigiert wird
  // 🔧 FIX: Robuste Jahrableitung ohne Fallback auf aktuelles Jahr
  const searchParams = new URLSearchParams(location.search);
  const queryYear = searchParams.get("year");
  const stateYear = (year && /^\d{4}$/.test(String(year))) ? String(year) : "";
  const storedYear = sessionStorage.getItem("selectedTaxYear") || "";
  
  // 🚀 NEU: Jahr aus userDataset ableiten wenn über datasetId navigiert
  const [userDataset, setUserDataset] = useState(null);
  
  const finalYear = useMemo(() => {
    // 1. Explizite Quellen (Router state, URL query, sessionStorage)
    const explicitYear = stateYear || (queryYear && /^\d{4}$/.test(queryYear) ? queryYear : "") || storedYear;
    
    // 2. Fallback: Jahr aus Dataset ableiten (wenn über datasetId navigiert)
    const datasetYear = userDataset?.year ? String(userDataset.year) : "";
    
    const result = explicitYear || datasetYear;
        return result;
  }, [stateYear, queryYear, storedYear, userDataset]);

  // ⛑ Persistenz für Tab-Wechsel
  const restoredFromCacheRef = useRef(false);
  // 🚀 FIX: Cache-Key um Country erweitern (verhindert DE-Cache für IT/GR)
  const cacheKey = useMemo(() => {
    const cc = String(selectedCountry || "DE").toUpperCase();
    return actualDatasetId && finalYear
      ? `analysis:${actualDatasetId}:${finalYear}:${cc}`
      : null;
  }, [actualDatasetId, finalYear, selectedCountry]);

  // ✅ DEBUG: Frontend Tax-Debugging
  useEffect(() => {
      }, [selectedCountry, finalYear, actualDatasetId]);

// 🎯 EINFACHER: Direkt loadFromSnapshot verwenden - kein redundanter State

// 🚀 DEBUG: Zeige was die AnalysisApp tatsächlich empfängt (ENTFERNT - zu viel Output)


// 🎯 DEBUG: Zeige welches Land geladen wurde (Performance-Optimierung: Logging entfernt)


  const [vip180Data, setVip180Data] = useState([]);
  const [vetVthoData, setVetVthoData] = useState([]);
  const [classifiedVip180Data, setClassifiedVip180Data] = useState([]); // 🚫 NEU: Vollständig klassifizierte VIP180-Daten


  const [syntheticVthoTxs, setSyntheticVthoTxs] = useState([]);
  const [detectedSwaps, setDetectedSwaps] = useState([]);
  const [enrichedNftData, setEnrichedNftData] = useState([]);
  const [vthoTaxData, setVthoTaxData] = useState([]);
  const [dailyVtho, setDailyVtho] = useState({});

  const [vetTotalProfit, setVetTotalProfit] = useState({ swapProfit: 0, otherIncome: 0, total: 0 });
  const [vthoTotalProfit, setVthoTotalProfit] = useState({ swapProfit: 0, otherIncome: 0, total: 0 });
  const [b3trTotalProfit, setB3trTotalProfit] = useState({ swapProfit: 0, otherIncome: 0, total: 0 });
  const [liquidityTotalProfit, setLiquidityTotalProfit] = useState({ swapProfit: 0, otherIncome: 0, total: 0 });
  const [nftTotalProfit, setNftTotalProfit] = useState(0);

  const [b3trTaxTableData, setB3trTaxTableData] = useState([]);
  const [nftTaxTableData, setNftTaxTableData] = useState([]);
  
  const [filteredEnrichedNftData, setFilteredEnrichedNftData] = useState([]);
  const [vip180Export, setVip180Export] = useState([]);
  const [vetVthoExportData, setVetVthoExportData] = useState([]);
  const [exchangeExportData, setExchangeExportData] = useState([]);
  const [vetTaxTableData, setVetTaxTableData] = useState([]);
  const [vthoTaxTableData, setVthoTaxTableData] = useState([]);
  const [liquidityTaxTableData, setLiquidityTaxTableData] = useState([]);
  const [allEnrichedNfts, setAllEnrichedNfts] = useState([]);
  const [allMatchedNfts, setAllMatchedNfts] = useState([]);
  const [fifoHasRunInitially, setFifoHasRunInitially] = useState(false);
  const [fifoCalculationComplete, setFifoCalculationComplete] = useState(false); // 🔧 NEU: Flag für abgeschlossene FIFO-Berechnung
  
  // 🧪 Preview-Mode State
  const [isPreviewMode, setIsPreviewMode] = useState(initialPreviewMode);

  // ✅ Robust: Rückkehr in die App IMMER als Restore behandeln
  const goToApp = useCallback(({ openBuy = false } = {}) => {
    try {
      sessionStorage.setItem("v3tax:return_intent", openBuy ? "buy" : "back");
    } catch (_) {}

    // ✅ Cleanup: previewExchangeRows NICHT mitgeben (nur temporär für Preview-Berechnung)
    navigate("/", {
      state: {
        restoreDraft: true,
        skipLandingIntro: true,
        openBuy,
        scrollToCta: openBuy,
        source: openBuy ? "preview-buy" : "preview",
        // ✅ WICHTIG: previewExchangeRows explizit weglassen (nicht persistent)
      },
    });
  }, [navigate]);

  const clearWalletConnectArtifacts = useCallback((hard = false) => {
    localStorage.removeItem("walletconnect");
    localStorage.removeItem("walletconnect-v2");

    if (hard) {
      localStorage.removeItem("wc_v3tax_pairing_topic");
    }

    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("wc@") ||
        key.startsWith("wc_") ||
        key.includes("walletconnect")
      ) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  const logoutFromAnalysisAppOnly = useCallback(async () => {
    
    sessionStorage.setItem("v3tax:force_logged_out", "1");

    try {
      setSource(null);
      clearWalletConnectArtifacts(false);
          } catch (cleanupError) {
          }

    try {
      localStorage.removeItem("v3tax_auth");
      window.dispatchEvent(new Event("v3tax-auth"));
    } catch (storageError) {
          }

    try {
      await logout();
          } catch (logoutError) {
          }

    try {
      await checkAuthStatus();
    } catch (statusError) {
          }

    setShowUserDropdown(false);
    goToApp();
  }, [setSource, logout, checkAuthStatus, goToApp, clearWalletConnectArtifacts]);
  
  // ✅ Cleanup: Beim Unmount optional sessionStorage aufräumen (falls später genutzt)
  useEffect(() => {
    return () => {
      // Optional: state cleanup ist sowieso weg – aber falls du mal sessionStorage nutzt
      try {
        sessionStorage.removeItem("preview_exchange_imports");
      } catch {
        // Page teardown is best-effort; the backend lease remains the fallback.
        return;
      }
    };
  }, []);

  // Preview-Mode aktualisieren wenn initialPreviewMode sich ändert
  useEffect(() => {
    setIsPreviewMode(initialPreviewMode);
  }, [initialPreviewMode]);
  
  // ✅ Optional: localStorage sauber machen wenn Dataset-Kontext aktiv ist
  useEffect(() => {
    if (hasDatasetContext) {
      localStorage.setItem("v3tax_preview_mode", "false");
    }
  }, [hasDatasetContext]);
  
  const hasAutoFifoRun = useRef(false);
  /** Nur für dedizierten Print-Renderer (`TaxReportPrint`); kein Umschalten der Live-Tabelle mehr */
  const [taxReportPdfVariant, setTaxReportPdfVariant] = useState("calc"); // "calc" | "raw"
  const handlePrintVariantChange = useCallback((variant) => {
    setTaxReportPdfVariant(variant);
    setIsTaxReportPrintMounted(true);
  }, []);
  const [fifoPools, setFifoPools] = useState({});
  const [fifoPoolsWithStargate, setFifoPoolsWithStargate] = useState({});
  const [lastFifoUpdate, setLastFifoUpdate] = useState(Date.now());
  const [lastVthoInflows, setLastVthoInflows] = useState([]);

// 🔧 WICHTIG: Spiegle State auf window für getEffectiveClassifiedData
useEffect(() => {
  window.vetVthoData = vetVthoData || [];
}, [vetVthoData]);

useEffect(() => {
  window.vip180Data = vip180Data || [];
}, [vip180Data]);

// 🚀 NEU: Notification für Dashboard nach erfolgreicher Berechnung
// useEffect(() => {
//   // Kriterium: Daten sind geladen/gerendert => erst dann „fertig"
//   const dataReady =
//     Array.isArray(vip180Data) && vip180Data.length > 0 &&
//     Array.isArray(vetVthoData) && vetVthoData.length > 0;

//   if (actualDatasetId && dataReady && !calcNotifiedRef.current) {
//     calcNotifiedRef.current = true;
//     notifyCalculationDone(actualDatasetId);

//     // Backend persistieren
//     fetch(apiUrl(`/api/datasets/${actualDatasetId}/mark-calculated`), {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ at: new Date().toISOString() })
//     }).catch((error) => {
//       void 0
//     });
//   }
// }, [actualDatasetId, vip180Data, vetVthoData]);

// 🎯 Manuelle Klassifizierung (stable txKey, nicht hash)
const handleClassificationChange = useCallback((overrideOrKey, newClassification, tx) => {
  const override =
    overrideOrKey && typeof overrideOrKey === "object" && overrideOrKey.key
      ? overrideOrKey
      : buildManualOverridePayload(tx || { hash: overrideOrKey }, newClassification);

  if (!override.key || !override.classification) return;

  const validation = validateManualOverrideClassificationForUi(override.classification);
  if (!validation.ok) {
    toast.error(language === "en" ? validation.messageEn : validation.message);
    return;
  }

  setPendingManualOverrides((prev) => ({
    ...prev,
    [override.key]: override,
  }));

  setManualClassifications((prev) => {
    const next = { ...prev };

    if (override.classification === "auto") {
      delete next[override.key];
    } else {
      next[override.key] = override.classification;
    }

    return next;
  });
}, [language]);

const hydrateManualClassificationsFromResult = useCallback((resultLike) => {
  const baseline = buildManualClassificationMapFromResult(resultLike);
  savedManualClassificationsRef.current = baseline;
  setManualClassifications(baseline);
  setPendingManualOverrides({});
  setSavedManualOverrideCount(Object.keys(baseline).length);
}, []);

const refreshSavedManualOverrideCount = useCallback(async () => {
  const { datasetId: effectiveDatasetId, year: effectiveYear } =
    resolveDatasetAnalysisContext({
      actualDatasetId,
      locationState: location.state,
      locationSearch: location.search,
      userDataset,
      backendData: backendDataRef.current,
      finalYear,
      routeStateYear: stateYear,
      storedTaxYear: storedYear,
    });

  if (!effectiveDatasetId || !effectiveYear || isPreviewMode) {
    setSavedManualOverrideCount(0);
    return;
  }

  try {
    const summary = await fetchManualOverrideSummary(effectiveDatasetId, effectiveYear);
    setSavedManualOverrideCount(summary.count || 0);
  } catch {
    setSavedManualOverrideCount(Object.keys(savedManualClassificationsRef.current || {}).length);
  }
}, [
  actualDatasetId,
  location.state,
  location.search,
  userDataset,
  finalYear,
  stateYear,
  storedYear,
  isPreviewMode,
]);

useEffect(() => {
  refreshSavedManualOverrideCount();
}, [refreshSavedManualOverrideCount]);

useEffect(() => {
  const onOverridesUpdated = (event) => {
    const id = actualDatasetId || location.state?.datasetId;
    if (!id || String(event.detail?.datasetId) !== String(id)) return;
    if (typeof event.detail?.count === "number") {
      setSavedManualOverrideCount(event.detail.count);
    } else {
      refreshSavedManualOverrideCount();
    }
  };

  window.addEventListener("manualOverrides:updated", onOverridesUpdated);
  return () => window.removeEventListener("manualOverrides:updated", onOverridesUpdated);
}, [actualDatasetId, location.state?.datasetId, refreshSavedManualOverrideCount]);

const cancelManualEdits = useCallback(() => {
  setManualClassifications({ ...savedManualClassificationsRef.current });
  setPendingManualOverrides({});
  setIsEditMode(false);
}, []);

// 🎯 Edit-Modus umschalten — beim Verlassen ohne Speichern alles zurücksetzen
const toggleEditMode = useCallback(() => {
  setIsEditMode((prev) => {
    if (prev) {
      setManualClassifications({ ...savedManualClassificationsRef.current });
      setPendingManualOverrides({});
    }
    return !prev;
  });
}, []);

// 📊 Dashboard umschalten
const toggleDashboard = () => {
  setShowDashboard(prev => !prev);
  // Performance-Optimierung: Logging entfernt
  
  // 🔄 Automatisch nach oben scrollen wenn Dashboard aktiviert wird
  if (!showDashboard) {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }
};

// 🔧 NEU: Handler für nicht-steuerpflichtige Inflows
const handleNonTaxableInflowChange = useCallback((inflowKey, checked) => {
  setNonTaxableInflows(prev => ({
    ...prev,
    [inflowKey]: checked
  }));
  
}, []);

// 🔧 NEU: Handler für nicht-steuerpflichtige Sales
const handleNonTaxableSaleChange = useCallback((saleKey, checked) => {
  setNonTaxableSales(prev => ({
    ...prev,
    [saleKey]: checked
  }));
  
}, []);

// 🔧 NEU: Handler für manuelle NFT-Daten
const handleManualNftDataChange = useCallback((nftKey, nftData) => {
  setManualNftData(prev => ({
    ...prev,
    [nftKey]: nftData
  }));
  }, []);



// 🎯 Daten mit gespeicherten manuellen Klassifizierungen anreichern (nur stable keys)
const getEnrichedData = (originalData) => {
  return originalData.map((tx) => {
    const manualClassification = pickManualClassification(manualClassifications, tx);
    if (manualClassification) {
      return {
        ...tx,
        classification: manualClassification,
        type: manualClassification,
      };
    }
    return tx;
  });
};

// 🎯 VEREINFACHT: Keine komplexen useEffect-Ketten mehr!
// useEffect(() => {
//   void 0
//   if (!isEditMode) {
//     void 0
//     void 0
//     hasAutoFifoRun.current = false;
//   }
//   if (isEditMode && vetVthoData.length > 0) {
//     void 0
//     setEditedVetVthoTxs(JSON.parse(JSON.stringify(vetVthoData)));
//     setEditedB3trTxs(JSON.parse(JSON.stringify(b3trTxs)));
//     setEditedNftTxs(JSON.parse(JSON.stringify(nftTxs)));
//     setHasPendingEdits(false);
//   }
// }, [isEditMode, vetVthoData, b3trTxs, nftTxs]);

// useEffect(() => {
//   const allReady =
//     isEditMode &&
//     editedVetVthoTxs.length > 0 &&
//     editedB3trTxs.length > 0 &&
//     editedNftTxs.length > 0;

//   if (allReady && !hasAutoFifoRun.current) {
//     hasAutoFifoRun.current = true;
//     void 0
//     // 🚀 ENTFERNT: handleRecalculateFifo - wurde ins Backend transferiert
//   }
// }, [isEditMode, editedVetVthoTxs, editedB3trTxs, editedNftTxs]);

  // 🆕 Edit erkannt (vereinfacht)
  const handleEditDetected = () => {
      };

// ✅ Eingaben übernehmen (vereinfacht)
// const handleApplyChanges = () => {
//   void 0
//   void 0

//   // 🔍 LOG: Übersicht aller Klassifizierungen nach Bearbeitung
//   console.table(
//     editedB3trTxs.map(tx => ({
//       hash: tx.hash,
//       token: tx.token,
//       type: tx.type,
//       classification: tx.classification,
//       amount: tx.amount
//     }))
//   );

//   setVetVthoData(editedVetVthoTxs);
//   setVetTxs(editedVetVthoTxs.filter(tx => sanitizeTokenName(tx.token) === "VET"));
//   setVthoTxs(editedVetVthoTxs.filter(tx => sanitizeTokenName(tx.token) === "VTHO"));

//   setB3trTxs(editedB3trTxs);
//   setVip180Data(editedB3trTxs); // 🔁 wichtig: wird weiterverwendet in filteredB3trData!

//   setNftTxs(editedNftTxs);
// };



  // 📥 Bearbeitung von VIP180-Tabelle (B3TR) - vereinfacht
  const handleVip180EditChange = (newList) => {
        // Keine State-Überschreibung mehr!
  };

  // 🆕 Preisdaten vom Backend vorausladen
useEffect(() => {
  // 🚫 BLOCKIERE API-Aufrufe wenn Snapshot geladen wurde
  if (loadFromSnapshot) {
        return;
  }
  
  const fetchBackendPrices = async () => {
    try {
      const [b3tr, vet, vtho] = await Promise.all([
        fetch("/api/data/b3tr-price").then(res => res.json()),
        fetch("/api/data/vet-price").then(res => res.json()),
        fetch("/api/data/vtho-price").then(res => res.json())
      ]);

      const combined = {
        B3TR: b3tr,
        VET: vet,
        VTHO: vtho
      };

      // 🚫 TODO: Preis-Map später implementieren wenn nötig
          } catch (err) {
          }
  };

  fetchBackendPrices();
}, [loadFromSnapshot]);

    const handleDataReady = useCallback((exportData) => {
    setNftTaxTableData(exportData);
  }, []);

const handleProfitCalculated = useCallback((profitData) => {
  // 🔧 FIX: Dashboard erwartet nur den Gesamtwert als Zahl für NFTs
  const nftProfit = typeof profitData === 'object' ? profitData.total : profitData;
  setNftTotalProfit(nftProfit);
}, []);

const filteredB3trData = useMemo(() => {
  // 🎯 NEU: Wenn ein Snapshot geladen wurde, verwende die bestehenden Daten
  if (loadFromSnapshot && vip180Data.length > 0) {
        const filtered = getEnrichedData(vip180Data).filter(
      tx =>
        (sanitizeTokenName(tx.token) === "B3TR" || sanitizeTokenName(tx.token) === "VEB3TR") &&
        (
          ["airdrop", "Governance_reward"].includes(tx.type) ||
          ["airdrop", "Governance_reward"].includes(tx.classification) ||
          tx.classification === "swap_out" || tx.type === "swap_out" ||
          ["sale", "sale_payment"].includes((tx.classification || "").toLowerCase()) ||
          tx.type === "mint_payment" || tx.classification === "nft_mint"
        ) &&
        !tx.hash?.includes("simulatedsalehash123")
    );
        return filtered;
  }
  
  // 🎯 VEREINFACHT: Verwende immer vip180Data und reichere mit manuellen Klassifizierungen an
  const enrichedData = getEnrichedData(vip180Data);

  const filtered = enrichedData.filter(
    tx =>
      (sanitizeTokenName(tx.token) === "B3TR" || sanitizeTokenName(tx.token) === "VEB3TR") &&
      (
        ["airdrop", "Governance_reward"].includes(tx.type) ||
        ["airdrop", "Governance_reward"].includes(tx.classification) ||
        tx.classification === "swap_out" || tx.type === "swap_out" ||
        ["sale", "sale_payment"].includes((tx.classification || "").toLowerCase()) || // 🛡️ sale_payment hinzufügen!
        tx.type === "mint_payment" || tx.classification === "nft_mint" // 🔧 Galaxy Mints hinzufügen!
      ) &&
      !tx.hash?.includes("simulatedsalehash123") // Entferne hartkodierte simulatedSale
  );
  

  
  return filtered;
}, [vip180Data, b3trTxs, isEditMode, loadFromSnapshot]);

useEffect(() => {
  // Logs entfernt
}, [filteredB3trData]);

const filteredVetSwapOutData = useMemo(() => {
  // 🎯 NEU: Wenn ein Snapshot geladen wurde, verwende die bestehenden Daten
  if (loadFromSnapshot && vetVthoData.length > 0) {
        return vetVthoData.filter(
      tx =>
        (tx.token || "").toUpperCase() === "VET" &&
        tx.classification === "swap_out"
    );
  }
  
  return vetVthoData.filter(
    tx =>
      (tx.token || "").toUpperCase() === "VET" &&
      tx.classification === "swap_out"
  );
}, [vetVthoData, loadFromSnapshot]);

  // 🧭 Wenn weder Wallet noch Dataset-ID gesetzt → redirect
  useEffect(() => {
    if (!walletAddress && !actualDatasetId) {
      navigate("/", { replace: true });
    }
  }, [walletAddress, actualDatasetId, navigate]);

  // 🚀 NEU: Initialisiere walletSet wenn nur walletAddress vorhanden ist
  useEffect(() => {
    if (walletAddress && walletSet.addresses.length === 0) {
      setWalletSet({
        addresses: [walletAddress],
        mainAddresses: [walletAddress],
        delegateAddresses: []
      });
    }
  }, [walletAddress, walletSet.addresses.length]);



// ✅ Hier einfügen:
const handleTotalProfitCalculated = useCallback((nftTotal) => {
  setNftTotalProfit(nftTotal);
}, []);

  // Danach folgen deine useEffect(...) usw.

    // 🆕 Neuer State für FIFO-berechnete NFT-Mints (inkl. matchedInflows)
  const [nftFifoData, setNftFifoData] = useState([]);

  // 📊 Menge synthetisch erzeugter VTHO
  const generatedVthoAmount = syntheticVthoTxs.reduce((sum, tx) => {
    if (tx.type !== "generated") return sum;
    const a = tx.amount_display ?? tx.amountDisplay ?? tx.amount;
    const n = Number(a);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0); // ✅ FIX: amount_display bevorzugen (human-readable)

// 📥 Steuerlich relevante B3TR-Inflows für FIFO (inkl. NFT-Verkäufe)
// 🟢 Datenquelle abhängig vom Bearbeitungsmodus
const rawVip180Source = isEditMode ? b3trTxs : vip180Data;

const vip180Source = rawVip180Source;

// 🔧 FIX: Verwende die neuesten Daten von Vip180TokenTable für B3TR Inflows
// Das Problem war, dass vip180Data möglicherweise nicht die neuesten swap_in Klassifizierungen enthält
const b3trInflowsSource = isEditMode ? b3trTxs : vip180Data;









// 📥 Steuerlich relevante B3TR-Inflows für FIFO (inkl. NFT-Verkäufe)
// 🔧 WICHTIG: Diese werden NACH der Swap-Klassifizierung erstellt



// 🔁 Erweiterte Inflows für VET (inkl. swap_in & NFT-Verkäufe)
const vetInflowsRaw = vetVthoData
  .map((t) => ({
    ...t,
    classification: pickManualClassification(manualClassifications, t) || t.classification || "",
  }))
  .filter((t) =>
    sanitizeTokenName(t.token) === "VET" &&
    ["Empfangen", "received", "swap_in", "sale_payment"].includes(
      (t.direction || t.type || t.classification || "").toLowerCase()
    )
  )
  .map((t) => {
    const base = {
      token: sanitizeTokenName(t.token),
      amount: (() => {
        const a = t.amount_display ?? t.amountDisplay ?? t.amount;
        const n = Number(a);
        return Number.isFinite(n) ? n : 0;
      })(), // ✅ FIX: amount_display bevorzugen (human-readable)
      timestamp: t.timestamp || parseDateToTimestamp(t.date),
      hash: t.hash || t.txid || t.key || "unknown-hash",
      recipient: t.recipient,
      sender: t.sender,
      euroPrice: 0, // 🚫 TODO: Preis-Berechnung später implementieren wenn nötig
    };
    const classified = classifyStargateDeposit(base, { walletAddress });
    return {
      ...base,
      ...classified,
    };
  });

const vetInflows = vetInflowsRaw
  .map((tx) => ({
    ...tx,
    classification: pickManualClassification(manualClassifications, tx) || tx.classification || "",
  }))
  .filter((tx) =>
  !(tx.classification || "").toLowerCase().includes("stargate")
);

// 📥 Steuerlich relevante VTHO-Inflows für FIFO (inkl. swap_in & NFT-Verkäufe)
// 🎯 NEU: Diese Berechnung wird NACH der Swap-Klassifizierung gemacht


  // 🎯 NEU: Snapshot laden wenn gewünscht (ERSETZT durch neuen useEffect oben)
  // useEffect(() => {
  //   if (loadFromSnapshot && walletAddress && year) {
  //     loadCalculationSnapshot();
  //   }
  // }, [loadFromSnapshot, walletAddress, year]);

  // 🎯 NEU: Speichere Snapshot nach Berechnung
  const saveCalculationSnapshot = async () => {
    try {
      // 🔧 KRITISCHE PRÜFUNG: Speichere NUR wenn matchedInflows tatsächlich vorhanden sind
      const hasMatchedInflows = fifoResultsState?.matchedOutflows?.some(tx => tx.matchedInflows?.length > 0) ||
                               fifoResultsState?.matchedSwapOuts?.some(tx => tx.matchedInflows?.length > 0);
      
      if (!hasMatchedInflows) {
                        return; // 🚫 ABBRECHEN - kein Snapshot speichern
      }
      
            
            
      // 🎯 NEU: Sammle ALLE Daten für den vollständigen Bearbeitungsmodus
      // 🔧 KRITISCH KORRIGIERT: Erstelle die korrekte Snapshot-Struktur
      const loadAndMatchData = {
        // API-Inputs (alle rohen Daten)
        rawNftTransfers: nftTxs || [], // Alle NFT-Transfers
        vip180Data: vip180Data || [], // Alle VIP180-Transaktionen
        vetVthoData: vetVthoData || [], // Alle VET/VTHO-Transaktionen
        priceData: {}, // Preis-Daten (können wir später hinzufügen)
        otherExternalData: [], // Weitere externe Daten
        
        // Verarbeitete Daten (alle für den Bearbeitungsmodus)
        nftTxs: nftTxs || [], // Alle NFT-Transaktionen (inkl. Klassifizierungen)
        nftMints: nftFifoData || [], // 🎯 NEU: NFT-Mints für Snapshot
        nftSales: enrichedNftData?.filter(nft => 
          (nft.classification === "sale" || nft.type === "sale")
        ) || [], // 🎯 NEU: NFT-Verkäufe für Snapshot
        syntheticMints: [], // Synthetische Mints (falls vorhanden)
        classifiedVip180: classifiedVip180Data || vip180Data || [], // 🚫 NEU: Vollständig klassifizierte VIP180-Daten
        classifiedVetVtho: vetVthoData || [], // Alle klassifizierten VET/VTHO-Daten
        
        // 🔧 KRITISCH KORRIGIERT: FIFO-Ergebnisse in der korrekten Struktur
        fifoResults: fifoResultsState || {}, // Alle FIFO-Ergebnisse
        fifoStacks: {}, // FIFO-Stacks (falls vorhanden)
        
        // Steuerberechnungen (alle Summen)
        vetTotalProfit: 0, // VET-Gesamtgewinn
        vthoTotalProfit: 0, // VTHO-Gesamtgewinn
        b3trTotalProfit: 0, // B3TR-Gesamtgewinn
        nftTotalProfit: 0, // NFT-Gesamtgewinn
        
        // 🔧 KRITISCH KORRIGIERT: matchedInflows in der korrekten Struktur
        matchedInflows: (() => {
          // 🔧 KORRIGIERT: Extrahiere matchedInflows direkt aus den ursprünglichen FIFO-Ergebnissen
          const inflowsMap = {};
          

          
          // 🔧 WICHTIG: Sammle matchedInflows direkt aus den ursprünglichen FIFO-Ergebnissen
          // Das ist zuverlässiger als aus vip180Data, da diese möglicherweise keine matchedInflows mehr haben
          
          // Sammle matchedInflows aus matchedOutflows
          if (fifoResultsState?.matchedOutflows) {
            fifoResultsState.matchedOutflows.forEach(tx => {
              if (tx.matchedInflows && tx.matchedInflows.length > 0) {
                // 🔧 NEU: Speichere mehrere txKey-Varianten für bessere Kompatibilität
                const txKeyVariants = [
                  `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`,
                  `${tx.hash}-${tx.block_timestamp || tx.timestamp || 0}`,
                  `${tx.hash}-0`
                ];
                
                // Speichere matchedInflows unter allen Varianten
                txKeyVariants.forEach(txKey => {
                  inflowsMap[txKey] = tx.matchedInflows;
                });

                // 🔧 Zusätzlich: immer auch unter dem reinen Hash speichern (robuster)
                if (tx.hash) {
                  inflowsMap[tx.hash] = tx.matchedInflows;
                }
                

              }
            });
          }
          
          // Sammle matchedInflows aus matchedSwapOuts
          if (fifoResultsState?.matchedSwapOuts) {
            fifoResultsState.matchedSwapOuts.forEach(tx => {
              if (tx.matchedInflows && tx.matchedInflows.length > 0) {
                // 🔧 NEU: Speichere mehrere txKey-Varianten für bessere Kompatibilität
                const txKeyVariants = [
                  `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`,
                  `${tx.hash}-${tx.block_timestamp || tx.timestamp || 0}`,
                  `${tx.hash}-0`
                ];
                
                // Speichere matchedInflows unter allen Varianten
                txKeyVariants.forEach(txKey => {
                  inflowsMap[txKey] = tx.matchedInflows;
                });

                // 🔧 Zusätzlich: immer auch unter dem reinen Hash speichern (robuster)
                if (tx.hash) {
                  inflowsMap[tx.hash] = tx.matchedInflows;
                }
                

              }
            });
          }
          
          // 🔧 FALLBACK: Sammle auch matchedInflows aus vip180Data (falls vorhanden)
          (vip180Data || []).forEach(tx => {
            if (tx.matchedInflows && tx.matchedInflows.length > 0) {
              // 🔧 NEU: Speichere mehrere txKey-Varianten für bessere Kompatibilität
              const txKeyVariants = [
                `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`,
                `${tx.hash}-${tx.block_timestamp || tx.timestamp || 0}`,
                `${tx.hash}-0`
              ];
              
              // Speichere matchedInflows unter allen Varianten
              txKeyVariants.forEach(txKey => {
                inflowsMap[txKey] = tx.matchedInflows;
              });

              // 🔧 Zusätzlich: immer auch unter dem reinen Hash speichern (robuster)
              if (tx.hash) {
                inflowsMap[tx.hash] = tx.matchedInflows;
              }
              

            }
          });
          
          // Sammle auch matchedInflows aus vetVthoData (falls vorhanden)
          (vetVthoData || []).forEach(tx => {
            if (tx.matchedInflows && tx.matchedInflows.length > 0) {
              // 🔧 NEU: Speichere mehrere txKey-Varianten für bessere Kompatibilität
              const txKeyVariants = [
                `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`,
                `${tx.hash}-${tx.block_timestamp || tx.timestamp || 0}`,
                `${tx.hash}-0`
              ];
              
              // Speichere matchedInflows unter allen Varianten
              txKeyVariants.forEach(txKey => {
                inflowsMap[txKey] = tx.matchedInflows;
              });

              // 🔧 Zusätzlich: immer auch unter dem reinen Hash speichern (robuster)
              if (tx.hash) {
                inflowsMap[tx.hash] = tx.matchedInflows;
              }
              
              // 🔍 DEBUG: matchedInflows für vetVthoData gespeichert (ENTFERNT - zu viel Output)
            }
          });
          
          // 🔍 DEBUG: matchedInflows für Snapshot gesammelt (ENTFERNT - zu viel Output)
          
          // 🔧 KRITISCHER DEBUG: inflowsMap vor dem Speichern (ENTFERNT - zu viel Output)
          
          return inflowsMap;
        })(), // Gematchte Zuflüsse
        syntheticVthoTxs: [], // Synthetische VTHO-Transaktionen
        
        // 🎯 NEU: Alle verarbeiteten Daten für die Anzeige (nur verfügbare State-Variablen)
        b3trData: b3trTxs || [], // B3TR-Transaktionen
        vthoTaxData: vthoTaxData || [], // VTHO-Steuerdaten
        vetTaxTableData: vetTaxTableData || [], // VET-Steuerdaten (verfügbar)
        nftTaxTableData: nftTaxTableData || [], // NFT-Steuerdaten (verfügbar)
        enrichedNftData: enrichedNftData || [], // Angereicherte NFT-Daten
        // allVthoTaxTxs: allVthoTaxTxs || [], // Alle VTHO-Steuer-Transaktionen (nicht verfügbar)
        // allVetTaxTxs: allVetTaxTxs || [], // Alle VET-Steuer-Transaktionen (nicht verfügbar)
        // allB3trTaxTxs: allB3trTaxTxs || [], // Alle B3TR-Steuer-Transaktionen (nicht verfügbar)
        // allNftTaxTxs: allNftTaxTxs || [] // Alle NFT-Steuer-Transaktionen (nicht verfügbar)
      };
      
      // 🎯 NEU: Sammle User-Inputs
      const userInputs = {
        manualClassifications: savedManualClassificationsRef.current,
        nonTaxableInflows,
        nonTaxableSales,
        manualAdjustments: {}
      };
      
      // 🔧 KRITISCH: Reiche vip180Data mit eingebetteten matchedInflows an
      const enrichedVip180Data = (vip180Data || []).map(tx => {
        let matchedInflowsForTx = tx.matchedInflows || [];
        if ((!matchedInflowsForTx || matchedInflowsForTx.length === 0) && fifoResultsState) {
          const byOutflow = fifoResultsState.matchedOutflows?.find(m => m.hash === tx.hash);
          if (byOutflow?.matchedInflows?.length > 0) matchedInflowsForTx = byOutflow.matchedInflows;
          if ((!matchedInflowsForTx || matchedInflowsForTx.length === 0)) {
            const bySwapOut = fifoResultsState.matchedSwapOuts?.find(m => m.hash === tx.hash);
            if (bySwapOut?.matchedInflows?.length > 0) matchedInflowsForTx = bySwapOut.matchedInflows;
          }
        }
        
        
        return { ...tx, matchedInflows: matchedInflowsForTx };
      });

      // Backend-b3trTaxRows sind die steuerliche Wahrheit (alle VIP180-Tokens inkl. Juicy-Income)
      const canonicalB3trTaxRows =
        backendData?.analysisResult?.dataset?.b3trTaxRows ||
        backendData?.dataset?.b3trTaxRows ||
        b3trTaxTableData ||
        [];

      // 🔧 KRITISCH: Verwende vetVthoData direkt ohne Frontend-Klassifizierung
      const enrichedVetVthoData = vetVthoData || [];

      // 🔧 KRITISCH: Speichere matchedInflows direkt in sourceData.vip180Data
                if (enrichedVip180Data.length > 0) {
                  loadAndMatchData.sourceData = {
                    ...loadAndMatchData.sourceData,
                    vip180Data: enrichedVip180Data
                  };
                }

      // 🔧 KRITISCH: Speichere interne Transfer-Klassifizierungen in vetVthoData
      if (enrichedVetVthoData.length > 0) {
        loadAndMatchData.vetVthoData = enrichedVetVthoData;
      }

      const snapshotData = {
        // alle anderen Daten
        ...loadAndMatchData,
        // vip180Data mit eingebetteten inflows (Top-Level, wie bisher vom Backend erwartet)
        vip180Data: enrichedVip180Data,
        // vetVthoData mit internen Transfer-Klassifizierungen
        vetVthoData: enrichedVetVthoData,

        // processedData Block explizit mit angereicherten Daten, falls Backend hier liest
        processedData: {
          ...(loadAndMatchData.processedData || {}),
          vip180Data: enrichedVip180Data,
          vetVthoData: enrichedVetVthoData,
          b3trTaxTableData: canonicalB3trTaxRows,
          b3trTaxRows: canonicalB3trTaxRows,
          fifoResults: fifoResultsState || {},

        }
      };


      
      const result = await snapshotAPI.saveCalculation(
        actualDatasetId || location.state?.datasetId,
        finalYear,
        snapshotData,
        veDelegateWallet,
        userInputs
      );
      
      if (result.success) {
                // 🎯 NEU: Prominenter Toast für erfolgreiche Speicherung
        toast.success(
          language === 'en' ? '✅ Calculation saved successfully!' : '✅ Berechnung erfolgreich gespeichert!',
          {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        
        // 🎯 NEU: Dashboard aktualisieren (falls es geöffnet ist)
        if (window.updateDashboardCalculationStatus) {
          window.updateDashboardCalculationStatus(walletAddress, year, 'calculated');
        }
      } else {
                toast.error(
          language === 'en' ? '❌ Failed to save calculation' : '❌ Fehler beim Speichern der Berechnung'
        );
      }
    } catch (error) {
          }
  };

  // 🎯 NEU: Lade gespeicherten Snapshot
  const loadCalculationSnapshot = async () => {
    try {
            
      
      // 🔧 NEU: Reset FIFO-Berechnungs-Status beim Laden
      setFifoCalculationComplete(false);
      
      const result = await snapshotAPI.loadCalculation(actualDatasetId || location.state?.datasetId, year);
      
                    if (result.success && result.calculation) {
        const snapshot = result.calculation;
                
        
        
        if (snapshot.fifoResults) {
                            }
        
        if (snapshot.matchedInflows) {
                            }
          
          // 🔍 DEBUG: Zeige alle verfügbaren Daten im Snapshot
          // 🔍 DEBUG: Verfügbare Daten im Snapshot (ENTFERNT - zu viel Output)
          // sourceData (ENTFERNT - zu viel Output)
          // processedData (ENTFERNT - zu viel Output)
          // processedData Debug-Logs entfernt
        
                  // 🎯 NEU: Stelle alle Daten aus dem Snapshot wieder her
          
          // VIP180-Daten (B3TR) - Lade aus dem korrekten Pfad
          
          if (snapshot.processedData?.vip180Data && snapshot.processedData.vip180Data.length > 0) {
            setVip180Data(snapshot.processedData.vip180Data);
                      } else if (snapshot.sourceData?.vip180Data && snapshot.sourceData.vip180Data.length > 0) {
            // Fallback: Lade aus sourceData
            setVip180Data(snapshot.sourceData.vip180Data);
                      } else {
                      }
          
          // VET/VTHO-Daten - Lade aus dem korrekten Pfad
          
          if (snapshot.processedData?.vetVthoData && snapshot.processedData.vetVthoData.length > 0) {
            setVetVthoData(snapshot.processedData.vetVthoData);
                      } else if (snapshot.sourceData?.vetVthoData && snapshot.sourceData.vetVthoData.length > 0) {
            // Fallback: Lade aus sourceData
            setVetVthoData(snapshot.sourceData.vetVthoData);
                      } else {
                      }
          
          if (snapshot.sourceData?.apiInputs) {
            const apiInputs = snapshot.sourceData.apiInputs;
            
            // NFT-Daten
            if (apiInputs.nftTransfers) {
              // 🔧 KORRIGIERT: Füge matchedInflows zu den NFT-Daten hinzu (Hash-first)
              const nftsWithInflows = apiInputs.nftTransfers.map(nft => {
                let matchedInflows = snapshot.processedData?.matchedInflows?.[nft.hash] || [];
                if (matchedInflows.length === 0) {
                  const availableKeys = Object.keys(snapshot.processedData?.matchedInflows || {});
                  const byHashKey = availableKeys.find(k => k.startsWith(nft.hash));
                  if (byHashKey) {
                    matchedInflows = snapshot.processedData.matchedInflows[byHashKey] || [];
                  }
                }
                if (matchedInflows.length === 0) {
                  const txKey = `${nft.hash}-${nft.timestamp || nft.block_timestamp || 0}`;
                  matchedInflows = snapshot.processedData?.matchedInflows?.[txKey] || [];
                }
                
                // 🔍 DEBUG: matchedInflows für NFT gefunden (ENTFERNT - zu viel Output)
                
                return {
                  ...nft,
                  matchedInflows
                };
              });
              
              setNftTxs(nftsWithInflows);
              setEnrichedNftData(nftsWithInflows);
              setNftFifoData(nftsWithInflows);
              
              // 🛡️ WICHTIG: Kombiniere nftsWithInflows mit NFT-Verkäufen für nftTaxTableData
              const allNftDataForSnapshotMatchedInflows = [...nftsWithInflows];
              
              // Füge NFT-Verkäufe hinzu, falls vorhanden
              if (snapshot.processedData?.nftSales && snapshot.processedData.nftSales.length > 0) {
                const nftSalesNotInSnapshotMatchedInflows = snapshot.processedData.nftSales.filter(nft => 
                  (nft.classification === "sale" || nft.type === "sale") &&
                  !nftsWithInflows.some(matchedNft => matchedNft.hash === nft.hash)
                );
                
                if (nftSalesNotInSnapshotMatchedInflows.length > 0) {
                  // 🔧 DEBUG: Füge NFT-Verkäufe zu nftTaxTableData in Snapshot matchedInflows hinzu (ENTFERNT - zu viel Output)
                  allNftDataForSnapshotMatchedInflows.push(...nftSalesNotInSnapshotMatchedInflows);
                }
              }
              
              setNftTaxTableData(allNftDataForSnapshotMatchedInflows);
              // 🔧 DEBUG: nftTaxTableData in Snapshot matchedInflows gesetzt (ENTFERNT - zu viel Output)
                          }
            
            // 🔧 KORRIGIERT: Verwende die korrekten Feldnamen aus dem Snapshot
            if (snapshot.processedData?.nftMints && snapshot.processedData.nftMints.length > 0) {
                            setNftTxs(snapshot.processedData.nftMints);
              
              // 🛡️ WICHTIG: Kombiniere NFT-Mints mit NFT-Verkäufen für nftFifoData
              const allNftDataForSnapshot = [...snapshot.processedData.nftMints];
              
              // Füge NFT-Verkäufe hinzu, falls vorhanden
              if (snapshot.processedData?.nftSales && snapshot.processedData.nftSales.length > 0) {
                const nftSalesNotInSnapshot = snapshot.processedData.nftSales.filter(nft => 
                  (nft.classification === "sale" || nft.type === "sale") &&
                  !snapshot.processedData.nftMints.some(mintNft => mintNft.hash === nft.hash)
                );
                
                if (nftSalesNotInSnapshot.length > 0) {
                                    allNftDataForSnapshot.push(...nftSalesNotInSnapshot);
                }
              }
              
              setNftFifoData(allNftDataForSnapshot);
                          }
            
            if (snapshot.processedData?.nftSales && snapshot.processedData.nftSales.length > 0) {
              setEnrichedNftData(snapshot.processedData.nftSales);
              
              // 🛡️ WICHTIG: Kombiniere NFT-Sales mit NFT-Mints für nftTaxTableData
              const allNftDataForSnapshotTaxTableDirect = [...snapshot.processedData.nftSales];
              
              // Füge NFT-Mints hinzu, falls vorhanden
              if (snapshot.processedData?.nftMints && snapshot.processedData.nftMints.length > 0) {
                const nftMintsNotInSnapshotTaxTableDirect = snapshot.processedData.nftMints.filter(nft => 
                  (nft.classification === "mint" || nft.type === "mint") &&
                  !snapshot.processedData.nftSales.some(saleNft => saleNft.hash === nft.hash)
                );
                
                if (nftMintsNotInSnapshotTaxTableDirect.length > 0) {
                  allNftDataForSnapshotTaxTableDirect.push(...nftMintsNotInSnapshotTaxTableDirect);
                }
              }
              
              setNftTaxTableData(allNftDataForSnapshotTaxTableDirect);
            }
          
          // Token-Daten
          if (apiInputs.vip180Transactions) {
            setVip180Data(apiInputs.vip180Transactions);
                      }
          
          if (apiInputs.vetVthoTransactions) {
            setVetVthoData(apiInputs.vetVthoTransactions);
                      }
        }
        
        // 🎯 NEU: Stelle verarbeitete Daten wieder her
        if (snapshot.processedData) {
          const processed = snapshot.processedData;
          
          // NFT-Daten (alle verarbeiteten NFTs)
          if (snapshot.processedData?.nftTxs) {
            setNftTxs(snapshot.processedData.nftTxs);
                        
            // 🛡️ WICHTIG: Aktualisiere auch nftFifoData mit den kombinierten Daten
            const allNftDataForSnapshotTxsUpdate = [...snapshot.processedData.nftTxs];
            
            // Füge NFT-Verkäufe hinzu, falls vorhanden
            if (snapshot.processedData?.nftSales && snapshot.processedData.nftSales.length > 0) {
              const nftSalesNotInSnapshotTxsUpdate = snapshot.processedData.nftSales.filter(nft => 
                (nft.classification === "sale" || nft.type === "sale") &&
                !snapshot.processedData.nftTxs.some(txNft => txNft.hash === nft.hash)
              );
              
              if (nftSalesNotInSnapshotTxsUpdate.length > 0) {
                                allNftDataForSnapshotTxsUpdate.push(...nftSalesNotInSnapshotTxsUpdate);
              }
            }
            
            setNftFifoData(allNftDataForSnapshotTxsUpdate);
                          // 🔧 DEBUG: nftFifoData in Snapshot txs update gesetzt (ENTFERNT - zu viel Output)
          }
          
          // 🔧 KRITISCH KORRIGIERT: Verarbeite matchedInflows ZUERST, bevor fifoResultsState gesetzt wird!
          
          // Gematchte Zuflüsse
          if (snapshot.processedData?.matchedInflows) {
            // 🔧 KORRIGIERT: Füge matchedInflows zu allen VIP180-Transaktionen hinzu
                        

            
            // Aktualisiere vip180Data mit matchedInflows (Hash-first)
            const updatedVip180Data = (vip180Data || []).map(tx => {
              let matchedInflows = snapshot.processedData.matchedInflows?.[tx.hash] || [];
              if (matchedInflows.length === 0) {
                const availableKeys = Object.keys(snapshot.processedData.matchedInflows || {});
                const byHashKey = availableKeys.find(k => k.startsWith(tx.hash));
                if (byHashKey) {
                  matchedInflows = snapshot.processedData.matchedInflows[byHashKey] || [];
                }
              }
              if (matchedInflows.length === 0) {
                const txKey = `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`;
                matchedInflows = snapshot.processedData.matchedInflows[txKey] || [];
              }
              

              
              return {
                ...tx,
                matchedInflows
              };
            });
            
            // 🔧 WICHTIG: Setze die aktualisierten vip180Data
            setVip180Data(updatedVip180Data);
                        
            // Aktualisiere auch classifiedVip180Data mit matchedInflows
            if (snapshot.processedData?.classifiedVip180) {
              const updatedClassifiedVip180Data = (snapshot.processedData.classifiedVip180 || []).map(tx => {
                let matchedInflows = snapshot.processedData.matchedInflows?.[tx.hash] || [];
                if (matchedInflows.length === 0) {
                  const availableKeys = Object.keys(snapshot.processedData.matchedInflows || {});
                  const byHashKey = availableKeys.find(k => k.startsWith(tx.hash));
                  if (byHashKey) {
                    matchedInflows = snapshot.processedData.matchedInflows[byHashKey] || [];
                  }
                }
                if (matchedInflows.length === 0) {
                  const txKey = `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`;
                  matchedInflows = snapshot.processedData.matchedInflows[txKey] || [];
                }
                
                return {
                  ...tx,
                  matchedInflows
                };
              });
              
              setClassifiedVip180Data(updatedClassifiedVip180Data);
                          }
          }
          
          // Synthetische VTHO-Transaktionen
          if (snapshot.processedData?.syntheticVthoTxs) {
            // Hier würden wir die synthetischen VTHO-Txs wiederherstellen
                      }
          
          // 🔧 JETZT: FIFO-Ergebnisse laden, nachdem matchedInflows verarbeitet wurden
          if (snapshot.processedData?.fifoResults) {
            setFifoResultsState(snapshot.processedData.fifoResults);
            
          }
          
          // FIFO-Stacks (für Neuberechnungen)
          if (snapshot.processedData?.fifoStacks) {
            // Hier würden wir die FIFO-Stacks wiederherstellen
                      }
          
          // Steuerberechnungen
          if (snapshot.processedData?.taxCalculations) {
            // Hier würden wir die Steuerwerte wiederherstellen
                      }
          
          // 🔧 WICHTIG: B3TR-Daten mit matchedInflows aus FIFO-Ergebnissen verarbeiten
          // Dies geschieht NACH der Anreicherung der vip180Data
          if (snapshot.processedData?.fifoResults) {
            // Versuche zuerst, B3TR-Daten aus dem Snapshot zu laden
            let b3trDataToProcess = [];
            
            if (snapshot.processedData?.b3trTaxTableData && snapshot.processedData.b3trTaxTableData.length > 0) {
              // 🔧 WICHTIG: Verwende b3trTaxTableData zuerst (enthält bereits alle matchedInflows!)
              b3trDataToProcess = snapshot.processedData.b3trTaxTableData;
                          } else if (snapshot.processedData?.b3trData && snapshot.processedData.b3trData.length > 0) {
              b3trDataToProcess = snapshot.processedData.b3trData;
                          } else if (snapshot.processedData?.fifoResults?.matchedOutflows || snapshot.processedData?.fifoResults?.matchedSwapOuts) {
              // 🔧 NEU: Verwende FIFO-Ergebnisse direkt (enthält alle matchedInflows!)
              const allMatchedOutflows = [
                ...(snapshot.processedData.fifoResults.matchedOutflows || []),
                ...(snapshot.processedData.fifoResults.matchedSwapOuts || [])
              ];
              
              // Filtere nur B3TR-Transaktionen
              b3trDataToProcess = allMatchedOutflows.filter(tx => 
                (tx.token === "B3TR" || tx.token === "VEB3TR") &&
                ["airdrop", "Governance_reward", "swap_out", "sale", "sale_payment", "mint_payment", "nft_mint"].includes(
                  (tx.classification || tx.type || "").toLowerCase()
                )
              );
              
                          } else if (snapshot.processedData?.vip180Data) {
              // 🔧 KRITISCH KORRIGIERT: Verwende processedData.vip180Data (enthält bereits matchedInflows!)
              const rawB3trData = snapshot.processedData.vip180Data.filter(tx => 
                (tx.token === "B3TR" || tx.token === "VEB3TR") &&
                ["airdrop", "Governance_reward", "swap_out", "sale", "sale_payment", "mint_payment", "nft_mint"].includes(
                  (tx.classification || tx.type || "").toLowerCase()
                )
              );
              
              // 🔧 WICHTIG: Die matchedInflows sind bereits in den vip180Data vorhanden!
              b3trDataToProcess = rawB3trData.map(tx => {
                // Prüfe ob matchedInflows bereits vorhanden sind
                if (tx.matchedInflows?.length > 0) {
                                    return tx;
                }
                
                // 🔧 KRITISCH KORRIGIERT: Suche zuerst in den FIFO-Ergebnissen (enthält alle matchedInflows!)
                let matchedInflows = [];
                
                // 🔧 KRITISCH KORRIGIERT: Suche zuerst in den Top-Level FIFO-Ergebnissen
                
                // 1. Versuche zuerst, matchedInflows direkt aus den Top-Level FIFO-Ergebnissen zu holen
                if (snapshot.fifoResults) {
                  const matchedOutflow = snapshot.fifoResults.matchedOutflows?.find(m => m.hash === tx.hash);
                  const matchedSwapOut = snapshot.fifoResults.matchedSwapOuts?.find(m => m.hash === tx.hash);
                  
                  if (matchedOutflow?.matchedInflows?.length > 0) {
                    matchedInflows = matchedOutflow.matchedInflows;
                                      } else if (matchedSwapOut?.matchedInflows?.length > 0) {
                    matchedInflows = matchedSwapOut.matchedInflows;
                                      }
                }
                
                // 2. Fallback: Versuche es in processedData.fifoResults
                if (matchedInflows.length === 0 && snapshot.processedData?.fifoResults) {
                  const matchedOutflow = snapshot.processedData.fifoResults.matchedOutflows?.find(m => m.hash === tx.hash);
                  const matchedSwapOut = snapshot.processedData.fifoResults.matchedSwapOuts?.find(m => m.hash === tx.hash);
                  
                  if (matchedOutflow?.matchedInflows?.length > 0) {
                    matchedInflows = matchedOutflow.matchedInflows;
                                      } else if (matchedSwapOut?.matchedInflows?.length > 0) {
                    matchedInflows = matchedSwapOut.matchedInflows;
                                      }
                }
                
                // 2. Fallback: Suche in matchedInflows (falls FIFO-Ergebnisse leer sind)
                if (matchedInflows.length === 0 && snapshot.processedData?.matchedInflows) {
                  matchedInflows = snapshot.processedData.matchedInflows[tx.hash] || [];
                  
                  if (matchedInflows.length === 0) {
                    // Versuche Hash-basierte Suche
                    const availableKeys = Object.keys(snapshot.processedData.matchedInflows || {});
                    const byHashKey = availableKeys.find(k => k.startsWith(tx.hash));
                    if (byHashKey) {
                      matchedInflows = snapshot.processedData.matchedInflows[byHashKey] || [];
                                          }
                  }
                  
                  if (matchedInflows.length === 0) {
                    // Fallback: Versuche txKey-Varianten
                    const txKey = `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`;
                    matchedInflows = snapshot.processedData.matchedInflows[txKey] || [];
                    
                    if (matchedInflows.length > 0) {
                                          } else {
                                            
                      // 🔧 NEU: Versuche alternative txKey-Formate
                      const alternativeKeys = [
                        `${tx.hash}-${tx.block_timestamp || 0}`,
                        `${tx.hash}-${tx.timestamp || 0}`,
                        `${tx.hash}-0`
                      ];
                      
                      for (const altKey of alternativeKeys) {
                        if (snapshot.processedData.matchedInflows[altKey]) {
                                                    matchedInflows.push(...snapshot.processedData.matchedInflows[altKey]);
                          break;
                        }
                      }
                    }
                  }
                }
                
                return {
                  ...tx,
                  matchedInflows
                };
              });
              
                          } else {
              // 🔧 FALLBACK: Verwende die aktuellen vip180Data (die bereits mit matchedInflows angereichert wurden)
              b3trDataToProcess = (vip180Data || []).filter(tx => 
                (tx.token === "B3TR" || tx.token === "VEB3TR") &&
                ["airdrop", "Governance_reward", "swap_out", "sale", "sale_payment", "mint_payment", "nft_mint"].includes(
                  (tx.classification || tx.type || "").toLowerCase()
                )
              );
                            

            }
            
                          // Verarbeite die B3TR-Daten mit matchedInflows aus FIFO-Ergebnissen
              if (b3trDataToProcess.length > 0) {
                const updatedB3trData = b3trDataToProcess.map(tx => {
                  // 🔧 WICHTIG: Behalte bereits vorhandene matchedInflows (aus dem Snapshot)
                  if (tx.matchedInflows?.length > 0) {
                    return tx;
                  }
                  
                  // 🔧 KRITISCH: Verwende matchedInflows direkt aus den vip180Data des Snapshots
                  if (snapshot.vip180Data) {
                    const snapshotTx = snapshot.vip180Data.find(s => s.hash === tx.hash);
                    if (snapshotTx?.matchedInflows?.length > 0) {
                      return { ...tx, matchedInflows: snapshotTx.matchedInflows };
                    }
                  }
                  
                  // 🔧 KRITISCH: Fallback: Verwende matchedInflows aus sourceData.vip180Data
                  if (snapshot.sourceData?.vip180Data) {
                    const sourceTx = snapshot.sourceData.vip180Data.find(s => s.hash === tx.hash);
                    if (sourceTx?.matchedInflows?.length > 0) {
                      return { ...tx, matchedInflows: sourceTx.matchedInflows };
                    }
                  }
                  

                  
                  // Fallback: Suche nach matchedInflows in den FIFO-Ergebnissen
                  const matchedOutflow = snapshot.processedData.fifoResults.matchedOutflows?.find(m => m.hash === tx.hash);
                  const matchedSwapOut = snapshot.processedData.fifoResults.matchedSwapOuts?.find(m => m.hash === tx.hash);
                  
                  if (matchedOutflow?.matchedInflows?.length > 0) {
                    return { ...tx, matchedInflows: matchedOutflow.matchedInflows };
                  } else if (matchedSwapOut?.matchedInflows?.length > 0) {
                    return { ...tx, matchedInflows: matchedSwapOut.matchedInflows };
                  }
                  return tx;
                });
                
                // Aktualisiere die State-Variablen mit den angereicherten Daten
                setB3trTxs(updatedB3trData);
                setB3trTaxTableData(updatedB3trData);
                

                

        }
      }
          
          // 🎯 NEU: Alle verarbeiteten Daten für die Anzeige wiederherstellen
          if (snapshot.processedData?.b3trData) {
                        
            // 🔧 WICHTIG: Füge matchedInflows aus dem Snapshot hinzu (Hash-first)
            const b3trDataWithInflows = snapshot.processedData.b3trData.map(tx => {
              let matchedInflows = snapshot.processedData.matchedInflows?.[tx.hash] || [];
              if (matchedInflows.length === 0) {
                const availableKeys = Object.keys(snapshot.processedData.matchedInflows || {});
                const byHashKey = availableKeys.find(k => k.startsWith(tx.hash));
                if (byHashKey) {
                  matchedInflows = snapshot.processedData.matchedInflows[byHashKey] || [];
                }
              }
              if (matchedInflows.length === 0) {
                const txKey = `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`;
                matchedInflows = snapshot.processedData.matchedInflows?.[txKey] || [];
              }
              

              
              return {
                ...tx,
                matchedInflows
              };
            });
            
            setB3trTxs(b3trDataWithInflows);
            setB3trTaxTableData(b3trDataWithInflows);
            

            
          } else if (snapshot.processedData?.b3trTaxTableData && snapshot.processedData.b3trTaxTableData.length > 0) {
            // 🔧 KORRIGIERT: Verwende b3trTaxTableData UND füge matchedInflows aus FIFO-Ergebnissen hinzu

            
            // 🔧 WICHTIG: Füge matchedInflows aus FIFO-Ergebnissen hinzu (funktioniert garantiert!)
            const b3trTaxTableDataWithInflows = snapshot.processedData.b3trTaxTableData.map(tx => {
              // 🔧 NEU: Suche nach matchedInflows in FIFO-Ergebnissen
              const matchedOutflow = snapshot.processedData.fifoResults?.matchedOutflows?.find(m => m.hash === tx.hash);
              const matchedSwapOut = snapshot.processedData.fifoResults?.matchedSwapOuts?.find(m => m.hash === tx.hash);
              
              let matchedInflows = [];
              
              if (matchedOutflow?.matchedInflows?.length > 0) {
                matchedInflows = matchedOutflow.matchedInflows;
              } else if (matchedSwapOut?.matchedInflows?.length > 0) {
                matchedInflows = matchedSwapOut.matchedInflows;
              }
              
              return {
                ...tx,
                matchedInflows
              };
            });
            
            setB3trTaxTableData(b3trTaxTableDataWithInflows);
            

            
          } else {
            // 🔧 VEREINFACHT: Verwende vip180Data aus sourceData als Fallback
            const b3trFromVip180 = (snapshot.sourceData?.vip180Data || []).filter(tx => 
              (tx.token === "B3TR" || tx.token === "VEB3TR") &&
              (tx.classification === "swap_out" || tx.type === "swap_out" ||
               tx.classification === "airdrop" || tx.type === "airdrop" ||
               tx.classification === "Governance_reward" || tx.type === "Governance_reward")
            );
            
            // 🔧 KRITISCH: Verwende matchedInflows direkt aus den vip180Data des Snapshots
            const b3trFromVip180WithInflows = b3trFromVip180.map(tx => {
              // Verwende matchedInflows direkt aus den vip180Data des Snapshots
              if (snapshot.vip180Data) {
                const snapshotTx = snapshot.vip180Data.find(s => s.hash === tx.hash);
                if (snapshotTx?.matchedInflows?.length > 0) {
                  return { ...tx, matchedInflows: snapshotTx.matchedInflows };
                }
              }
              
              // Fallback: Verwende matchedInflows aus sourceData.vip180Data
              if (snapshot.sourceData?.vip180Data) {
                const sourceTx = snapshot.sourceData.vip180Data.find(s => s.hash === tx.hash);
                if (sourceTx?.matchedInflows?.length > 0) {
                  return { ...tx, matchedInflows: sourceTx.matchedInflows };
                }
              }
              
              return tx;
            });
            
            // 🔧 VEREINFACHT: Setze die B3TR-Daten direkt
            setB3trTaxTableData(b3trFromVip180WithInflows);
          }
          
          if (processed.vthoTaxData) {
            setVthoTaxData(processed.vthoTaxData);
                      }
          
          if (processed.vetTaxTableData) {
            setVetTaxTableData(processed.vetTaxTableData);
                      }
          
          // 🔧 KORRIGIERT: Verwende die korrekten Feldnamen aus dem Snapshot
          if (processed.nftMints && processed.nftMints.length > 0) {
                        setNftTxs(processed.nftMints);
            setNftFifoData(processed.nftMints);
                      }
          
          if (processed.nftSales && processed.nftSales.length > 0) {
            setEnrichedNftData(processed.nftSales);
            
            // 🛡️ WICHTIG: Kombiniere NFT-Sales mit NFT-Mints für nftTaxTableData
            const allNftDataForSnapshotTaxTable = [...processed.nftSales];
            
            // Füge NFT-Mints hinzu, falls vorhanden
            if (processed.nftMints && processed.nftMints.length > 0) {
              const nftMintsNotInSnapshotTaxTable = processed.nftMints.filter(nft => 
                (nft.classification === "mint" || nft.type === "mint") &&
                !processed.nftSales.some(saleNft => saleNft.hash === nft.hash)
              );
              
              if (nftMintsNotInSnapshotTaxTable.length > 0) {
                allNftDataForSnapshotTaxTable.push(...nftMintsNotInSnapshotTaxTable);
              }
            }
            
            setNftTaxTableData(allNftDataForSnapshotTaxTable);
                      }
          
          // 🔧 FALLBACK: Wenn keine spezifischen NFT-Felder vorhanden, verwende die alten Felder
          if (!processed.nftMints && !processed.nftSales) {
            if (processed.nftTaxTableData) {
              // 🛡️ WICHTIG: Kombiniere nftTaxTableData mit NFT-Verkäufen
              const allNftDataForSnapshotTaxTableFallback = [...processed.nftTaxTableData];
              
              // Füge NFT-Verkäufe hinzu, falls vorhanden
              if (processed.nftSales && processed.nftSales.length > 0) {
                const nftSalesNotInSnapshotTaxTableFallback = processed.nftSales.filter(nft => 
                  (nft.classification === "sale" || nft.type === "sale") &&
                  !processed.nftTaxTableData.some(taxNft => taxNft.hash === nft.hash)
                );
                
                if (nftSalesNotInSnapshotTaxTableFallback.length > 0) {
                  allNftDataForSnapshotTaxTableFallback.push(...nftSalesNotInSnapshotTaxTableFallback);
                }
              }
              
              setNftTaxTableData(allNftDataForSnapshotTaxTableFallback);
                          } else if (processed.enrichedNftData && processed.enrichedNftData.length > 0) {
                            
              // 🛡️ WICHTIG: Kombiniere enrichedNftData mit NFT-Verkäufen
              const allNftDataForSnapshotEnrichedFallback = [...processed.enrichedNftData];
              
              // Füge NFT-Verkäufe hinzu, falls vorhanden
              if (processed.nftSales && processed.nftSales.length > 0) {
                const nftSalesNotInSnapshotEnrichedFallback = processed.nftSales.filter(nft => 
                  (nft.classification === "sale" || nft.type === "sale") &&
                  !processed.enrichedNftData.some(enrichedNft => enrichedNft.hash === nft.hash)
                );
                
                if (nftSalesNotInSnapshotEnrichedFallback.length > 0) {
                                    allNftDataForSnapshotEnrichedFallback.push(...nftSalesNotInSnapshotEnrichedFallback);
                }
              }
              
              setNftTaxTableData(allNftDataForSnapshotEnrichedFallback);
                                        } else if (processed.nftTxs && processed.nftTxs.length > 0) {
                            
              // 🛡️ WICHTIG: Kombiniere nftTxs mit NFT-Verkäufen
              const allNftDataForSnapshotTxsFallback = [...processed.nftTxs];
              
              // Füge NFT-Verkäufe hinzu, falls vorhanden
              if (processed.nftSales && processed.nftSales.length > 0) {
                const nftSalesNotInSnapshotTxsFallback = processed.nftSales.filter(nft => 
                  (nft.classification === "sale" || nft.type === "sale") &&
                  !processed.nftTxs.some(txNft => txNft.hash === nft.hash)
                );
                
                if (nftSalesNotInSnapshotTxsFallback.length > 0) {
                                    allNftDataForSnapshotTxsFallback.push(...nftSalesNotInSnapshotTxsFallback);
                }
              }
              
              setNftTaxTableData(allNftDataForSnapshotTxsFallback);
                                        } else {
                                                                                                }
          }
          
          // 🚫 KORRIGIERT: Lade enrichedNftData direkt aus dem Snapshot
          if (processed.enrichedNftData && processed.enrichedNftData.length > 0) {
            setEnrichedNftData(processed.enrichedNftData);
                      } else if (processed.nftTxs && processed.nftTxs.length > 0) {
            // Fallback: Verwende nftTxs als enrichedNftData
            setEnrichedNftData(processed.nftTxs);
                      } else {
            // Keine NFT-Daten im Snapshot gefunden
                      }
          
          // 🚫 KORRIGIERT: Stelle auch nftFifoData aus dem Snapshot wieder her
          if (processed.nftFifoData && processed.nftFifoData.length > 0) {
            // 🛡️ WICHTIG: Kombiniere NFT-FIFO-Daten mit NFT-Verkäufen
            const allNftDataForSnapshotFifo = [...processed.nftFifoData];
            
            // Füge NFT-Verkäufe hinzu, falls vorhanden
            if (processed.nftSales && processed.nftSales.length > 0) {
              const nftSalesNotInSnapshotFifo = processed.nftSales.filter(nft => 
                (nft.classification === "sale" || nft.type === "sale") &&
                !processed.nftFifoData.some(fifoNft => fifoNft.hash === nft.hash)
              );
              
              if (nftSalesNotInSnapshotFifo.length > 0) {
                                allNftDataForSnapshotFifo.push(...nftSalesNotInSnapshotFifo);
              }
            }
            
            setNftFifoData(allNftDataForSnapshotFifo);
                      } else if (processed.enrichedNftData && processed.enrichedNftData.length > 0) {
            // Fallback: Verwende enrichedNftData als nftFifoData
            // 🛡️ WICHTIG: Kombiniere enrichedNftData mit NFT-Verkäufen
            const allNftDataForSnapshotEnriched = [...processed.enrichedNftData];
            
            // Füge NFT-Verkäufe hinzu, falls vorhanden
            if (processed.nftSales && processed.nftSales.length > 0) {
              const nftSalesNotInSnapshotEnriched = processed.nftSales.filter(nft => 
                (nft.classification === "sale" || nft.type === "sale") &&
                !processed.enrichedNftData.some(enrichedNft => enrichedNft.hash === nft.hash)
              );
              
              if (nftSalesNotInSnapshotEnriched.length > 0) {
                                allNftDataForSnapshotEnriched.push(...nftSalesNotInSnapshotEnriched);
              }
            }
            
            setNftFifoData(allNftDataForSnapshotEnriched);
                      } else if (processed.nftTxs && processed.nftTxs.length > 0) {
            // Zweiter Fallback: Verwende nftTxs als nftFifoData
            // 🛡️ WICHTIG: Kombiniere nftTxs mit NFT-Verkäufen
            const allNftDataForSnapshotTxs = [...processed.nftTxs];
            
            // Füge NFT-Verkäufe hinzu, falls vorhanden
            if (processed.nftSales && processed.nftSales.length > 0) {
              const nftSalesNotInSnapshotTxs = processed.nftSales.filter(nft => 
                (nft.classification === "sale" || nft.type === "sale") &&
                !processed.nftTxs.some(txNft => txNft.hash === nft.hash)
              );
              
              if (nftSalesNotInSnapshotTxs.length > 0) {
                                allNftDataForSnapshotTxs.push(...nftSalesNotInSnapshotTxs);
              }
            }
            
            setNftFifoData(allNftDataForSnapshotTxs);
                      } else {
            // Keine NFT FIFO-Daten im Snapshot gefunden
                      }
          
          // 🔍 DEBUG: NFT-Daten Status nach dem Setzen (ENTFERNT - zu viel Output)
          
          // 🚫 BLOCKIERE alle weiteren API-Aufrufe
                    
            // 🚫 WICHTIG: Blockiere alle weiteren API-Aufrufe
    
  // 🚫 WICHTIG: Blockiere alle weiteren API-Aufrufe in den Komponenten
    
  // 🚫 WICHTIG: Blockiere alle weiteren API-Aufrufe in den Komponenten
            
          // 🎯 WICHTIG: Stelle auch die ursprünglichen API-Daten wieder her
          if (processed.classifiedVip180) {
            // 🚫 KORRIGIERT: Reichere classifiedVip180 mit matchedInflows an
            const enrichedClassifiedVip180 = (processed.classifiedVip180 || []).map(tx => {
              const txKey = `${tx.hash}-${tx.timestamp || tx.block_timestamp || 0}`;
              const matchedInflows = processed.matchedInflows?.[txKey] || [];
              
              return {
                ...tx,
                matchedInflows
              };
            });
            
            setVip180Data(enrichedClassifiedVip180);
            setClassifiedVip180Data(enrichedClassifiedVip180); // 🚫 NEU: Setze auch classifiedVip180Data
                        
            // 🔍 DEBUG: VIP180-Daten Klassifizierungen im Snapshot (ENTFERNT - zu viel Output)
          }
          
          if (processed.classifiedVetVtho) {
            setVetVthoData(processed.classifiedVetVtho);
                        
            // 🔍 DEBUG: VET/VTHO-Daten Klassifizierungen im Snapshot (ENTFERNT - zu viel Output)
          }
          
          if (processed.nftTxs) {
            setNftTxs(processed.nftTxs);
                      }
          
          // 🎯 NEU: Stelle gespeicherte Swaps aus dem Snapshot wieder her
          if (processed.detectedSwaps) {
            setDetectedSwaps(processed.detectedSwaps);
                        
            // 🔍 DEBUG: Alle gespeicherten Swaps im Snapshot (ENTFERNT - zu viel Output)
          } else {
            // 🔍 DEBUG: Prüfe ob bereits Swap-Klassifizierungen in den Daten vorhanden sind
            const existingSwapsInVip180 = processed.classifiedVip180?.filter(tx => 
              tx.classification === "swap_in" || tx.classification === "swap_out"
            ) || [];
            const existingSwapsInVetVtho = processed.classifiedVetVtho?.filter(tx => 
              tx.classification === "swap_in" || tx.classification === "swap_out"
            ) || [];
            
            const totalExistingSwaps = existingSwapsInVip180.length + existingSwapsInVetVtho.length;
            
            if (totalExistingSwaps > 0) {
                            
              // Erstelle detectedSwaps aus den bereits klassifizierten Daten
              const detectedSwapsFromData = [
                ...existingSwapsInVip180.map(tx => ({
                  hash: tx.hash,
                  classification: tx.classification,
                  type: tx.type,
                  token: tx.token,
                  sender: tx.sender,
                  recipient: tx.recipient,
                  timestamp: tx.timestamp
                })),
                ...existingSwapsInVetVtho.map(tx => ({
                  hash: tx.hash,
                  classification: tx.classification,
                  type: tx.type,
                  token: tx.token,
                  sender: tx.sender,
                  recipient: tx.recipient,
                  timestamp: tx.timestamp
                }))
              ];
              
              setDetectedSwaps(detectedSwapsFromData);
                          } else {
                            setDetectedSwaps([]);
            }
          }
        }
        
        // 🎯 NEU: Stelle User-Inputs wieder her
        if (snapshot.sourceData?.userInputs) {
          const userInputs = snapshot.sourceData.userInputs;
          
          if (userInputs.classifications) {
            setManualClassifications(userInputs.classifications);
                      }
          
          if (userInputs.nonTaxableInflows) {
            setNonTaxableInflows(userInputs.nonTaxableInflows);
                      }
          
          if (userInputs.nonTaxableSales) {
            setNonTaxableSales(userInputs.nonTaxableSales);
                      }
        }
        
                          
        // 🔍 DEBUG: Zeige die tatsächlich geladenen Snapshot-Daten
                                                                
      } else {
                // Fallback: Normale Berechnung starten
        if (fromViewAnalysis) {
          // Wenn View Analysis geklickt wurde aber kein Snapshot existiert, starte neue Berechnung
          setTimeout(() => {
            // 🚀 ENTFERNT: loadAndMatch() - wurde ins Backend transferiert
                        // TODO: Implementiere Backend-Aufruf für neue Berechnung
          }, 1000);
        }
      }
    } catch (error) {
            toast.error(language === 'en' ? 'Error loading snapshot' : 'Fehler beim Laden des Snapshots');
      
      // Fallback: Zeige Fehlermeldung
          }
  };

  // 🚀 NEU: Backend-Analyse State
  const [backendData, setBackendData] = useState(null);

  useEffect(() => {
    backendDataRef.current = backendData;
    if (backendData) {
      refreshSavedManualOverrideCount();
    }
  }, [backendData, refreshSavedManualOverrideCount]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // --- RAW EXPORT (ohne UI Tabellen) ---
  const rawVetVthoForExport = backendData?.dataset?.vetVthoData || vetVthoData || [];
  const rawVip180ForExport  = backendData?.dataset?.vip180Data || vip180Data || [];

  const [exportPrepEnabled, setExportPrepEnabled] = useState(false);

  const rawTransferCount = useMemo(
    () => (rawVetVthoForExport?.length || 0) + (rawVip180ForExport?.length || 0),
    [rawVetVthoForExport, rawVip180ForExport]
  );

  const deferExportPrep = shouldDeferExportPrep(rawTransferCount);

  useEffect(() => {
    if (!deferExportPrep) setExportPrepEnabled(true);
  }, [deferExportPrep]);

  const enableExportPrep = useCallback(() => {
    setExportPrepEnabled(true);
    uiPerfLog("Export prep enabled on demand", { rawTransferCount });
  }, [rawTransferCount]);

  const exportCallbacksEnabled = exportPrepEnabled || !deferExportPrep;

  const vetVthoExportComputed = useMemo(() => {
    if (deferExportPrep && !exportPrepEnabled) {
      return [];
    }

    const source = Array.isArray(rawVetVthoForExport)
      ? rawVetVthoForExport
      : [];

    if (!source.length) {
      return [];
    }

    return source
      .map((tx, index) =>
        buildRawLedgerExportRow(
          tx,
          manualClassifications,
          index
        )
      )
      .sort(
        (a, b) =>
          Number(b.timestamp || 0) -
          Number(a.timestamp || 0)
      );
  }, [
    rawVetVthoForExport,
    manualClassifications,
    deferExportPrep,
    exportPrepEnabled,
  ]);

  const vip180ExportComputed = useMemo(() => {
    if (deferExportPrep && !exportPrepEnabled) {
      return [];
    }

    const source = Array.isArray(rawVip180ForExport)
      ? rawVip180ForExport
      : [];

    if (!source.length) {
      return [];
    }

    return source
      .map((tx, index) =>
        buildRawLedgerExportRow(
          tx,
          manualClassifications,
          index
        )
      )
      .sort(
        (a, b) =>
          Number(b.timestamp || 0) -
          Number(a.timestamp || 0)
      );
  }, [
    rawVip180ForExport,
    manualClassifications,
    deferExportPrep,
    exportPrepEnabled,
  ]);
  
  const initialAutorunMode =
    location.state?.autorun ||
    (typeof window !== "undefined"
      ? getAdminReplayAutorunMode(location.search, location.pathname)
      : null);
  const hasInitialAutorun = initialAutorunMode === 'calc' || initialAutorunMode === 'view';
  const adminReplayLaunch = useMemo(
    () =>
      isAdminReplayUrl(location.search)
        ? readAdminReplayLaunch({
            search: location.search,
            pathname: location.pathname,
          })
        : null,
    [location.search, location.pathname]
  );

  // 🎨 UI-Phase und Progress für professionelles Loading
  const [uiPhase, setUiPhase] = useState(hasInitialAutorun ? 'loading' : 'idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const [analysisError, setAnalysisError] = useState(null);
  const [progress, setProgress] = useState(hasInitialAutorun ? 5 : 0);

  const [analysisProgressState, setAnalysisProgressState] = useState({
    jobId: null,
    currentStep: "initializing",
    completedSteps: [],
    message: hasInitialAutorun
      ? (language === "en" ? "Preparing analysis" : "Analyse wird vorbereitet")
      : "",
    detail: hasInitialAutorun
      ? (
          initialAutorunMode === "view"
            ? (language === "en"
                ? "Opening saved analysis and restoring your snapshot"
                : "Gespeicherte Analyse wird geöffnet und dein Snapshot wird wiederhergestellt")
            : (language === "en"
                ? "Loading analysis workspace and preparing blockchain data"
                : "Analysebereich wird geladen und Blockchain-Daten werden vorbereitet")
        )
      : "",
    indeterminate: false,
  });

  const analysisPollAbortRef = useRef(null);
  const activeAnalysisJobIdRef = useRef(null);
  const progressRef = useRef(0);
  const progressAnimRafRef = useRef(null);
  const transferTickerRef = useRef(null);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    return () => {
      if (progressAnimRafRef.current) cancelAnimationFrame(progressAnimRafRef.current);
      if (transferTickerRef.current) clearInterval(transferTickerRef.current);
    };
  }, []);

  // ⛑ Cache: nur Meta (kein backendData — vermeidet synchrones JSON.stringify bei 300MB+)
  useEffect(() => {
    if (!cacheKey) return;
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.uiPhase) {
          setUiPhase(cached.uiPhase);
          setProgress(cached.progress ?? 0);
        }
      }
    } catch (e) {
          }
  }, [cacheKey]);

  useEffect(() => {
    if (!cacheKey) return;
    if (uiPhase === 'ready' && backendData) {
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({
            uiPhase: 'ready',
            progress: 100,
            datasetId: actualDatasetId,
            year: finalYear,
            selectedCountry,
            cachedAt: Date.now(),
          })
        );
      } catch (e) {
              }
    }
  }, [cacheKey, uiPhase, backendData, actualDatasetId, finalYear, selectedCountry]);

  // ✅ FIX Schritt 1: Hilfsfunktion zur robusten Adress-Extraktion
  const deriveAddresses = (ds, backend, fallbackWallet) => {
    const candidates = [
      // 1. Dataset-Status Quelle (aus GET /api/datasets/:id/status)
      // 🚀 PHASE 4: Nur aktive Wallets für Berechnung verwenden
      ds?.dataset?.wallets?.filter(w => w.is_active !== false),
      ds?.dataset?.wallets?.filter(w => w.is_active !== false)?.map(w => w.main_wallet || w.address || w).filter(Boolean),
      ds?.dataset?.walletSet?.addresses,
      ds?.wallets?.filter(w => w.is_active !== false),
      ds?.wallets?.filter(w => w.is_active !== false)?.map(w => w.main_wallet || w.address || w).filter(Boolean),
      ds?.addresses,
      // 2. Backend Analysis-Response Quelle (aus POST /api/datasets/:id/calculate)
      // ✅ WICHTIG: balanceVerifier.addresses ist die wichtigste Quelle!
      backend?.balanceVerifier?.addresses,
      backend?.balanceVerifier?.parameters?.addresses,
      // ✅ WICHTIG: analysisResult kann auch direkt unter backend liegen
      backend?.analysisResult?.balanceVerifier?.addresses,
      // 🚀 PHASE 4: Nur aktive Wallets für Berechnung verwenden
      backend?.analysisResult?.dataset?.wallets?.filter(w => w.is_active !== false)?.map(w => w.main_wallet || w.address || w).filter(Boolean),
      // Andere mögliche Felder
      backend?.engineResults?.addresses,
      backend?.engineResults?.addressesAnalyzed,
      backend?.results?.wallets,
      backend?.meta?.wallets,
      backend?.meta?.addresses,
      // ✅ WICHTIG: dataset.wallets aus Response (kann direkt unter backend.dataset sein)
      // 🚀 PHASE 4: Nur aktive Wallets für Berechnung verwenden
      backend?.dataset?.wallets?.filter(w => w.is_active !== false)?.map(w => w.main_wallet || w.address || w).filter(Boolean),
      backend?.dataset?.addresses,
      backend?.addresses,
      // 3. Direkte Array-Felder
      Array.isArray(backend?.wallets) ? backend.wallets : null,
      Array.isArray(backend?.addresses) ? backend.addresses : null,
      // 4. Fallback
      fallbackWallet ? [fallbackWallet] : []
    ].filter(Boolean);

    const flat = [...new Set(
      candidates.flat().filter(Boolean).map(a => {
        // Handle sowohl Strings als auch Objekte
        if (typeof a === 'string') return a.toLowerCase().trim();
        if (a?.main_wallet) return String(a.main_wallet).toLowerCase().trim();
        if (a?.address) return String(a.address).toLowerCase().trim();
        return String(a).toLowerCase().trim();
      })
    )].filter(Boolean);

    return flat;
  };

  const stopAnalysisPolling = useCallback(() => {
    if (analysisPollAbortRef.current) {
      analysisPollAbortRef.current.abort();
      analysisPollAbortRef.current = null;
    }
  }, []);

  const BACKEND_PIPELINE_STEPS = [
    "initializing",
    "fetching",
    "classifying",
    "pricing",
    "fifo",
    "finalizing",
  ];

  const OVERLAY_PIPELINE_STEPS = [
    ...BACKEND_PIPELINE_STEPS,
    "transferring_results",
  ];

  const overlayProgressPct = useCallback((step, backendProgress = 0) => {
    if (step === "done") return 100;
    const idx = OVERLAY_PIPELINE_STEPS.indexOf(step);
    if (idx < 0) return 5;
    const segmentSize = 100 / OVERLAY_PIPELINE_STEPS.length;
    const inSegment = Math.max(0, Math.min(1, Number(backendProgress) / 100));
    return Math.min(99, Math.round(idx * segmentSize + inSegment * segmentSize));
  }, []);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const ANALYSIS_POLL_TIMEOUT_MS = 45_000;
  const ANALYSIS_RESULT_TIMEOUT_MS = 180_000;
  const ANALYSIS_RESULT_MAX_ATTEMPTS = 6;
  const ANALYSIS_POLL_NETWORK_RETRIES = 8;
  const ANALYSIS_TRANSFER_WATCHDOG_MS = 30 * 60_000;

  const composeAbortSignals = (...signals) => {
    const usable = signals.filter(Boolean);
    if (!usable.length) return undefined;
    if (typeof AbortSignal !== "undefined" && typeof AbortSignal.any === "function") {
      return AbortSignal.any(usable);
    }
    const controller = new AbortController();
    const abort = () => controller.abort();
    for (const signal of usable) {
      if (signal.aborted) {
        abort();
        break;
      }
      signal.addEventListener("abort", abort, { once: true });
    }
    return controller.signal;
  };

  const fetchWithTimeout = async (url, options = {}, timeoutMs = 20_000) => {
    const timeoutController = new AbortController();
    const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
    const signal = composeAbortSignals(options.signal, timeoutController.signal);
    try {
      return await fetch(url, { ...options, signal });
    } catch (err) {
      if (timeoutController.signal.aborted) {
        const timeoutErr = new Error(
          options.timeoutMessage || `Request timed out after ${timeoutMs}ms`
        );
        timeoutErr.code = "FETCH_TIMEOUT";
        throw timeoutErr;
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  };

  const describeAnalysisTransportError = (error, { status = 0, errorCode = "" } = {}) => {
    const isEnglish = String(language || "").toLowerCase().startsWith("en");
    const code = String(errorCode || error?.code || "").toUpperCase();
    const msg = String(error?.message || "").toLowerCase();

    if (code === "JOB_NOT_FOUND" || status === 404) {
      return isEnglish
        ? "The analysis session was lost — usually after a server restart. Please start the calculation again."
        : "Die Analyse-Sitzung ging verloren — meist nach einem Server-Neustart. Bitte die Berechnung erneut starten.";
    }
    if (code === "FETCH_TIMEOUT" || msg.includes("timed out") || error?.name === "AbortError") {
      return isEnglish
        ? "The server did not respond in time. Please try again."
        : "Der Server hat nicht rechtzeitig geantwortet. Bitte erneut versuchen.";
    }
    if (msg.includes("failed to fetch") || msg.includes("network")) {
      return isEnglish
        ? "Connection to the server failed. If the backend was restarted, please run the calculation again."
        : "Verbindung zum Server fehlgeschlagen. Nach einem Backend-Neustart bitte die Berechnung erneut starten.";
    }
    return error?.message || (isEnglish ? "Analysis failed" : "Analyse fehlgeschlagen");
  };

  const stopTransferProgressTicker = useCallback(() => {
    if (transferTickerRef.current) {
      clearInterval(transferTickerRef.current);
      transferTickerRef.current = null;
    }
  }, []);

  const startTransferProgressTicker = useCallback((cap = 99) => {
    stopTransferProgressTicker();
    transferTickerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= cap) return prev;
        const remaining = cap - prev;
        const bump =
          remaining > 8
            ? Math.max(0.35, remaining * 0.045)
            : Math.max(0.08, remaining * 0.018);
        return Math.min(cap, Math.round((prev + bump) * 10) / 10);
      });
    }, 320);
  }, [stopTransferProgressTicker]);

  const animateProgressTo = useCallback((target, durationMs = 700) => {
    stopTransferProgressTicker();
    if (progressAnimRafRef.current) cancelAnimationFrame(progressAnimRafRef.current);

    const from = progressRef.current;
    if (from >= target) {
      setProgress(target);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const started = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - started) / durationMs);
        const eased = 1 - (1 - t) ** 3;
        const next = Math.round(from + (target - from) * eased);
        setProgress(next);
        if (t < 1) {
          progressAnimRafRef.current = requestAnimationFrame(tick);
        } else {
          progressAnimRafRef.current = null;
          setProgress(target);
          resolve();
        }
      };
      progressAnimRafRef.current = requestAnimationFrame(tick);
    });
  }, [stopTransferProgressTicker]);

  const finalizeAnalysisOverlay = useCallback(async () => {
    const isEnglish = String(language || "").toLowerCase().startsWith("en");
    stopTransferProgressTicker();
    setProgress(100);
    setAnalysisProgressState((prev) => ({
      ...prev,
      currentStep: "done",
      completedSteps: [...OVERLAY_PIPELINE_STEPS],
      message: isEnglish ? "Ready" : "Fertig",
      detail: isEnglish ? "Opening report…" : "Auswertung wird geöffnet…",
      indeterminate: false,
    }));
    await sleep(850);
  }, [language, stopTransferProgressTicker]);

  const showTransferringResultsPhase = useCallback(
    (detail, { transferRatio = 0, startTicker = true, tickerCap = 99 } = {}) => {
      const isEnglish = String(language || "").toLowerCase().startsWith("en");
      const nextPct = overlayProgressPct("transferring_results", transferRatio * 100);
      setProgress((prev) => Math.max(prev, nextPct));
      setAnalysisProgressState((prev) => ({
        ...prev,
        currentStep: "transferring_results",
        completedSteps: BACKEND_PIPELINE_STEPS,
        message: isEnglish
          ? "Transferring results"
          : "Ergebnisse werden übertragen",
        detail:
          detail ||
          (isEnglish
            ? "Loading calculation results from the server. Large wallets may take a moment."
            : "Berechnungsergebnisse werden vom Server geladen. Bei großen Wallets kann das etwas dauern."),
        indeterminate: false,
      }));
      if (startTicker) startTransferProgressTicker(tickerCap);
    },
    [language, overlayProgressPct, startTransferProgressTicker]
  );

  const buildAnalysisError = useCallback((error, context = {}) => {
    const isEnglish = String(language || "").toLowerCase().startsWith("en");

    const technicalDetail = [
      String(error?.message || "").trim(),
      String(context?.detail || "").trim(),
      context?.source ? `source: ${context.source}` : "",
      context?.jobId || analysisProgressState.jobId
        ? `jobId: ${context.jobId || analysisProgressState.jobId}`
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    const step = context.step || analysisProgressState.currentStep || null;

    return {
      detail:
        technicalDetail ||
        (isEnglish ? "Unknown calculation error" : "Unbekannter Berechnungsfehler"),
      code: context.code || error?.code || "ANALYSIS_FAILED",
      source: context.source || "analysis",
      step,
      jobId: context.jobId || analysisProgressState.jobId || null,
      datasetId:
        context.datasetId || actualDatasetId || location.state?.datasetId || null,
      taxYear: context.year || finalYear || null,
      ts: new Date().toISOString(),
    };
  }, [
    language,
    analysisProgressState.currentStep,
    analysisProgressState.jobId,
    actualDatasetId,
    location.state?.datasetId,
    finalYear,
  ]);

  const resetAnalysisFailureState = useCallback(() => {
    analysisFailedRef.current = false;
    setAnalysisError(null);
    setBackendData(null);

    if (cacheKey) {
      try {
        sessionStorage.removeItem(cacheKey);
      } catch (_) {}
    }
  }, [cacheKey]);

  const failAnalysis = useCallback(
    (error, context = {}) => {
      analysisFailedRef.current = true;

      stopTransferProgressTicker();
      stopAnalysisPolling();

      if (cacheKey) {
        try {
          sessionStorage.removeItem(cacheKey);
        } catch (_) {}
      }

      setBackendData(null);
      setAnalysisError(buildAnalysisError(error, context));
      setUiPhase("error");
      setProgress((prev) => Math.min(Number(prev || 0), 99));
      setIsAnalyzing(false);
    },
    [buildAnalysisError, stopTransferProgressTicker, stopAnalysisPolling, cacheKey]
  );

  const persistAnalysisReturnDraft = useCallback(() => {
    const routeWallets = Array.isArray(location.state?.wallets)
      ? location.state.wallets
      : [];

    const fallbackWallets = walletAddress
      ? [
          {
            wallet: walletAddress,
            delegateWallet: veDelegateWallet || null,
            year: finalYear,
            plan,
          },
        ]
      : [];

    const wallets = routeWallets.length ? routeWallets : fallbackWallets;

    const draft = {
      wallet: wallets[0]?.wallet || walletAddress || "",
      delegateWallet: wallets[0]?.delegateWallet || veDelegateWallet || null,
      year: finalYear,
      plan,
      country: selectedCountry,
      analyzedWallets: wallets,
      returnSource: "analysis_failure",
    };

    try {
      sessionStorage.setItem("v3tax:draft_wallet_set", JSON.stringify(draft));
      sessionStorage.setItem("v3tax:return_intent", "back");
      localStorage.setItem("analyzedWallets", JSON.stringify(wallets));
    } catch (_) {}
  }, [
    location.state,
    walletAddress,
    veDelegateWallet,
    finalYear,
    plan,
    selectedCountry,
  ]);

  const handleBackFromAnalysisError = useCallback(() => {
    stopAnalysisPolling();
    stopTransferProgressTicker();
    persistAnalysisReturnDraft();

    navigate("/", {
      replace: true,
      state: {
        restoreDraft: true,
        fromAnalysisCrash: true,
        openMyWalletsDashboard: Boolean(actualDatasetId || location.state?.datasetId),
        datasetId: actualDatasetId || location.state?.datasetId || null,
        year: finalYear,
        selectedCountry,
      },
    });
  }, [
    stopAnalysisPolling,
    stopTransferProgressTicker,
    persistAnalysisReturnDraft,
    navigate,
    actualDatasetId,
    location.state,
    finalYear,
    selectedCountry,
  ]);

  useEffect(() => {
    return () => stopAnalysisPolling();
  }, [stopAnalysisPolling]);

  useEffect(() => {
    const detachActiveJob = () => {
      const jobId = activeAnalysisJobIdRef.current;
      if (!jobId) return;
      const detachUrl = apiUrl(`/api/analysis-jobs/${jobId}/detach`);

      // Best effort: keepalive lets the request survive tab teardown. The
      // backend waits through a grace period, so an immediate reopen can attach
      // to the same job instead of starting another blockchain scan.
      try {
        if (
          typeof navigator !== "undefined" &&
          typeof navigator.sendBeacon === "function" &&
          navigator.sendBeacon(detachUrl)
        ) {
          return;
        }
        fetch(detachUrl, {
          method: "POST",
          credentials: "include",
          keepalive: true,
        }).catch(() => {});
      } catch (_) {}
    };

    window.addEventListener("pagehide", detachActiveJob);
    return () => {
      window.removeEventListener("pagehide", detachActiveJob);
      detachActiveJob();
    };
  }, []);

  const applyAnalysisJobState = useCallback((job) => {
    if (!job) return;

    const isEnglish = String(language || "").toLowerCase().startsWith("en");
    const step = job.currentStep || "initializing";
    const localizedStepMessageMap = {
      initializing: isEnglish ? "Preparing analysis" : "Analyse wird vorbereitet",
      fetching: isEnglish ? "Fetching transfers" : "Transfers werden geladen",
      classifying: isEnglish ? "Classifying transactions" : "Transaktionen werden klassifiziert",
      pricing: isEnglish ? "Building price index" : "Preisindex wird aufgebaut",
      fifo: isEnglish ? "Running FIFO" : "FIFO wird ausgeführt",
      finalizing: isEnglish ? "Finalizing analysis" : "Analyse wird finalisiert",
      transferring_results: isEnglish
        ? "Transferring results"
        : "Ergebnisse werden übertragen",
      done: isEnglish ? "Ready" : "Fertig",
    };

    const rawDetail = String(job.detail || "");
    const normalizedDetail = (() => {
      if (!rawDetail) return "";
      if (!isEnglish) {
        // Backend (Engine/recompute) liefert detail fast immer auf Englisch → fuer DE-Overlay uebersetzen.
        return translateBackendJobDetailToGerman(rawDetail);
      }

      // EN: Backend teils Deutsch (preview) oder uebersetzte DE-Strings → Englisch angleichen.
      return translateBackendJobDetailToEnglish(rawDetail);
    })();

    setAnalysisProgressState({
      jobId: job.id ?? null,
      currentStep: step,
      completedSteps: Array.isArray(job.completedSteps) ? job.completedSteps : [],
      message: localizedStepMessageMap[step] || (isEnglish ? "Analysis in progress" : "Analyse läuft"),
      detail: normalizedDetail,
      indeterminate: false,
    });

    if (step !== "done") {
      const subProgress =
        step === "transferring_results"
          ? 0
          : (typeof job.progress === "number" ? job.progress : 0);
      const nextPct = overlayProgressPct(step, subProgress);
      setProgress((prev) => (step === "transferring_results" ? nextPct : Math.max(prev, nextPct)));
    }

    if (job.status === "error") {
      const jobErrorMessage =
        job?.error?.message || job?.detail || job?.message || "Analysis job failed";
      failAnalysis(new Error(jobErrorMessage), {
        source: "analysis_job",
        code: job?.error?.code || job.code || "ANALYSIS_JOB_FAILED",
        jobId: job.id,
        step: job.currentStep,
        detail: jobErrorMessage,
      });
    }
  }, [language, overlayProgressPct, failAnalysis]);

  const pollAnalysisJob = useCallback(async (jobId, signal) => {
    let networkRetries = 0;

    while (!signal?.aborted) {
      let res;
      let data;

      try {
        res = await fetchWithTimeout(
          apiUrl(`/api/analysis-jobs/${jobId}`),
          {
            credentials: "include",
            signal,
            timeoutMessage:
              language === "en"
                ? "Status polling timed out"
                : "Statusabfrage hat zu lange gedauert",
          },
          ANALYSIS_POLL_TIMEOUT_MS
        );
        data = await res.json().catch(() => null);
      } catch (err) {
        networkRetries += 1;
        // A FETCH_TIMEOUT commonly means that the single backend process is
        // busy in a CPU-heavy calculation.  The async job is still alive, so a
        // few unanswered status requests must not turn into a false failure.
        // The overall analysis watchdog remains the hard upper bound.
        const backendBusyTimeout = String(err?.code || "").toUpperCase() === "FETCH_TIMEOUT";
        if (
          (!backendBusyTimeout && networkRetries >= ANALYSIS_POLL_NETWORK_RETRIES) ||
          signal?.aborted
        ) {
          const wrapped = new Error(describeAnalysisTransportError(err));
          wrapped.code = err?.code || "POLL_FAILED";
          throw wrapped;
        }
        await sleep(Math.min(10_000, 1500 * networkRetries));
        continue;
      }

      if (!res.ok || !data?.ok || !data?.job) {
        const wrapped = new Error(
          describeAnalysisTransportError(null, {
            status: res.status,
            errorCode: data?.error,
          })
        );
        wrapped.code = data?.error || "POLL_FAILED";
        throw wrapped;
      }

      networkRetries = 0;

      if (data.job.status === "done") {
        applyAnalysisJobState({
          ...data.job,
          currentStep: "transferring_results",
          completedSteps: BACKEND_PIPELINE_STEPS,
          progress: 0,
        });
        return data.job;
      }

      applyAnalysisJobState(data.job);

      if (data.job.status === "error") {
        throw new Error(
          data.job?.error?.message ||
          data.job?.detail ||
          "Analysis job failed"
        );
      }

      await sleep(1000);
    }

    throw new Error("Analysis polling aborted");
  }, [applyAnalysisJobState, language]);

  const fetchSnapshotForDataset = useCallback(async (datasetId, year, signal) => {
    try {
      return await fetchDatasetSnapshotWire(
        apiUrl(`/api/datasets/${datasetId}/snapshot?year=${encodeURIComponent(year)}`),
        {
          credentials: "include",
          signal,
          fetchImpl: (url, opts) =>
            fetchWithTimeout(
              url,
              {
                ...opts,
                timeoutMessage:
                  language === "en"
                    ? "Snapshot download timed out"
                    : "Snapshot-Download hat zu lange gedauert",
              },
              ANALYSIS_RESULT_TIMEOUT_MS
            ),
        }
      );
    } catch (err) {
      const requiresRecalc = err?.code === "SNAPSHOT_REQUIRES_RECALC";
      const wrapped = new Error(
        language === "en"
          ? requiresRecalc
            ? "Saved snapshot uses a legacy format. Please recalculate."
            : "Saved snapshot could not be loaded."
          : requiresRecalc
            ? "Gespeicherter Snapshot nutzt ein Legacy-Format. Bitte neu berechnen."
            : "Gespeicherter Snapshot konnte nicht geladen werden."
      );
      wrapped.code = err?.code || "SNAPSHOT_LOAD_FAILED";
      throw wrapped;
    }
  }, [language]);

  const fetchAnalysisJobResult = useCallback(async (jobId, signal, fallback = {}) => {
    let lastErr = null;

    for (let attempt = 1; attempt <= ANALYSIS_RESULT_MAX_ATTEMPTS; attempt += 1) {
      if (signal?.aborted) {
        const wrapped = new Error(
          language === "en"
            ? "Result download was cancelled (server timeout or restart)."
            : "Ergebnis-Download abgebrochen (Server-Timeout oder Neustart)."
        );
        wrapped.code = "RESULT_DOWNLOAD_ABORTED";
        throw wrapped;
      }

      try {
        const res = await fetchWithTimeout(
          apiUrl(`/api/analysis-jobs/${jobId}/result`),
          {
            credentials: "include",
            signal,
            timeoutMessage:
              language === "en"
                ? "Result download timed out"
                : "Ergebnis-Download hat zu lange gedauert",
          },
          ANALYSIS_RESULT_TIMEOUT_MS
        );

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const errorCode = String(data?.error || "");
          const retriable =
            attempt < ANALYSIS_RESULT_MAX_ATTEMPTS &&
            (res.status === 409 ||
              errorCode === "RESULT_NOT_READY" ||
              errorCode === "JOB_NOT_FINISHED");

          if (retriable) {
            await sleep(1000);
            continue;
          }

          const wrapped = new Error(
            describeAnalysisTransportError(null, {
              status: res.status,
              errorCode,
            })
          );
          wrapped.code = errorCode || `HTTP_${res.status}`;
          throw wrapped;
        }

        const text = await res.text();
        await sleep(0);

        let data;
        try {
          data = JSON.parse(text);
        } catch (_) {
          const wrapped = new Error(
            language === "en"
              ? "Server response could not be parsed."
              : "Server-Antwort konnte nicht gelesen werden."
          );
          wrapped.code = "RESULT_PARSE_FAILED";
          throw wrapped;
        }

        if (!data?.ok) {
          const errorCode = String(data?.error || "");
          const wrapped = new Error(
            describeAnalysisTransportError(null, { errorCode })
          );
          wrapped.code = errorCode || "RESULT_INVALID";
          throw wrapped;
        }

        const result = data.result;

        if (result?.kind === "snapshot_ref" && result.datasetId && result.year) {
          const snapshot = await fetchSnapshotForDataset(result.datasetId, result.year, signal);

          return {
            ...snapshot,
            __loadedFromSnapshotRef: true,
            __snapshotRef: result,
          };
        }

        return result;
      } catch (err) {
        lastErr = err;
        const fatalCode = String(err?.code || "").toUpperCase();
        if (
          fatalCode === "RESULT_SERIALIZE_FAILED" ||
          fatalCode === "RESULT_TOO_LARGE_USE_SNAPSHOT" ||
          fatalCode === "RESULT_PARSE_FAILED"
        ) {
          const fallbackDatasetId = fallback.datasetId;
          const fallbackYear = fallback.year;

          if (fallbackDatasetId && fallbackYear) {
            try {
              const snapshot = await fetchSnapshotForDataset(fallbackDatasetId, fallbackYear, signal);
              return {
                ...snapshot,
                __loadedFromSnapshotRef: true,
                __snapshotRef: { datasetId: fallbackDatasetId, year: fallbackYear },
              };
            } catch (snapshotErr) {
              lastErr = snapshotErr;
            }
          }
        }

        if (
          fatalCode === "JOB_NOT_FOUND" ||
          fatalCode === "RESULT_SERIALIZE_FAILED" ||
          fatalCode === "RESULT_TOO_LARGE_USE_SNAPSHOT" ||
          fatalCode === "RESULT_PARSE_FAILED" ||
          fatalCode === "FORBIDDEN"
        ) {
          break;
        }

        const retriable =
          attempt < ANALYSIS_RESULT_MAX_ATTEMPTS &&
          !signal?.aborted &&
          (err?.name === "TypeError" ||
            fatalCode === "FETCH_TIMEOUT" ||
            String(err?.message || "").toLowerCase().includes("failed to fetch") ||
            String(err?.message || "").toLowerCase().includes("network"));

        if (retriable) {
          await sleep(Math.min(8000, 1200 * attempt));
          continue;
        }
        break;
      }
    }

    if (lastErr) {
      const wrapped = new Error(describeAnalysisTransportError(lastErr));
      wrapped.code = lastErr.code || "RESULT_DOWNLOAD_FAILED";
      throw wrapped;
    }

    throw new Error(
      language === "en" ? "Failed to load analysis result" : "Analyseergebnis konnte nicht geladen werden"
    );
  }, [fetchSnapshotForDataset, language]);

  // ⬇️ Snapshot laden: liest die letzte persistierte Analyse aus der DB
  const fetchSnapshot = async () => {
    resetAnalysisFailureState();
    setUiPhase("loading");
    setProgress(0);

    try {
      setIsAnalyzing(true);
      setAnalysisProgressState({
        jobId: null,
        currentStep: "transferring_results",
        completedSteps: BACKEND_PIPELINE_STEPS,
        message:
          language === "en"
            ? "Transferring results"
            : "Ergebnisse werden übertragen",
        detail:
          language === "en"
            ? "Loading saved analysis from server…"
            : "Gespeicherte Analyse wird vom Server geladen…",
        indeterminate: false,
      });
      setProgress(overlayProgressPct("transferring_results", 0));
      startTransferProgressTicker(99);
      let effectiveDatasetId = actualDatasetId || location.state?.datasetId; // 🚀 PRIORITÄT: URL zuerst, dann State
      
      // 🚀 FIX: Wenn keine Dataset-ID aus URL/State, versuche sie aus dem aktuellen Kontext zu bekommen
      if (!effectiveDatasetId) {
        // Versuche die Dataset-ID aus dem aktuellen Datensatz zu bekommen
        if (userDataset?.id) {
          effectiveDatasetId = userDataset.id.toString();
                  }
      }
      
            
      // 🚀 KORRIGIERT: Lade Wallet-Set aus Dataset-Daten
      let allAddresses = [walletAddress];
      let delegateAddresses = [];
      let voteRewardAddresses = [];
      
      if (effectiveDatasetId) {
        try {
          // Lade Dataset-Informationen um alle Wallets zu bekommen
                    const datasetResponse = await fetch(`/api/datasets/${effectiveDatasetId}/status`, {
            credentials: 'include'
          });
                    
          if (datasetResponse.ok) {
            const datasetData = await datasetResponse.json();
                        
            // ✅ NEU: selectedCountry aus Dataset laden
            const ccRaw = (
              datasetData?.country ||
              datasetData?.selectedCountry ||
              datasetData?.country_code ||
              datasetData?.countryCode ||
              datasetData?.taxCountry ||
              datasetData?.tax_country ||
              datasetData?.dataset?.country ||
              datasetData?.dataset?.selectedCountry ||
              datasetData?.dataset?.country_code ||
              datasetData?.dataset?.countryCode
            );
            const cc = ccRaw ? String(ccRaw).toUpperCase() : null;
            if (!explicitSelectedCountry && cc && cc !== selectedCountry) {
                            setSelectedCountry(cc);
            }
            
            if (datasetData.wallets && Array.isArray(datasetData.wallets)) {
              // 🚀 PHASE 4: Nur aktive Wallets für Berechnung verwenden
              const wallets = datasetData.wallets.filter(w => w.is_active !== false);
              allAddresses = wallets.map(w => w.main_wallet).filter(Boolean);
              delegateAddresses = wallets.map(w => w.delegate_wallet).filter(Boolean);
                          } else {
                          }
          } else {
                      }
        } catch (error) {
                  }
      }
      
      const walletSet = {
        addresses: allAddresses,
        mainAddresses: allAddresses,
        delegateAddresses: delegateAddresses,
        voteRewardAddresses: voteRewardAddresses
      };
      
      // 🚀 NEU: Aktualisiere walletSet State für MultiVthoOverview
      setWalletSet(walletSet);

      // ✅ KORRIGIERT: Verwende /snapshot statt /analysis
      const url = effectiveDatasetId
        ? apiUrl(`/api/datasets/${effectiveDatasetId}/snapshot?year=${finalYear}`)
        : apiUrl(`/api/analysis/snapshot?year=${finalYear}&walletSet=${encodeURIComponent(JSON.stringify(walletSet))}`);

      const snap = await fetchDatasetSnapshotWire(url, {
        credentials: "include",
        fetchImpl: (wireUrl, opts) => fetch(wireUrl, opts),
      });
      if (!snap) throw new Error(`snapshot fetch failed`);

      stopTransferProgressTicker();
      await animateProgressTo(99, 450);

      const ds = assertUsableAnalysisResult(snap);

      setBackendData(snap);

      const snapshotCountry = resolveAnalysisCountry(snap, explicitSelectedCountry || selectedCountry);
      if (snapshotCountry && !explicitSelectedCountry) {
        setSelectedCountry(snapshotCountry);
      }
      
      // 🚀 WICHTIG: Balance-Verifier-Daten sind jetzt direkt im Snapshot enthalten!
      if (snap?.balanceVerifier) {
              } else if (snap?.holdingsClean || snap?.holdingsFullBalance) {
              } else {
              }
      
      // 🚀 Dataset-Kontext aus Snapshot ableiten (für Save/Recalc auch ohne URL-Param)
      const snapshotDatasetId =
        effectiveDatasetId ||
        snap?.balanceVerifier?.meta?.datasetId ||
        snap?.meta?.datasetId;
      const snapshotYear =
        ds?.year ||
        ds?.purchaseYear ||
        ds?.datasetYear ||
        snap?.balanceVerifier?.meta?.year ||
        snap?.meta?.year ||
        finalYear;
      if (snapshotDatasetId || snapshotYear) {
        setUserDataset((prev) => ({
          id: snapshotDatasetId || prev?.id,
          year: snapshotYear ? String(snapshotYear) : prev?.year,
        }));
      }
      
      setVip180Data(ds?.vip180Data || []);
      setVetVthoData(ds?.vetVthoData || []);
      setNftTxs(ds?.enrichedNftData || []);
      setFifoResultsState(ds?.fifoResults || { vet:[], vtho:[], b3tr:[], nfts:[] });
      setVthoTaxTableData(ds?.vthoTaxRows || []);
      setVetTaxTableData(ds?.vetTaxRows || []);
      hydrateManualClassificationsFromResult(snap);
      
      stopTransferProgressTicker();
      await animateProgressTo(99, 450);

      // 🎨 UI-Phase wird nach Abschluss von Schritt 7 im Autorun-Effect finalisiert
      
      // 🚀 NEU: Notification für Dashboard nach erfolgreichem Snapshot-Load
      const doneAt = new Date().toISOString();
      const idNum = Number(actualDatasetId || location.state?.datasetId);
      if (idNum) {
        window.dispatchEvent(new CustomEvent('analysis:snapshotLoaded', {
          detail: { datasetId: idNum, ts: doneAt }
        }));
      }
      
    } catch (e) {
      stopTransferProgressTicker();
      failAnalysis(e, {
        source: "snapshot_load",
        datasetId: actualDatasetId || location.state?.datasetId,
        year: finalYear,
      });
      toast.error(
        language === "en" ? "Error loading snapshot" : "Fehler beim Laden des Snapshots"
      );

      throw e;
    } finally {
      stopTransferProgressTicker();
      setIsAnalyzing(false);
    }
  };

  // ✅ FIX: React Query Cache-Invalidierung nach Analyse
  const queryClient = useQueryClient();

  const runBackendAnalysis = async (options = {}) => {
    const { notifyPreservedOverrides = true } = options;
    resetAnalysisFailureState();
    setUiPhase("loading");
    setProgress(5);

    if (notifyPreservedOverrides) {
      preservedOverrideCountAtRunStartRef.current = savedManualOverrideCount;
    } else {
      preservedOverrideCountAtRunStartRef.current = 0;
    }

    try {
      setIsAnalyzing(true);
      const datasetId = actualDatasetId || location.state?.datasetId; // 🚀 PRIORITÄT: URL zuerst, dann State
      let resolvedDatasetId = null; // ✅ Fallback-DatasetId (falls nicht über Route/State vorhanden)
      const previewWalletsRaw = localStorage.getItem('previewWallets'); // 🧪 Vorschau-Flow persistiert Wallets hier
      
      // 🚨 KORRIGIERT: Lade ALLE WALLETS für Multi-Wallet-Set

      // 🚨 WICHTIG: Lade alle Wallets für diesen User automatisch
      let allAddresses = [];
      let delegateAddresses = [];
      let voteRewardAddresses = [];
      let datasetCountryForRun = null;

      const applyDatasetCountryForRun = (datasetData) => {
        if (!datasetData || typeof datasetData !== 'object') return null;
        const ccRaw = (
          datasetData?.country ||
          datasetData?.countryCode ||
          datasetData?.tax_country ||
          datasetData?.selectedCountry ||
          datasetData?.dataset?.country ||
          datasetData?.dataset?.selectedCountry ||
          datasetData?.dataset?.country_code ||
          datasetData?.dataset?.countryCode ||
          datasetData?.dataset?.residence_country
        );
        const cc = ccRaw ? normalizeUiCountryCode(ccRaw) : null;
        if (!cc) return null;

        datasetCountryForRun = cc;

        if (!explicitSelectedCountry && cc !== selectedCountry) {
          setSelectedCountry(cc);
        }

        return cc;
      };
      
      // ✅ FIX Schritt 1: Versuch 1 - Adressen aus Dataset-Status ableiten
      if (datasetId) {
        try {
                    const datasetResponse = await fetch(`/api/datasets/${datasetId}/status`, {
            credentials: 'include'
          });
          
          if (datasetResponse.ok) {
            const datasetData = await datasetResponse.json();
                        
            applyDatasetCountryForRun(datasetData);
            
            // ✅ FIX Schritt 1: Verwende deriveAddresses für robuste Extraktion
            const derivedAddrs = deriveAddresses(datasetData, null, walletAddress);
            if (derivedAddrs.length > 0) {
              allAddresses = derivedAddrs;
              
              // Extrahiere delegate addresses separat
              // 🚀 PHASE 4: Nur aktive Wallets für Berechnung verwenden
              const allWallets = datasetData.dataset?.wallets || datasetData.wallets;
              if (allWallets && Array.isArray(allWallets)) {
                const wallets = allWallets.filter(w => w.is_active !== false);
                delegateAddresses = wallets.map(w => w.delegate_wallet).filter(Boolean);
              }
              
              // ✅ FIX Schritt 1: Setze walletSet direkt nach Dataset-Status
              const walletSet = {
                addresses: allAddresses,
                mainAddresses: allAddresses,
                delegateAddresses: delegateAddresses,
                voteRewardAddresses: []
              };
              setWalletSet(walletSet);
                          } else {
                          }
          } else {
                      }
        } catch (error) {
                  }
      }
      
      // 🧪 PREVIEW: Wenn keine datasetId vorhanden ist, nutze Wallets aus dem Vorschau-Flow (localStorage)
      if (!datasetId && previewWalletsRaw) {
        try {
          const previewWallets = JSON.parse(previewWalletsRaw);
          if (Array.isArray(previewWallets) && previewWallets.length > 0) {
            allAddresses = previewWallets
              .map(w => w?.wallet)
              .filter(Boolean)
              .map(a => String(a).toLowerCase().trim());
            delegateAddresses = previewWallets
              .map(w => w?.delegateWallet)
              .filter(Boolean)
              .map(a => String(a).toLowerCase().trim());
            
            const walletSet = {
              addresses: allAddresses,
              mainAddresses: allAddresses,
              delegateAddresses: delegateAddresses,
              voteRewardAddresses: []
            };
            setWalletSet(walletSet);
                      }
        } catch (e) {
                  }
      }

      // 🚀 FALLBACK: Wenn keine Wallets gefunden, nutze walletAddress
      if (allAddresses.length === 0 && walletAddress) {
        allAddresses = [walletAddress];
        const walletSet = {
          addresses: allAddresses,
          mainAddresses: allAddresses,
          delegateAddresses: [],
          voteRewardAddresses: []
        };
        setWalletSet(walletSet);
              }

      try {
        const response = await fetch('/api/datasets');
        if (response.ok) {
          const payload = await response.json();
          
          // 🚀 NEU: Defensiv parsen - unterstütze verschiedene Response-Formate
          const list = Array.isArray(payload) ? payload
                     : Array.isArray(payload?.data) ? payload.data
                     : Array.isArray(payload?.items) ? payload.items
                     : [];
          
          // 🚨 KORRIGIERT: Nutze Owner-Wallet aus Auth-Kontext für Dataset-Lookup
          const ownerWallet = (latestPlan?.userWallet || user?.walletAddress || walletAddress || '').toLowerCase();
          const userDataset = list.find(d => 
            d.userWallet?.toLowerCase() === ownerWallet && 
            String(d.year) === String(finalYear)
          );
          
          if (userDataset) {
            // Lade alle Wallets für dieses Dataset
            const datasetResponse = await fetch(`/api/datasets/${userDataset.id}/status`);
            // 🚀 WICHTIG: Verwende diese Dataset-ID ab jetzt für den Endpunkt
            resolvedDatasetId = String(userDataset.id);
            if (datasetResponse.ok) {
              const datasetData = await datasetResponse.json();
              
              applyDatasetCountryForRun(datasetData);
              
              if (datasetData.wallets && Array.isArray(datasetData.wallets)) {
                // 🚀 PHASE 4: Nur aktive Wallets für Berechnung verwenden
                const wallets = datasetData.wallets.filter(w => w.is_active !== false);
                allAddresses = wallets.map(w => w.main_wallet).filter(Boolean);
                delegateAddresses = wallets.map(w => w.delegate_wallet).filter(Boolean);
                
                // 🚀 WICHTIG: Setze walletSet auch hier für Balance Verifier
                const walletSet = {
                  addresses: allAddresses,
                  mainAddresses: allAddresses,
                  delegateAddresses: delegateAddresses,
                  voteRewardAddresses: []
                };
                setWalletSet(walletSet);
                              }
            }
          } else {
            
            // 🚀 FIX: Wenn kein Dataset gefunden, versuche es mit der Dataset-ID aus der URL
            if (actualDatasetId) {
              try {
                const datasetResponse = await fetch(`/api/datasets/${actualDatasetId}/status`);
                if (datasetResponse.ok) {
                  const datasetData = await datasetResponse.json();
                  
                  applyDatasetCountryForRun(datasetData);
                  
                  if (datasetData.wallets && Array.isArray(datasetData.wallets)) {
                    // 🚀 PHASE 4: Nur aktive Wallets für Berechnung verwenden
                    const wallets = datasetData.wallets.filter(w => w.is_active !== false);
                    allAddresses = wallets.map(w => w.main_wallet).filter(Boolean);
                    delegateAddresses = wallets.map(w => w.delegate_wallet).filter(Boolean);
                    
                    // 🚀 WICHTIG: Setze walletSet auch hier für Balance Verifier
                    const walletSet = {
                      addresses: allAddresses,
                      mainAddresses: allAddresses,
                      delegateAddresses: delegateAddresses,
                      voteRewardAddresses: []
                    };
                    setWalletSet(walletSet);
                                      }
                }
              } catch (error) {
                              }
            }
          }
        }
      } catch (error) {
              }
      
            
      // ✅ Wichtig: Engine/BalanceVerifier nutzen `walletSet.addresses` als Source of Truth.
      // Daher muss `addresses` = Main + Delegate (+ VoteReward) sein.
      const normalizeAddrs = (arr) => [...new Set((arr || [])
        .filter(Boolean)
        .map(a => String(a).toLowerCase().trim())
        .filter(Boolean)
      )];

      const mainAddrs = normalizeAddrs(allAddresses);          // nur Main
      const delegateAddrs = normalizeAddrs(delegateAddresses); // nur Delegate
      const voteAddrs = normalizeAddrs(voteRewardAddresses);

      const combinedAddrs = normalizeAddrs([...mainAddrs, ...delegateAddrs, ...voteAddrs]);

      const walletSet = {
        addresses: combinedAddrs,       // ✅ UNION (Main+Delegate+VoteReward)
        mainAddresses: mainAddrs,       // ✅ Main-only (für Header/Report!)
        delegateAddresses: delegateAddrs,
        voteRewardAddresses: voteAddrs,

        // 🚀 optional/kompatibel (Engine kann das auch lesen)
        mainWallets: mainAddrs,
        delegateWallets: delegateAddrs
      };
      
      // 🚀 WICHTIG: Setze walletSet NACH dem Dataset-Status-Call
            setWalletSet(walletSet);
      
      // ✅ DatasetId robust bestimmen (Route/State/Fallback aus /api/datasets)
      const rawDatasetId = actualDatasetId || location.state?.datasetId || resolvedDatasetId;
      const effectiveDatasetId =
        rawDatasetId && rawDatasetId !== 'undefined' && rawDatasetId !== 'null'
          ? String(rawDatasetId)
          : null;

      // ✅ WICHTIG: Wenn KEINE datasetId vorhanden ist, immer Preview-Endpoint nutzen (Preview muss ohne Kauf funktionieren)
      const usePreviewEndpoint = !effectiveDatasetId;

      const effectivePreview =
        adminReplayLaunch?.isPreview === true ||
        isPreviewFromQuery ||
        isPreview ||
        !effectiveDatasetId;
      if (effectivePreview !== isPreviewMode) setIsPreviewMode(effectivePreview);

      const countryForRun =
        adminReplayLaunch?.selectedCountry ||
        explicitSelectedCountry ||
        datasetCountryForRun ||
        (!effectiveDatasetId ? selectedCountry : null);

      // ✅ Year bestimmen (dein bestehender Code ist okay – Hauptsache effectiveYear ist gesetzt)
      const effectiveYear = finalYear ? String(finalYear) : (year ? String(year) : "");
      if (!effectiveYear) {
        toast.error(t('toast.noTaxYearSelected', language));
        throw new Error("Missing year");
      }

      // ✅ Body je nach Modus
      // Fallback auf sessionStorage falls Browser/Router state bei Reload verloren geht
      const previewExchangeRows =
        (location.state?.previewExchangeRows && Array.isArray(location.state.previewExchangeRows))
          ? location.state.previewExchangeRows
          : adminReplayLaunch?.previewExchangeRows?.length
            ? adminReplayLaunch.previewExchangeRows
            : readPreviewExchangeImports();

      const body = usePreviewEndpoint
        ? {
            year: Number(effectiveYear),
            walletSet,
            selectedCountry: countryForRun || selectedCountry || 'DE',
            exchangeImportsRows: Array.isArray(previewExchangeRows) ? previewExchangeRows : [],
          }
        : {
            year: Number(effectiveYear),
            ...(countryForRun ? { selectedCountry: countryForRun } : {}),
            walletSet,
            datasetId: effectiveDatasetId,
            exchangeImportsRows: Array.isArray(previewExchangeRows) ? previewExchangeRows : [],
            ...(effectivePreview ? { preview: true } : {}),
          };

      
      const startUrl = usePreviewEndpoint
        ? apiUrl("/api/preview/calculate/start")
        : apiUrl(`/api/datasets/${effectiveDatasetId}/calculate/start`);

      stopAnalysisPolling();

      const controller = new AbortController();
      analysisPollAbortRef.current = controller;

      setUiPhase("loading");
      setProgress(5);
      setAnalysisProgressState({
        jobId: null,
        currentStep: "initializing",
        completedSteps: [],
        message: language === "en" ? "Preparing analysis" : "Analyse wird vorbereitet",
        detail: `${walletSet?.addresses?.length || 0} Wallet(s), ${language === "en" ? "Tax year" : "Steuerjahr"} ${effectiveYear}`,
        indeterminate: false,
      });

      const startResponse = await fetch(startUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      const startPayload = await startResponse.json().catch(() => null);

      if (!startResponse.ok) {
        if (startResponse.status === 403 && startPayload?.code === 'PLAN_LIMIT_EXCEEDED') {
          toast.error(
            startPayload.message ||
              (language === 'en'
                ? 'Plan transaction limit exceeded – please upgrade your plan to run a new calculation.'
                : 'Transaktionslimit des Plans überschritten – bitte führe ein Upgrade durch, um eine neue Berechnung zu starten.')
          );
          const err = new Error(startPayload?.message || "Plan limit exceeded");
          err.code = startPayload?.code || "PLAN_LIMIT_EXCEEDED";

          failAnalysis(err, {
            source: "backend_calculation",
            code: err.code,
            datasetId: effectiveDatasetId,
            year: effectiveYear,
          });

          throw err;
        }

        throw new Error(
          startPayload?.debug?.message ||
          startPayload?.error ||
          (language === "en"
            ? `Calculation start failed (${startResponse.status})`
            : `Berechnungsstart fehlgeschlagen (${startResponse.status})`)
        );
      }

      const jobId = startPayload?.jobId || startPayload?.job?.id;
      if (!jobId) {
        throw new Error("No jobId returned from backend");
      }

      activeAnalysisJobIdRef.current = jobId;

      if (startPayload?.job) {
        applyAnalysisJobState(startPayload.job);
      }

      const transferWatchdog = setTimeout(() => {
        try {
          controller.abort();
        } catch (_) {}
      }, ANALYSIS_TRANSFER_WATCHDOG_MS);

      let analysisResult;
      try {
        await pollAnalysisJob(jobId, controller.signal);

        showTransferringResultsPhase(
          language === "en"
            ? "Downloading results from server…"
            : "Ergebnisse werden vom Server heruntergeladen…",
          { transferRatio: 0, startTicker: true, tickerCap: 99 }
        );

        analysisResult = await fetchAnalysisJobResult(jobId, controller.signal, {
          datasetId: effectiveDatasetId,
          year: effectiveYear,
        });
        activeAnalysisJobIdRef.current = null;
      } finally {
        clearTimeout(transferWatchdog);
      }

      const ds = assertUsableAnalysisResult(analysisResult);

      stopTransferProgressTicker();
      await animateProgressTo(99, 450);

      showTransferringResultsPhase(
        language === "en"
          ? "Preparing report in browser…"
          : "Auswertung wird im Browser aufbereitet…",
        { transferRatio: 0.55, startTicker: false }
      );

      const responseData = { analysisResult };
      const payload = analysisResult;

      const serverPreview =
        !!responseData?.preview?.isPreview ||
        !!responseData?.analysisResult?.preview?.isPreview;

      setIsPreviewMode(!!(effectivePreview || serverPreview));

      setBackendData(payload);
      
      // ✅ DEBUG: Frontend Tax-Debugging - Backend Response
      
      const backendCC = resolveAnalysisCountry(
        { analysisResult, meta: analysisResult?.meta },
        explicitSelectedCountry || selectedCountry
      );
      if (backendCC && !explicitSelectedCountry && backendCC !== selectedCountry) {
        setSelectedCountry(backendCC);
      }
      
            const disposals = analysisResult?.fifo?.disposals || analysisResult?.dataset?.nftTaxRows || analysisResult?.dataset?.vthoTaxRows || [];
            
      // 🚨 DEBUG: Was kommt vom Backend an?
            
      // ✅ DEBUG: Logge Response-Struktur für Debugging
            
      // ✅ FIX Schritt 1: IMMER versuchen aus Analyse-Result zu extrahieren (wichtigste Quelle!)
      const derivedAddrsFromAnalysis = deriveAddresses(null, analysisResult, walletAddress);
      
      // Helper für Adress-Normalisierung (wird mehrfach verwendet)
      const normalizeAddrsForUpdate = (arr) => [...new Set((arr || [])
        .filter(Boolean)
        .map(a => String(a).toLowerCase().trim())
        .filter(Boolean)
      )];
      
      if (derivedAddrsFromAnalysis.length > 0) {
        // ✅ FIX: Priorisiere Adressen aus Analysis-Result (sind die "source of truth")
        if (allAddresses.length === 0 || derivedAddrsFromAnalysis.length >= allAddresses.length) {
          allAddresses = derivedAddrsFromAnalysis;
          
          const delegateAddrsNorm = normalizeAddrsForUpdate(delegateAddresses);
          const voteAddrsNorm = normalizeAddrsForUpdate(voteRewardAddresses);
          const derivedAll = normalizeAddrsForUpdate(derivedAddrsFromAnalysis);

          // Main = alles außer Delegate (damit Report-Header korrekt bleibt)
          const mainFromDerived = derivedAll.filter(a => !delegateAddrsNorm.includes(a));
          const mainAddrsNorm =
            mainFromDerived.length > 0
              ? mainFromDerived
              : normalizeAddrsForUpdate(walletSet?.mainAddresses || []);

          const combinedAll = normalizeAddrsForUpdate([...derivedAll, ...delegateAddrsNorm, ...voteAddrsNorm]);

          const walletSetUpdate = {
            addresses: combinedAll,
            mainAddresses: mainAddrsNorm,
            delegateAddresses: delegateAddrsNorm,
            voteRewardAddresses: voteAddrsNorm,
            mainWallets: mainAddrsNorm,
            delegateWallets: delegateAddrsNorm
          };
          
          setWalletSet(walletSetUpdate);
                  } else {
          // Kombiniere beide Quellen (kann mehr Adressen geben)
          const combined = [...new Set([...allAddresses, ...derivedAddrsFromAnalysis])];
          if (combined.length > allAddresses.length) {
            allAddresses = combined;
            
            const delegateAddrsNorm = normalizeAddrsForUpdate(delegateAddresses);
            const voteAddrsNorm = normalizeAddrsForUpdate(voteRewardAddresses);
            const derivedAll = normalizeAddrsForUpdate(combined);

            // Main = alles außer Delegate (damit Report-Header korrekt bleibt)
            const mainFromDerived = derivedAll.filter(a => !delegateAddrsNorm.includes(a));
            const mainAddrsNorm =
              mainFromDerived.length > 0
                ? mainFromDerived
                : normalizeAddrsForUpdate(walletSet?.mainAddresses || []);

            const combinedAll = normalizeAddrsForUpdate([...derivedAll, ...delegateAddrsNorm, ...voteAddrsNorm]);

            const walletSetUpdate = {
              addresses: combinedAll,
              mainAddresses: mainAddrsNorm,
              delegateAddresses: delegateAddrsNorm,
              voteRewardAddresses: voteAddrsNorm,
              mainWallets: mainAddrsNorm,
              delegateWallets: delegateAddrsNorm
            };
            
            setWalletSet(walletSetUpdate);
                      }
        }
      } else if (allAddresses.length === 0) {
        // Letzter Fallback: nur walletAddress
              }
      
      // ✅ FIX B: Validiere dass Balance-Verifier-Daten direkt aus API-Response kommen
      if (analysisResult?.balanceVerifier) {
              } else {
              }
      
      // ✅ FIX: React Query Cache-Invalidierung nach erfolgreicher Analyse
      const datasetIdForCache = actualDatasetId || datasetId;
      const yearForCache = effectiveYear;
      
      if (datasetIdForCache && yearForCache) {
        try {
          await queryClient.invalidateQueries(['balanceVerifier', datasetIdForCache, yearForCache]);
          await queryClient.invalidateQueries(['analysis', datasetIdForCache, yearForCache]);
                  } catch (cacheError) {
                  }
      }
      
      const analysisDatasetId =
        effectiveDatasetId ||
        analysisResult?.balanceVerifier?.meta?.datasetId ||
        analysisResult?.meta?.datasetId;
      const analysisYear =
        analysisResult?.year ||
        analysisResult?.balanceVerifier?.meta?.year ||
        effectiveYear;
      if (analysisDatasetId || analysisYear) {
        setUserDataset((prev) => ({
          id: analysisDatasetId || prev?.id,
          year: analysisYear ? String(analysisYear) : prev?.year,
        }));
      }
      
      // 🚨 KORRIGIERT: Daten robust aus verschiedenen Response-Strukturen extrahieren (ds bereits validiert)
      // 🚨 DEBUG: Was wird gesetzt?
      if (ds?.vip180Data) {
                        setVip180Data(ds.vip180Data);
      } else {
              }
      
      if (ds?.vetVthoData && ds.vetVthoData.length > 0) {
                        setVetVthoData(ds.vetVthoData);
      } else {
                      }
      
      // 🚀 NEU: Setze VTHO-Daten direkt aus Backend-Response (kein Extra-Fetch!)
      setVthoTaxTableData(ds?.vthoTaxRows || []);
      setVetTaxTableData(ds?.vetTaxRows || []);
      setB3trTaxTableData(ds?.b3trTaxRows || []);
      setNftTaxTableData(ds?.nftTaxRows || []);
      
      if (ds?.enrichedNftData) {
                setNftTxs(ds.enrichedNftData);
      } else {
              }
      
      if (ds?.fifoResults) {
        setFifoResultsState(ds.fifoResults);
      }

      hydrateManualClassificationsFromResult(analysisResult);

      if (
        notifyPreservedOverrides &&
        preservedOverrideCountAtRunStartRef.current > 0
      ) {
        toast.info(
          manualOverridePreservedToast(
            preservedOverrideCountAtRunStartRef.current,
            language
          )
        );
      }

      window.dispatchEvent(
        new CustomEvent("manualOverrides:updated", {
          detail: {
            datasetId: actualDatasetId || location.state?.datasetId,
            count: Object.keys(savedManualClassificationsRef.current || {}).length,
          },
        })
      );

      stopTransferProgressTicker();
      await animateProgressTo(99, 450);

      if (!analysisFailedRef.current) {
        await finalizeAnalysisOverlay();
        setUiPhase("ready");
      }
      
      // 🚀 NEU: Notification für Dashboard nach erfolgreicher Backend-Analyse
      const doneAt = new Date().toISOString();
      const idNum = Number(actualDatasetId || location.state?.datasetId);
      if (idNum) {
        window.dispatchEvent(new CustomEvent('analysis:calculationCompleted', {
          detail: { datasetId: idNum, ts: doneAt }
        }));
        localStorage.setItem(`v3tax:lastCalculatedAt:${idNum}`, doneAt);
      }
      
    } catch (error) {
      stopTransferProgressTicker();
      failAnalysis(error, {
        source: "backend_calculation",
        datasetId: actualDatasetId || location.state?.datasetId,
        year: finalYear,
        jobId: analysisProgressState.jobId,
      });
      toast.error(language === "en" ? "Analysis failed" : "Analyse fehlgeschlagen");

      throw error;
    } finally {
      stopAnalysisPolling();
      stopTransferProgressTicker();
      setIsAnalyzing(false);
    }
  };

  async function saveManualOverridesAndRecalculate() {
    const { datasetId: effectiveDatasetId, year: effectiveYear } =
      resolveDatasetAnalysisContext({
        actualDatasetId,
        locationState: location.state,
        locationSearch: location.search,
        userDataset,
        backendData,
        finalYear,
        routeStateYear: stateYear,
        storedTaxYear: storedYear,
      });

    const overrides = Object.values(pendingManualOverrides || {});

    if (!effectiveDatasetId || !effectiveYear) {
      toast.error(
        missingAnalysisContextMessage({
          datasetId: effectiveDatasetId,
          year: effectiveYear,
          language,
        }) ||
          (language === "en"
            ? "Dataset or tax year missing."
            : "Dataset oder Steuerjahr fehlt.")
      );
      return;
    }

    if (!overrides.length) {
      toast.info(
        language === "en"
          ? "No manual changes to save."
          : "Keine manuellen Änderungen zum Speichern."
      );
      return;
    }

    for (const override of overrides) {
      const validation = validateManualOverrideClassificationForUi(override.classification);
      if (!validation.ok) {
        toast.error(language === "en" ? validation.messageEn : validation.message);
        return;
      }
    }

    setIsSavingManualOverrides(true);

    try {
      const res = await fetch(apiUrl(`/api/datasets/${effectiveDatasetId}/overrides`), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          year: Number(effectiveYear),
          overrides,
        }),
      });

      const payload = await res.json().catch(() => null);

      if (!res.ok || payload?.success === false) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            `Override save failed (${res.status})`
        );
      }

      const savedOverrides = payload?.overrides || {};
      const savedMap = {};
      for (const [key, entry] of Object.entries(savedOverrides)) {
        const cls =
          typeof entry === "string"
            ? entry
            : entry?.classification || entry?.manualClassification;
        if (key && cls) savedMap[key] = String(cls);
      }
      savedManualClassificationsRef.current = savedMap;
      setManualClassifications(savedMap);
      setSavedManualOverrideCount(
        Number(payload?.count ?? Object.keys(savedMap).length)
      );

      setPendingManualOverrides({});
      setIsEditMode(false);

      if (cacheKey) {
        sessionStorage.removeItem(cacheKey);
      }

      analysisStartedRef.current = false;

      toast.info(
        language === "en"
          ? "Manual changes saved. Recalculating FIFO…"
          : "Manuelle Änderungen gespeichert. FIFO wird neu berechnet…"
      );

      await runBackendAnalysis({ notifyPreservedOverrides: false });

      window.dispatchEvent(
        new CustomEvent("manualOverrides:updated", {
          detail: {
            datasetId: effectiveDatasetId,
            count: Object.keys(savedManualClassificationsRef.current || {}).length,
          },
        })
      );

      toast.success(
        language === "en"
          ? "Manual changes applied and calculation updated."
          : "Manuelle Änderungen übernommen und Berechnung aktualisiert."
      );
    } catch (error) {
      console.error("Manual override recompute failed:", error);
      toast.error(
        error?.message ||
          (language === "en"
            ? "Manual changes could not be applied."
            : "Manuelle Änderungen konnten nicht übernommen werden.")
      );
    } finally {
      setIsSavingManualOverrides(false);
    }
  }

  async function handleSaveNftTransformFmv(payload = {}) {
    const { datasetId: effectiveDatasetId, year: effectiveYear } =
      resolveDatasetAnalysisContext({
        actualDatasetId,
        locationState: location.state,
        locationSearch: location.search,
        userDataset,
        backendData,
        finalYear,
        routeStateYear: stateYear,
        storedTaxYear: storedYear,
      });

    if (!effectiveDatasetId || !effectiveYear) {
      throw new Error(
        missingAnalysisContextMessage({
          datasetId: effectiveDatasetId,
          year: effectiveYear,
          language,
        }) ||
          (language === "en"
            ? "Dataset or tax year missing."
            : "Dataset oder Steuerjahr fehlt.")
      );
    }

    await saveNftTransformFmvOverrides(effectiveDatasetId, effectiveYear, [
      {
        ...payload,
        valuationSource: payload.valuationSource || "manual_verified",
      },
    ]);

    toast.info(
      language === "en"
        ? "Market value saved. Recalculating…"
        : "Marktwert gespeichert. Berechnung wird aktualisiert…"
    );

    analysisStartedRef.current = false;
    await runBackendAnalysis({ notifyPreservedOverrides: false });

    toast.success(
      language === "en"
        ? "Transform valuation applied."
        : "Transform-Bewertung übernommen."
    );
  }

  async function resetAllManualOverridesAndRecalculate() {
    const { datasetId: effectiveDatasetId, year: effectiveYear } =
      resolveDatasetAnalysisContext({
        actualDatasetId,
        locationState: location.state,
        locationSearch: location.search,
        userDataset,
        backendData,
        finalYear,
        routeStateYear: stateYear,
        storedTaxYear: storedYear,
      });
    const count = savedManualOverrideCount;

    if (!effectiveDatasetId || !effectiveYear) {
      toast.error(
        missingAnalysisContextMessage({
          datasetId: effectiveDatasetId,
          year: effectiveYear,
          language,
        }) ||
          (language === "en"
            ? "Dataset or tax year missing."
            : "Dataset oder Steuerjahr fehlt.")
      );
      return;
    }

    if (!count) {
      toast.info(
        language === "en"
          ? "No saved manual classifications to reset."
          : "Keine gespeicherten manuellen Klassifizierungen zum Zurücksetzen."
      );
      return;
    }

    if (!confirmResetManualOverrides(count, language)) return;

    setIsResettingManualOverrides(true);

    try {
      await resetManualOverrides(effectiveDatasetId, effectiveYear);

      savedManualClassificationsRef.current = {};
      setManualClassifications({});
      setPendingManualOverrides({});
      setSavedManualOverrideCount(0);
      setIsEditMode(false);

      if (cacheKey) {
        sessionStorage.removeItem(cacheKey);
      }

      analysisStartedRef.current = false;

      toast.info(
        language === "en"
          ? "Manual classifications reset. Recalculating with auto-classification only…"
          : "Manuelle Klassifizierungen zurückgesetzt. Neuberechnung nur mit Auto-Klassifizierung…"
      );

      await runBackendAnalysis({ notifyPreservedOverrides: false });

      window.dispatchEvent(
        new CustomEvent("manualOverrides:updated", {
          detail: { datasetId: effectiveDatasetId, count: 0 },
        })
      );

      toast.success(manualOverrideResetSuccessToast(language));
    } catch (error) {
      console.error("Manual override reset failed:", error);
      toast.error(
        language === "en"
          ? "Manual classifications could not be reset."
          : "Manuelle Klassifizierungen konnten nicht zurückgesetzt werden."
      );
    } finally {
      setIsResettingManualOverrides(false);
    }
  }

  // ⛔️ Nichts automatisch starten – nur wenn explizit vom Dashboard gesetzt
  useEffect(() => {
    const mode =
      location.state?.autorun ||
      getAdminReplayAutorunMode(location.search, location.pathname);
    if (mode !== 'calc' && mode !== 'view') return;
    // 🚀 FIX: backendData aus Early-Return entfernen (verhindert, dass alte Country-Ergebnisse bleiben)
    if (isAnalyzing || analysisStartedRef.current) return;

    analysisStartedRef.current = true;
    setIsAnalyzing(true);

    resetAnalysisFailureState();
    setUiPhase("loading");
    setProgress(5);
    setAnalysisProgressState({
      jobId: null,
      currentStep: "initializing",
      completedSteps: [],
      message: language === "en" ? "Preparing analysis" : "Analyse wird vorbereitet",
      detail: "",
      indeterminate: false,
    });

    const run = async () => {
      try {
        if (mode === "view") {
          await fetchSnapshot();
          if (analysisFailedRef.current) return;
          await finalizeAnalysisOverlay();
          if (analysisFailedRef.current) return;
          setUiPhase("ready");
        } else {
          if (cacheKey) sessionStorage.removeItem(cacheKey);

          if (location.state?.resetManualOverrides) {
            const { datasetId: effectiveDatasetId, year: effectiveYear } =
              resolveDatasetAnalysisContext({
                actualDatasetId,
                locationState: location.state,
                locationSearch: location.search,
                userDataset,
                backendData,
                finalYear,
                routeStateYear: stateYear,
                storedTaxYear: storedYear,
              });

            if (effectiveDatasetId && effectiveYear) {
              await resetManualOverrides(effectiveDatasetId, effectiveYear);
              savedManualClassificationsRef.current = {};
              setManualClassifications({});
              setPendingManualOverrides({});
              setSavedManualOverrideCount(0);
            }
          }

          await runBackendAnalysis({
            notifyPreservedOverrides: !location.state?.resetManualOverrides,
          });
          if (analysisFailedRef.current) return;
        }

        if (mode === "view") {
          toast.success(
            language === "en"
              ? "✅ Snapshot loaded successfully!"
              : "✅ Snapshot erfolgreich geladen!"
          );
        } else if (location.state?.resetManualOverrides) {
          toast.success(manualOverrideResetSuccessToast(language));
          window.dispatchEvent(
            new CustomEvent("manualOverrides:updated", {
              detail: {
                datasetId: actualDatasetId || location.state?.datasetId,
                count: 0,
              },
            })
          );
        } else {
          toast.success(
            language === "en"
              ? "Dataset calculated successfully!"
              : "Dataset erfolgreich berechnet!"
          );
        }
      } catch (e) {
        if (!analysisFailedRef.current) {
          failAnalysis(e, {
            source: mode === "view" ? "snapshot_load" : "autorun_calculation",
            datasetId: actualDatasetId || location.state?.datasetId,
            year: finalYear,
          });
        }
        toast.error(
          language === "en" ? "Analysis failed" : "Analyse fehlgeschlagen"
        );
      } finally {
        analysisStartedRef.current = false;
        setIsAnalyzing(false);
        stopAnalysisPolling();
        stopTransferProgressTicker();

        const cleanedSearch = isAdminReplayUrl(location.search)
          ? stripAdminReplayAutorunParams(location.search)
          : location.search;

        if (isAdminReplayUrl(location.search)) {
          clearAdminReplayLaunch();
        }

        navigate(`${location.pathname}${cleanedSearch}`, {
          replace: true,
          state: {
            ...location.state,
            autorun: undefined,
            resetManualOverrides: undefined,
          },
        });
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // 🔍 DEBUG: useEffect um State-Änderungen nach Snapshot-Laden zu überwachen (ENTFERNT - zu viel Output)

  // 🔐 Paywall-Prüfung für Premium-Features (vor jedem frühen return — Hook-Reihenfolge)
  const hasPremiumAccess = checkPlanAccess('premium');
  const hasEnterpriseAccess = checkPlanAccess('enterprise');

  const [effectivePlanByYear, setEffectivePlanByYear] = useState({});

  const loadEffectivePlan = useCallback(async () => {
    if (!isAuthenticated || !user?.walletAddress) return;

    try {
      const response = await fetch(apiUrl("/api/veworld/user-purchases"), {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.purchasesByYear) {
          const effectivePlans = {};
          Object.keys(data.purchasesByYear).forEach((yearStr) => {
            effectivePlans[yearStr] = data.purchasesByYear[yearStr].effectivePlan;
          });
          setEffectivePlanByYear(effectivePlans);
                  }
      }
    } catch (error) {
          }
  }, [isAuthenticated, user?.walletAddress]);

  useEffect(() => {
    loadEffectivePlan();
  }, [loadEffectivePlan]);

  // ── Balance Verifier: Hooks immer ausführen (nicht in !showDashboard-IIFE) ──
  const datasetId =
    actualDatasetId ||
    backendData?.purchaseId ||
    backendData?.datasetId ||
    location.state?.datasetId;

  const balanceVerifierAvailableWallets = useMemo(() => {
    const derived = deriveAddresses(null, backendData, walletAddress);
    const walletSetAddrs = walletSet?.addresses || [];
    return [...new Set([...derived, ...walletSetAddrs])]
      .map((a) => String(a).toLowerCase().trim())
      .filter(Boolean);
  }, [backendData, walletSet?.addresses, walletAddress]);

  const bvEffectiveYear = finalYear || backendData?.year || userDataset?.year;
  const bvValidatedYear =
    bvEffectiveYear && /^\d{4}$/.test(String(bvEffectiveYear))
      ? String(bvEffectiveYear)
      : null;

  const bvHasYear = !!bvValidatedYear;
  const bvHasWallets = balanceVerifierAvailableWallets.length > 0;
  const bvHasEngineResults = !!backendData && !isAnalyzing;
  const bvShouldRenderEvenWithoutExplicitWallets =
    bvHasYear &&
    bvHasEngineResults &&
    !bvHasWallets &&
    !!backendData?.balanceVerifier;
  const canRenderBalanceVerifier =
    (bvHasYear && bvHasWallets && bvHasEngineResults) ||
    bvShouldRenderEvenWithoutExplicitWallets;

  const hasBalanceVerifierBv = !!backendData?.balanceVerifier;
  const hasBalanceVerifierDataBv =
    !!backendData?.balanceVerifier &&
    ((backendData.balanceVerifier.diffs &&
      Array.isArray(backendData.balanceVerifier.diffs)) ||
      (backendData.balanceVerifier.balanceVerifierMulti &&
        Array.isArray(backendData.balanceVerifier.balanceVerifierMulti.rows)) ||
      (backendData.balanceVerifier.fifoTotals &&
        typeof backendData.balanceVerifier.fifoTotals === "object") ||
      (backendData.balanceVerifier.onchainTotals &&
        typeof backendData.balanceVerifier.onchainTotals === "object"));

  const softMissingBv = {
    hasBalanceVerifier: hasBalanceVerifierBv,
    hasBalanceVerifierData: hasBalanceVerifierDataBv,
  };

  const balanceVerifierDebugInfo = {
    hasYear: bvHasYear,
    hasWallets: bvHasWallets,
    hasEngineResults: bvHasEngineResults,
    canRenderBalanceVerifier,
    ...softMissingBv,
    datasetId,
    availableWalletsLength: balanceVerifierAvailableWallets.length,
    availableWallets: balanceVerifierAvailableWallets,
    effectiveYear: bvEffectiveYear,
    balanceVerifierKeys: backendData?.balanceVerifier
      ? Object.keys(backendData.balanceVerifier)
      : [],
    balanceVerifierAddresses: backendData?.balanceVerifier?.addresses || [],
    walletSetAddresses: walletSet?.addresses || [],
    diffsLength: backendData?.balanceVerifier?.diffs?.length || 0,
    hasBackendData: !!backendData,
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.V3DBG = {
      bvPreconditions: () => {
        const currentValidatedYear = bvValidatedYear;
        const currentAvailableWallets = balanceVerifierAvailableWallets;
        const currentBackendData = backendData;
        const currentIsAnalyzing = isAnalyzing;
        const currentDatasetId = datasetId;
        const currentFinalYear = finalYear;

        return {
          selectedYear: currentValidatedYear,
          wallets: currentAvailableWallets,
          hasEngineResults:
            !!currentBackendData && !currentIsAnalyzing,
          isAnalyzing: currentIsAnalyzing,
          hasBackendData: !!currentBackendData,
          datasetId: currentDatasetId,
          availableWalletsLength: currentAvailableWallets.length,
          finalYear: currentFinalYear,
          hasBalanceVerifier: !!currentBackendData?.balanceVerifier,
          hasBalanceVerifierData:
            !!currentBackendData?.balanceVerifier?.diffs?.length,
          balanceVerifierKeys: currentBackendData?.balanceVerifier
            ? Object.keys(currentBackendData.balanceVerifier)
            : [],
          canRenderBalanceVerifier:
            currentValidatedYear &&
            currentAvailableWallets.length > 0 &&
            !!currentBackendData &&
            !currentIsAnalyzing,
          softMissing: {
            hasBalanceVerifier: !!currentBackendData?.balanceVerifier,
            hasBalanceVerifierData:
              !!currentBackendData?.balanceVerifier?.diffs?.length,
          },
        };
      },
    };
      }, [
    bvValidatedYear,
    balanceVerifierAvailableWallets,
    backendData,
    isAnalyzing,
    datasetId,
    finalYear,
  ]);

  // Steuer-Tabellen: useMemo/useCallback dürfen nicht in {!showDashboard && …} stehen — sonst #300 beim Dashboard-Toggle
  const vetTaxDataFromPropsForTable = useMemo(() => {
    const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
    const backendRows = ds?.vetTaxRows || [];
    const stateRows = vetTaxTableData || [];
    return backendRows.length > 0 ? backendRows : stateRows;
  }, [backendData, vetTaxTableData]);

  const vetTaxTotalsFromPropsForTable = useMemo(() => {
    const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
    return ds?.vetTotals || {};
  }, [backendData]);

  const onVetTaxTableDataReady = useCallback((data) => {
    setVetTaxTableData((prev) => {
      const current = prev || [];
      const hasChanged =
        current.length !== data.length ||
        !current.every((tx, i) => tx.hash === data[i]?.hash);
      return hasChanged ? data : prev;
    });
  }, []);

  const vthoTaxDataFromPropsForTable = useMemo(() => {
    const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
    const backendRows = ds?.vthoTaxRows || [];
    const stateRows = vthoTaxTableData || [];
    return backendRows.length > 0 ? backendRows : stateRows;
  }, [backendData, vthoTaxTableData]);

  const vthoTaxTotalsFromPropsForTable = useMemo(() => {
    const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
    return ds?.vthoTotals || {};
  }, [backendData]);

  const onVthoTaxTableDataReady = useCallback((data) => {
    setVthoTaxTableData((prev) => {
      const current = prev || [];
      const hasChanged =
        current.length !== data.length ||
        !current.every((tx, i) => tx.hash === data[i]?.hash);
      return hasChanged ? data : prev;
    });
  }, []);

  const b3trTaxDataFromPropsForTable = useMemo(() => {
    const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
    const backendRows = ds?.b3trTaxRows || [];
    const stateRows = b3trTaxTableData || [];
    return backendRows.length > 0 ? backendRows : stateRows;
  }, [backendData, b3trTaxTableData]);

  const b3trTaxTotalsFromPropsForTable = useMemo(() => {
    const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
    return ds?.b3trTotals || {};
  }, [backendData]);

  const onB3trTaxTableDataReady = useCallback((data) => {
    setB3trTaxTableData((prev) => {
      const current = prev || [];
      const hasChanged =
        current.length !== data.length ||
        !current.every((tx, i) => tx.hash === data[i]?.hash);
      return hasChanged ? data : prev;
    });
  }, []);

  const onLiquidityTaxTableDataReady = useCallback((rows) => {
    setLiquidityTaxTableData((prev) => {
      const current = prev || [];
      const hasChanged =
        current.length !== rows.length ||
        !current.every(
          (r, i) => r.hash === rows[i]?.hash && r.type === rows[i]?.type
        );
      return hasChanged ? rows : prev;
    });
  }, []);

  const onNftTaxTableDataReady = useCallback((data) => {
    setNftTaxTableData((prev) => {
      const current = prev || [];
      const hasChanged =
        current.length !== data.length ||
        !current.every((tx, i) => tx.hash === data[i]?.hash);
      return hasChanged ? data : prev;
    });
  }, []);

  // 🔐 Auth-Prüfung - Optional für AnalysisApp
  // 🚀 FIX: Loader nur zeigen, wenn noch keine Daten vorhanden sind
  if (authLoading && !backendData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {language === 'en' ? 'Loading...' : 'Laden...'}
          </p>
        </div>
      </div>
    );
  }

  // 🚀 NEU: Bestimme effektiven Plan für aktuelles Jahr
  const selectedYearStr =
    finalYear && /^\d{4}$/.test(String(finalYear))
      ? String(finalYear)
      : String(new Date().getFullYear());

  const effectivePlanForYear = effectivePlanByYear[selectedYearStr];
  const planToUse = effectivePlanForYear || latestPlan?.plan || user?.plan || "free";

  // 🆕 NEU: Aktueller Plan-Info (mit effektivem Plan pro Jahr)
  const currentPlanInfo = getPlanInfo(planToUse, language);

  const renderBalanceVerifierPanel = () => {
    
                    
    if (!canRenderBalanceVerifier) {
            return (
        <div className="mt-6 p-4 border rounded-xl bg-yellow-50 shadow-lg text-sm min-h-[260px]">
          <div className="font-semibold text-yellow-800 mb-2">⚠️ Balance Verifier: Bedingungen nicht erfüllt</div>
          <div className="text-xs text-yellow-700">
            <p className="mb-2"><strong>Hard-Requirements:</strong></p>
            <pre className="mt-2 p-2 bg-yellow-100 rounded overflow-auto">
              {JSON.stringify({ hasYear: bvHasYear, hasWallets: bvHasWallets, hasEngineResults: bvHasEngineResults }, null, 2)}
            </pre>
            <p className="mt-3 text-yellow-600">Die übrigen Flags sind optional und blockieren nicht mehr.</p>
            <details className="mt-2">
              <summary className="cursor-pointer text-yellow-700 font-semibold">Debug-Info (optional)</summary>
              <pre className="mt-2 p-2 bg-yellow-100 rounded overflow-auto text-xs">
                {JSON.stringify(balanceVerifierDebugInfo, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      );
    }

        return (
      <div className="print:hidden w-full min-w-0">
        <BalanceVerifierMulti
          key={[
            backendData?.balanceVerifier?.timestamp,
            backendData?.balanceVerifier?.meta?.manualOverrideCount,
            backendData?.dataset?.manualOverrideCount,
            bvValidatedYear,
          ]
            .filter((v) => v != null && v !== "")
            .join("-") || "balance-verifier"}
          datasetId={datasetId || null}
          walletSet={{
            addresses: balanceVerifierAvailableWallets,
            mainAddresses: balanceVerifierAvailableWallets,
            delegateAddresses: walletSet?.delegateAddresses || []
          }}
          year={bvValidatedYear}
          includeStakedVet={false}
          useClean={false}
          loadFromSnapshot={loadFromSnapshot}
          snapshotData={backendData?.balanceVerifier || null}
          dataSource={
            isPreviewMode && !datasetId
              ? "preview"
              : backendData?.__loadedFromSnapshotRef || loadFromSnapshot
                ? "snapshot"
                : "live"
          }
          taxCountryCode={(() => {
            const ccUsed = String(backendData?.meta?.countryCode || selectedCountry || "DE").toUpperCase();
            return ccUsed;
          })()}
          taxCountryName={(() => {
            const ccUsed = String(backendData?.meta?.countryCode || selectedCountry || "DE").toUpperCase();
            const nameUsed = ({ DE: "Germany", IT: "Italy", GR: "Greece", US: "United States", UK: "United Kingdom", GB: "United Kingdom" }[ccUsed] || ccUsed);
            return nameUsed;
          })()}
          taxCalculationMethod={(() => {
            const ccUsed = String(backendData?.meta?.countryCode || selectedCountry || "DE").toUpperCase();
            const methodUsed =
              backendData?.meta?.costBasisMethod ||
              (ccUsed === "IT"
                ? "LIFO"
                : ccUsed === "UK" || ccUsed === "GB"
                  ? "UK_S104_POOLING"
                  : "FIFO");
            return methodUsed;
          })()}
          onRecalculate={
            isPreviewMode && !datasetId
              ? runBackendAnalysis
              : undefined
          }
          softMissing={softMissingBv}
        />
      </div>
    );
  };
  
  // 🚀 DEBUG: Zeige was geladen wird (ENTFERNT - zu viel Output)

  // 🧮 Kursmethodik aus Backend-Metadaten (kein Coinpaprika-Default, kein Opening-Price)
  const priceSourceLabel = (() => {
    const ds =
      backendData?.analysisResult?.dataset ||
      backendData?.dataset ||
      {};
    const meta =
      backendData?.analysisResult?.meta ||
      backendData?.meta ||
      ds.meta ||
      {};
    const pm =
      ds.pricingMethodology ||
      meta.pricingMethodology ||
      null;

    if (pm?.publicLabelDe || pm?.publicLabelEn) {
      return language === 'en'
        ? (pm.publicLabelEn || pm.publicLabelDe)
        : (pm.publicLabelDe || pm.publicLabelEn);
    }

    // Alte Snapshots ohne pricingMethodology: neutral, kein Anbieter raten
    return language === 'en'
      ? 'Historical transaction-time pricing data'
      : 'Historische, transaktionszeitbezogene Kursdaten';
  })();

  const reportCurrencyLabel = (() => {
    const ds =
      backendData?.analysisResult?.dataset ||
      backendData?.dataset ||
      {};
    const meta =
      backendData?.analysisResult?.meta ||
      backendData?.meta ||
      ds.meta ||
      {};
    const pm = ds.pricingMethodology || meta.pricingMethodology || null;
    return String(
      pm?.reportCurrency ||
        meta.fiatCurrency ||
        ds.fiatCurrency ||
        ''
    ).toUpperCase() || null;
  })();

  const pricingMethodologyNote = (() => {
    const ds =
      backendData?.analysisResult?.dataset ||
      backendData?.dataset ||
      {};
    const meta =
      backendData?.analysisResult?.meta ||
      backendData?.meta ||
      ds.meta ||
      {};
    const pm = ds.pricingMethodology || meta.pricingMethodology || null;
    if (!pm) return null;
    return language === 'en'
      ? (pm.publicNoteEn || pm.publicNoteDe)
      : (pm.publicNoteDe || pm.publicNoteEn);
  })();

  // 🧾 PDF Variant (Anlage 1/2)
  const pdfVariantShort = taxReportPdfVariant === 'raw' ? 'A2' : 'A1';
  const pdfVariantTitle = taxReportPdfVariant === 'raw'
    ? (language === 'en'
      ? 'Appendix 2 – Classified ledger'
      : 'Anlage 2 – Klassifiziertes Ledger')
    : (language === 'en'
      ? 'Appendix 1 – Calculation report'
      : 'Anlage 1 – Berechnungsreport');
  
  const pdfSectionClass = (_hasData, extra = '') => {
    return `report-section ${extra}`.trim();
  };

  const pdfDs = backendData?.analysisResult?.dataset || backendData?.dataset || {};

  const exchangeImports = pdfDs?.exchangeImports || [];
  const exchangeTaxRows = pdfDs?.exchangeTaxRows || [];
  const exchangeTotals = pdfDs?.exchangeTotals || null;

  const rawVetVthoTableSource = backendData?.dataset?.vetVthoData || vetVthoData || [];
  const rawVip180TableSource = backendData?.dataset?.vip180Data || vip180Data || [];

  // Web: volle Historie — Normalisierung nur in RawTransfersTable (eine Quelle)
  const rawVetVthoTxs = rawVetVthoTableSource;
  const rawVip180Txs = rawVip180TableSource;

  const hasRawTransfers = rawVetVthoTxs.length > 0 || rawVip180Txs.length > 0;

  // ------------------------------------------------------------
  // 🖨️ PDF: Leere Tabellen wirklich weglassen (Flags müssen wie die Tables filtern)
  // ------------------------------------------------------------
  const toSecForPdf = (ts) => {
    if (!ts) return 0;
    if (typeof ts === "string") {
      const ms = new Date(ts).getTime();
      return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0;
    }
    const n = Number(ts);
    if (!Number.isFinite(n)) return 0;
    return n > 1e12 ? Math.floor(n / 1000) : n;
  };

  const selectedYearStrForPdf =
    finalYear && /^\d{4}$/.test(String(finalYear)) ? String(finalYear) : "";

  const isUkCountryForPdf = (() => {
    const c = String(selectedCountry || '').toUpperCase();
    return c === 'UK' || c === 'GB' || c.includes('UNITED KINGDOM') || c.includes('UNITED KINGDOM');
  })();

  const getUkTaxYearFromTsForPdf = (tsSec) => {
    const sec = Number(tsSec || 0);
    if (!sec) return null;
    const d = new Date(sec * 1000);
    const y = d.getUTCFullYear();
    const startCurrent = Date.UTC(y, 3, 6, 0, 0, 0);
    return d.getTime() >= startCurrent ? y : y - 1;
  };

  const ukTaxYearLabelForPdf =
    isUkCountryForPdf && selectedYearStrForPdf
      ? `${selectedYearStrForPdf}/${String((Number(selectedYearStrForPdf) + 1) % 100).padStart(2, '0')}`
      : selectedYearStrForPdf;

  const inSelectedYear = (ts) => {
    // Ohne Jahr: "zeige alles", aber nur wenn Timestamp existiert
    if (!selectedYearStrForPdf) return !!toSecForPdf(ts);
    const sec = toSecForPdf(ts);
    if (!sec) return false;

    if (isUkCountryForPdf) {
      return String(getUkTaxYearFromTsForPdf(sec)) === selectedYearStrForPdf;
    }

    return new Date(sec * 1000).getFullYear().toString() === selectedYearStrForPdf;
  };

  const pickRows = (backendRows, stateRows) => {
    const b = Array.isArray(backendRows) ? backendRows : [];
    if (b.length > 0) return b;
    return Array.isArray(stateRows) ? stateRows : [];
  };

  // ExchangeImportsTable: im PDF nur drucken, wenn im gewählten Jahr wirklich Rows existieren
  // (exchangeTotals ist oft {} -> wäre sonst truthy und würde die leere Section drucken)
  const hasExchangeData =
    (Array.isArray(exchangeImports) ? exchangeImports : []).some((r) =>
      inSelectedYear(r?.timestamp ?? r?.ts ?? r?.date ?? r?.time)
    ) ||
    (Array.isArray(exchangeTaxRows) ? exchangeTaxRows : []).some((r) =>
      inSelectedYear(r?.timestamp ?? r?.ts ?? r?.date ?? r?.time)
    );

  // VetTaxTable: filtert zusätzlich steuerrelevante Klassifizierungen
  const isSwapOutLikeForVet = (cls) => {
    const c = String(cls || "").toLowerCase();
    return c === "swap_out" || c === "nfbc_purchase" || c === "v3tax_purchase" || c === "dex_aggregator_execution_cost" || c === "dex_aggregator_fee";
  };
  const isVetTaxRelevantForPdf = (cls) => {
    const c = String(cls || "").toLowerCase();
    return (
      isSwapOutLikeForVet(c) ||
      c === "solarwise_electricity_revenue" ||
      c === "staking_reward" ||
      c === "governance_reward" ||
      c === "airdrop" ||
      c === "foundation_bonus" ||
      c === "juicyfinance_lending_income" ||
      c === "juicyfinance_unmatched_withdrawal_principal" ||
      c === "juicyfinance_borrow_principal" ||
      c === "juicyfinance_repay_principal" ||
      c === "juicyfinance_borrow_interest_paid"
    );
  };

  // NftTaxTable: nur Mint/SalePayment + Year
  const isNftTaxRelevantForPdf = (cls) => {
    const c = String(cls || "").toLowerCase();
    return c === "nft_mint" ||
      c === "nft_galaxy_mint" ||
      c === "nft_marketplace_purchase_payment" ||
      c === "sale_payment";
  };

  const hasVetTax = pickRows(pdfDs?.vetTaxRows, vetTaxTableData).some((r) => {
    const cls = r?.classification || r?.type || "";
    return isVetTaxRelevantForPdf(cls) && inSelectedYear(r?.timestamp);
  });

  const hasVthoTax = pickRows(pdfDs?.vthoTaxRows, vthoTaxTableData).some((r) =>
    inSelectedYear(r?.timestamp)
  );

  const hasB3trTax = pickRows(pdfDs?.b3trTaxRows, b3trTaxTableData).some((r) =>
    inSelectedYear(r?.timestamp)
  );

  // Liquidity: falls Backend mehrere Jahre liefert, year-sicher machen
  const hasLiquidityTax = pickRows(pdfDs?.liquidityTaxRows, liquidityTaxTableData).some((r) =>
    inSelectedYear(r?.timestamp)
  );

  const hasNftTax = pickRows(pdfDs?.nftTaxRows, nftTaxTableData).some((r) => {
    const cls = r?.classification || r?.type || "";
    return isNftTaxRelevantForPdf(cls) && inSelectedYear(r?.timestamp);
  });

  // ------------------------------------------------------------
  // 📊 Totals: TotalIncomeSummary props (used in UI + PDF)
  // ------------------------------------------------------------
  const bvMetaForSummary =
    backendData?.balanceVerifier?.meta || backendData?.meta || {};
  const bvSnapshotLabelForSummary =
    bvMetaForSummary?.onchainMode === "year_end" &&
    Number.isFinite(Number(bvMetaForSummary?.onchainAsOfTs))
      ? `Snapshot: ${new Date(
          Number(bvMetaForSummary.onchainAsOfTs) * 1000
        )
          .toISOString()
          .slice(0, 10)}`
      : null;
  const bvSnapshotTooltipForSummary =
    bvMetaForSummary?.onchainMode === "year_end"
      ? "Historischer On-chain Snapshot enthält nur liquid balances. Staking/Delegate-Contracts sind zeitabhängig und werden bewusst ausgeschlossen."
      : null;
  const effectiveCountryForSummary = resolveAnalysisCountry(
    backendData,
    selectedCountry
  );
  const taxChipCodeForSummary = effectiveCountryForSummary;
  const taxChipNameForSummary =
    ({
      DE: "Germany",
      IT: "Italy",
      GR: "Greece",
      US: "United States",
      UK: "United Kingdom",
      GB: "United Kingdom",
    }[taxChipCodeForSummary] || taxChipCodeForSummary);
  const taxMethodForSummary =
    backendData?.meta?.costBasisMethod ||
    (taxChipCodeForSummary === "IT"
      ? "LIFO"
      : taxChipCodeForSummary === "UK" || taxChipCodeForSummary === "GB"
        ? "UK_S104_POOLING"
        : "FIFO");

  const summaryDataset = resolveAnalysisDataset(backendData);

  const totalIncomeSummaryProps = {
    isPreviewMode,
    previewMaskedTotals: backendData?.preview?.maskedTotals,
    previewTaxStatus: backendData?.preview?.taxStatus,
    vet: summaryDataset?.vetTotals,
    vtho: summaryDataset?.vthoTotals,
    b3tr: summaryDataset?.b3trTotals,
    nft: summaryDataset?.nftTotals,
    exchange: summaryDataset?.exchangeTotals,
    liquidity: summaryDataset?.liquidityTotals,
    selectedCountry: effectiveCountryForSummary,
    selectedYear: finalYear,
    datasetId,
    planName: currentPlanInfo?.name || user?.plan || "Free",
    walletCount: balanceVerifierAvailableWallets.length,
    bvSnapshotLabel: bvSnapshotLabelForSummary,
    bvSnapshotTooltip: bvSnapshotTooltipForSummary,
    taxChipCode: taxChipCodeForSummary,
    taxChipName: taxChipNameForSummary,
    taxMethod:
      summaryDataset?.ukTaxSummary?.costBasisMethod || taxMethodForSummary,
    ukTaxSummary: summaryDataset?.ukTaxSummary || null,
    dataset: summaryDataset,
  };

  // Erste "echte" Tabelle in Anlage 1 (calc) dynamisch bestimmen (damit report-section--first nicht auf leere Section zeigt)
  const firstCalcSectionKey =
    (
      [
        ["exchange", hasExchangeData],
        ["vet", hasVetTax],
        ["vtho", hasVthoTax],
        ["b3tr", hasB3trTax],
        ["liquidity", hasLiquidityTax],
        ["nft", hasNftTax],
      ].find(([, has]) => has)?.[0]
    ) ?? "vet";

  const isFirstCalc = (key) => key === firstCalcSectionKey;

return (
  <>
  <div className="min-h-screen v3-page-bg">
    <div
      id="pdf-export-content"
      className="print-area mx-auto w-full max-w-[var(--v3-content-max)] v3-main-sheet print:bg-white print:max-w-screen
                 print:shadow-none print:ring-0 print:rounded-none"
    >
    {uiPhase === "loading" && (
      <VeWorldLoadingOverlay
        phase="loading"
        progress={progress}
        currentStep={analysisProgressState.currentStep}
        completedSteps={analysisProgressState.completedSteps}
        progressMessage={analysisProgressState.message}
        progressDetail={analysisProgressState.detail}
        indeterminate={analysisProgressState.indeterminate}
        language={language}
      />
    )}

    {adminReplayLaunch && (
      <div className="mx-4 mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm">
        <div className="font-semibold">
          {language === "de"
            ? "Admin-Nachrechnung (isoliert)"
            : "Admin replay (isolated)"}
        </div>
        <div className="mt-1 text-amber-900/90">
          {language === "de"
            ? `Quelle: User-Dataset #${adminReplayLaunch.adminReplay?.sourceDatasetId ?? "—"} · Jahr ${adminReplayLaunch.year} · ${String(adminReplayLaunch.selectedCountry || "DE").toUpperCase()} · ${adminReplayLaunch.isPreview ? "Preview" : "Final"} · User-Snapshots werden nicht überschrieben.`
            : `Source: user dataset #${adminReplayLaunch.adminReplay?.sourceDatasetId ?? "—"} · year ${adminReplayLaunch.year} · ${String(adminReplayLaunch.selectedCountry || "DE").toUpperCase()} · ${adminReplayLaunch.isPreview ? "preview" : "final"} · user snapshots are not overwritten.`}
        </div>
      </div>
    )}

    {uiPhase === "error" && (
      <AnalysisFailureModal
        language={language}
        error={analysisError}
        onRetry={() => {
          resetAnalysisFailureState();
          setUiPhase("loading");
          setProgress(location.state?.autorun === "view" ? 0 : 5);
          setAnalysisProgressState({
            jobId: null,
            currentStep: "initializing",
            completedSteps: [],
            message:
              language === "en" ? "Preparing analysis" : "Analyse wird vorbereitet",
            detail: "",
            indeterminate: false,
          });

          const retryRun =
            location.state?.autorun === "view" ? fetchSnapshot : runBackendAnalysis;

          retryRun()
            .then(async () => {
              if (analysisFailedRef.current) return;

              if (location.state?.autorun === "view") {
                await finalizeAnalysisOverlay();
                if (analysisFailedRef.current) return;
                setUiPhase("ready");
              }
            })
            .catch((err) => {
              if (!analysisFailedRef.current) {
                failAnalysis(err, {
                  source: "retry",
                  datasetId: actualDatasetId || location.state?.datasetId,
                  year: finalYear,
                });
              }
            });
        }}
        onBack={handleBackFromAnalysisError}
        onSupport={() => {
          setShowHelpModal(true);
          setHelpActiveSection("support");
          setSupportForm((prev) => ({
            ...prev,
            category:
              language === "en" ? "Calculation Error" : "Berechnungsfehler",
            description: buildAnalysisSupportDescription(
              analysisError,
              language
            ),
            calculationErrorConsent: true,
          }));
        }}
      />
    )}

    {uiPhase === "ready" && backendData ? (
    <div className="pdf-report-body transition-opacity duration-300 ease-out opacity-100">
    <AnalysisCrashBoundary
      resetKey={`${actualDatasetId || "preview"}-${finalYear}-${uiPhase}`}
      onCrash={(error, info) => {
        failAnalysis(error, {
          source: "frontend_render",
          code: "ANALYSIS_RENDER_FAILED",
          detail: info?.componentStack || "",
          datasetId: actualDatasetId || location.state?.datasetId,
          year: finalYear,
        });
      }}
      fallback={null}
    >
      {/* 🎨 Header mit Navigation */}
      <div
        className="app-header no-print fixed top-0 left-1/2 z-40 flex h-[54px] w-full max-w-[var(--v3-content-max)] -translate-x-1/2 items-center justify-between border border-white/10 bg-gradient-to-r from-[#2563eb] via-[#2458dc] to-[#1e40af] px-3 sm:px-4 print:hidden shadow-[0_10px_28px_rgba(15,23,42,0.16)]"
        style={{ width: "calc(100% - 24px)" }}
      >
      {/* Linke Seite: Menu-Button + Zurück-Button in einem Container */}
      <div className="flex items-center gap-2">
        {/* Dropdown-Menü */}
        <div className="relative dropdown-container">
          <button 
            onClick={() => setShowDropdown(!showDropdown)}
            className="text-white hover:text-blue-100 px-2.5 py-1.5 rounded-lg transition-all duration-300 flex items-center gap-2 hover:bg-white/10 group"
          >
            <svg className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-sm font-medium">Menu</span>
          </button>
          
          {showDropdown && (
            <div className="absolute top-full left-0 mt-2 w-60 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden" style={{marginLeft: '-16px'}}>
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700">
                  {language === 'en' ? 'Navigation' : 'Navigation'}
                </h3>
              </div>
              
              <div className="py-1">
                {/* Dashboard */}
                <button 
                  onClick={() => {
                    if (isPreviewMode) {
                      setShowDropdown(false);
                      return;
                    }
                    toggleDashboard();
                    setShowDropdown(false);
                  }}
                  disabled={isPreviewMode}
                  className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 flex items-center gap-3 ${
                    isPreviewMode
                      ? 'text-gray-400 cursor-not-allowed opacity-50'
                      : (showDashboard 
                          ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-500' 
                          : 'text-gray-700 hover:bg-blue-50 hover:border-l-4 hover:border-blue-400'
                        )
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium">{t('dashboard', language)}</div>
                    <div className="text-xs text-gray-500">
                      {language === 'en' ? 'Overview of all calculated values' : 'Übersicht aller berechneten Werte'}
                    </div>

                    {isPreviewMode && (
                      <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
                        <Lock className="w-3 h-3 shrink-0 text-slate-500" strokeWidth={2.25} aria-hidden />
                        <span>{language === 'en' ? 'Available after purchase' : 'Nach Kauf verfügbar'}</span>
                      </div>
                    )}
                  </div>
                </button>
                
                {/* Divider */}
                <div className="h-px bg-gray-200 my-1"></div>
                
                {/* Main Menu Items */}
                <button 
                  onClick={() => {
                    if (isPreviewMode) {
                      setShowDropdown(false);
                      return;
                    }
                    setShowMyWalletsDashboard(true);
                    setShowDropdown(false);
                  }}
                  disabled={isPreviewMode}
                  className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 flex items-center gap-3 ${
                    isPreviewMode
                      ? 'text-gray-400 cursor-not-allowed opacity-50'
                      : 'text-gray-700 hover:bg-blue-50 hover:border-l-4 hover:border-blue-400'
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {language === 'en' ? 'My Wallets' : 'Meine Wallets'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en' ? 'Manage active wallets' : 'Aktive Wallets verwalten'}
                    </div>

                    {isPreviewMode && (
                      <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
                        <Lock className="w-3 h-3 shrink-0 text-slate-500" strokeWidth={2.25} aria-hidden />
                        <span>{language === 'en' ? 'Available after purchase' : 'Nach Kauf verfügbar'}</span>
                      </div>
                    )}
                  </div>
                </button>
                
                <button 
                  onClick={() => {
                    if (isPreviewMode) {
                      setShowDropdown(false);
                      return;
                    }
                    document.querySelector('#pdf-export-content')?.scrollIntoView({behavior: 'smooth', block: 'end'});
                    setShowDropdown(false);
                    // Show export hint after scroll
                    setTimeout(() => {
                      setShowExportHint(true);
                      // Auto-hide after 20 seconds minimum
                      setTimeout(() => setShowExportHint(false), 20000);
                    }, 800);
                  }}
                  disabled={isPreviewMode}
                  className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 flex items-center gap-3 ${
                    isPreviewMode
                      ? 'text-gray-400 cursor-not-allowed opacity-50'
                      : 'text-gray-700 hover:bg-blue-50 hover:border-l-4 hover:border-blue-400'
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {language === 'en' ? 'Generate Tax Reports' : 'Steuerberichte erstellen'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en' ? 'Export PDF & CSV reports' : 'PDF & CSV Berichte exportieren'}
                    </div>
                    {isPreviewMode && (
                      <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
                        <Lock className="w-3 h-3 shrink-0 text-slate-500" strokeWidth={2.25} aria-hidden />
                        <span>{language === 'en' ? 'Available after purchase' : 'Nach Kauf verfügbar'}</span>
                      </div>
                    )}
                  </div>
                </button>
                
                {/* Divider */}
                <div className="h-px bg-gray-200 my-1"></div>
                
                {/* Support Items */}
                <button 
                  onClick={() => {setShowLegalModal(true); setShowDropdown(false);}}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-all duration-200 flex items-center gap-3 hover:border-l-4 hover:border-blue-400"
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {language === 'en' ? 'Legal Notices' : 'Rechtliche Hinweise'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en' ? 'Terms & privacy policy' : 'AGB & Datenschutz'}
                    </div>
                  </div>
                </button>
                
                <button 
                  onClick={() => {
                    setHelpActiveSection('overview');
                    setShowHelpModal(true);
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-all duration-200 flex items-center gap-3 hover:border-l-4 hover:border-blue-400"
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {language === 'en' ? 'Help & Support' : 'Hilfe & Support'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en' ? 'Get help and documentation' : 'Hilfe und Dokumentation'}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

                 {/* Zurück-Button direkt rechts neben dem Menu-Button */}
         <button 
           onClick={() => goToApp()}
           className="text-white hover:text-blue-100 px-2.5 py-1.5 rounded-lg transition-all duration-300 flex items-center gap-2 hover:bg-white/10 group"
           title={language === 'en' ? 'Back to App' : 'Zurück zur App'}
         >
           <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
           </svg>
           <span className="text-sm font-medium">
             {language === 'en' ? 'Back' : 'Zurück'}
           </span>
         </button>

         {isPreviewMode && (
           <button
             onClick={() => {
               try { sessionStorage.setItem("v3tax:buy_pulse", "1"); } catch (_) {}

               toast.success(
                 language === "en"
                   ? "Preview created → Unlock to view full report"
                   : "Vorschau erstellt → Jetzt freischalten",
                 { autoClose: 1000 }
               );

               goToApp({ openBuy: true });
             }}
             className={[
               "group relative overflow-hidden",
               "inline-flex items-center gap-1.5",
               "px-2.5 py-1.5 rounded-lg",
               "text-sm font-medium text-white whitespace-nowrap",
               "bg-green-600 hover:bg-green-700",
               "shadow-[0_6px_14px_rgba(22,163,74,0.18)]",
               "ring-1 ring-white/10",
               "transition-all duration-200",
               "hover:-translate-y-[1px] hover:shadow-[0_10px_18px_rgba(21,128,61,0.22)]",
               "active:translate-y-0 active:scale-[0.99]",
               "focus:outline-none focus:ring-2 focus:ring-emerald-300/40",
             ].join(" ")}
             title={language === "en" ? "Unlock full report" : "Vollbericht freischalten"}
             type="button"
           >
             <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.12),rgba(255,255,255,0.02))]" />
             <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/20" />

             <svg
               className="relative z-10 w-4 h-4 opacity-95 transition-transform duration-200 group-hover:scale-105"
               fill="none"
               stroke="currentColor"
               viewBox="0 0 24 24"
               aria-hidden="true"
             >
               <path
                 strokeLinecap="round"
                 strokeLinejoin="round"
                 strokeWidth={2}
                 d="M12 6V4m0 2a4 4 0 014 4v2m-8 0V10a4 4 0 014-4m-6 8h12v6H6v-6z"
               />
             </svg>

             <span className="relative z-10">
               {language === "en" ? "Unlock" : "Freischalten"}
             </span>
           </button>
         )}
      </div>

      {/* Titel in der Mitte */}
      <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2 header-title">
        <span className="text-white text-lg font-semibold hover:text-blue-100 transition-all duration-500 relative overflow-hidden group">
          <span className="relative z-20">VeChain</span>
          
          {/* ✨ Eleganter Glow-Effekt */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 opacity-0 group-hover:opacity-30 transition-all duration-700 rounded-lg blur-lg scale-110 group-hover:scale-100"></div>
          
          {/* 🌊 Sanfte Wellen-Animation */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-300 via-blue-400 to-blue-500 opacity-0 group-hover:opacity-20 transition-all duration-1000 rounded-lg blur-xl scale-125 group-hover:scale-110 group-hover:animate-[gentleWave_3s_ease-in-out_infinite]"></div>
          
          {/* 💎 Subtiler Glanz */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-200 via-blue-300 to-blue-400 opacity-0 group-hover:opacity-15 transition-all duration-1200 rounded-lg blur-2xl scale-150 group-hover:scale-120"></div>
        </span>
        <span className="text-blue-100 text-sm">•</span>
        <span className="text-blue-100 text-sm font-medium">Blockchain Analysis</span>
        {isPreviewMode && (
          <span
            className="text-white text-xs bg-amber-500/80 px-2 py-1 rounded-full ml-2 shadow-sm ring-1 ring-white/20 transition-all duration-200 hover:bg-amber-500 hover:brightness-110 hover:scale-[1.03]"
            title={language === "en" ? "Values masked" : "Werte maskiert"}
          >
            {language === "en" ? "Preview" : "Vorschau"}
          </span>
        )}
        <span className="text-blue-200 text-xs bg-blue-700 px-2 py-1 rounded-full ml-2">{APP_VERSION_LABEL}</span>
      </div>

      {/* Controls rechts - kompakter */}
      <div className="flex items-center gap-2">
        {/* User Account Status */}
        {user ? (
        <div className="relative">
          <div
            className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-lg cursor-pointer hover:bg-white/20 transition-all duration-200"
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          >
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-white text-xs font-medium">
              {user?.walletAddress?.slice(0, 6)}...{user?.walletAddress?.slice(-4)}
            </span>
            {/* 🆕 NEU: Zeige den aktuellen Plan mit PlanBadge */}
            <div className="flex items-center">
              <PlanBadge
                planId={user?.isDev ? "dev" : planToUse}
                size="xxs"
                variant="compact"
                title={currentPlanInfo?.label || currentPlanInfo?.name || ""}
              />
            </div>
            <svg
              className={`w-3 h-3 text-white ml-1 transition-transform duration-200 ${showUserDropdown ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* 🚀 GEILES BREITES DROPDOWN MODAL - MODERN UND PROFESSIONELL */}
          {showUserDropdown && (() => {
            // --- Account Dropdown: consistent typography / UI tokens ---
            const DD_LABEL = "text-[11px] font-semibold uppercase tracking-wider";
            const DD_VALUE = "text-sm font-semibold text-slate-900 leading-5";
            const DD_CARD =
              "bg-white border border-slate-200 rounded-xl p-3 shadow-sm hover:shadow-md hover:border-sky-300 transition-all duration-200";
            const DD_ICON_BTN =
              "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 " +
              "hover:bg-blue-100 hover:border-blue-300 transition-colors " +
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 " +
              "disabled:opacity-50 disabled:pointer-events-none";
            const DD_DOT =
              "w-2.5 h-2.5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full shadow-sm ring-2 ring-white";

            return (
              <div className="absolute right-0 mt-3 w-[460px] max-w-[95vw] bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
              {/* 🎨 GEILER HEADER MIT BESSEREN PROPORTIONEN */}
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50/20">
                {/* 🎯 GEILER PLAN-INDICATOR - BREITER UND ELEGANTER */}
                <div className="flex items-center gap-4 mb-5">
                  <div className="relative">
                    <PlanBadge
                      planId={user?.isDev ? "dev" : planToUse}
                      size="lg"
                      title={currentPlanInfo?.label || currentPlanInfo?.name || ""}
                      className="shrink-0"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="font-bold text-gray-800 text-xl mb-2">
                      {language === 'en' ? 'Connected Wallet' : 'Verbundene Wallet'}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">
                      {language === 'en' ? 'Premium Account' : 'Premium-Account'}
                    </div>
                  </div>
                </div>
                
                {/* ✅ Alles wie in App.jsx: Wallet-Adresse + Kacheln + Kaufdatum + Dev-Card im selben Block */}
                <div className="space-y-4">
                  {/* ✅ Wallet Address – one line + inline copy */}
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className={`${DD_LABEL} text-slate-500`}>
                      {language === "en" ? "Wallet Address" : "Wallet-Adresse"}
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-3">
                      <div className="overflow-x-auto flex-1 min-w-0">
                        <div
                          className="font-mono text-sm font-semibold text-slate-900 whitespace-nowrap"
                          title={user?.walletAddress || ""}
                        >
                          {user?.walletAddress}
                        </div>
                      </div>

                      <button
                        onClick={() => copyAddress(user?.walletAddress)}
                        className={`${DD_ICON_BTN} flex-shrink-0`}
                        title={language === "en" ? "Copy address" : "Adresse kopieren"}
                        aria-label={language === "en" ? "Copy address" : "Adresse kopieren"}
                        type="button"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Karten unterhalb der Wallet-Adresse */}
                  <div className="grid grid-cols-3 gap-3">
                    {/* User ID */}
                    <div className={DD_CARD}>
                      <div className={`${DD_LABEL} text-slate-500`}>
                        {language === "en" ? "User ID" : "User-ID"}
                      </div>

                      <div className="mt-1 flex items-center justify-between gap-2">
                        <div className={`${DD_VALUE} tabular-nums`}>{user?.userId ?? "-"}</div>

                        <button
                          onClick={() => user?.userId && copyAddress(String(user.userId))}
                          className={DD_ICON_BTN}
                          title={language === "en" ? "Copy user ID" : "User-ID kopieren"}
                          aria-label={language === "en" ? "Copy user ID" : "User-ID kopieren"}
                          type="button"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Plan */}
                    <div className={DD_CARD}>
                      <div className={`${DD_LABEL} text-blue-600`}>
                        {language === "en" ? "Plan" : "Plan"}
                      </div>
                      <div className={`${DD_VALUE} mt-1`}>
                        {currentPlanInfo?.label || currentPlanInfo?.name || user?.plan || "Free"}
                      </div>
                    </div>

                    {/* Status */}
                    <div className={DD_CARD}>
                      <div className={`${DD_LABEL} text-emerald-600`}>
                        {language === "en" ? "Status" : "Status"}
                      </div>

                      <div className="mt-1 flex items-center gap-2">
                        <span className={DD_DOT} />
                        <span className={DD_VALUE}>{language === "en" ? "Active" : "Aktiv"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Kaufdatum */}
                  {latestPlan?.purchaseDate && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 shadow-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className={`${DD_LABEL} text-emerald-700`}>
                          {language === "en" ? "Purchased" : "Gekauft"}
                        </span>

                        <span className="text-sm font-semibold text-emerald-900 tabular-nums">
                          {new Date(latestPlan.purchaseDate).toLocaleDateString(
                            language === "de" ? "de-DE" : "en-US"
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Dev Card */}
                  {user?.isDev && (
                    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-gradient-to-r from-blue-300 via-indigo-400 to-purple-400 rounded-xl p-3 shadow-sm hover:shadow-md transition-all duration-300 group">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                            <span className="text-white text-sm">👑</span>
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full opacity-20 blur-sm group-hover:opacity-30 transition-opacity"></div>
                        </div>

                        <div className="flex-1">
                          <div className="text-blue-700 font-bold text-sm group-hover:text-blue-800 transition-colors">
                            Admin
                          </div>
                          <div className="text-indigo-600 text-xs font-medium opacity-80">
                            {language === "en" ? "Premium Access" : "Premium-Zugriff"}
                          </div>
                        </div>

                        <div className="w-2.5 h-2.5 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full shadow-sm"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-4 pt-3 space-y-3">
                {/* Wallet trennen Button */}
                <button
                  onClick={logoutFromAnalysisAppOnly}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2.5 px-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {language === 'en' ? 'Disconnect Wallet' : 'Wallet trennen'}
                </button>
              </div>
            </div>
            );
          })()}
        </div>
        ) : (
          /* Offline/Disconnected Status */
          <button
            type="button"
            onClick={() => goToApp()}
            className="
              group flex items-center gap-2 h-8
              px-3 rounded-lg
              bg-white/10 border border-white/20
              hover:bg-white/20
              transition-all duration-200
              shadow-sm hover:shadow
            "
            aria-label={language === "en" ? "Login" : "Anmelden"}
            title={language === "en" ? "Login" : "Anmelden"}
          >
            <span className="text-white text-sm font-medium whitespace-nowrap">
              {language === "en" ? "Login" : "Login"}
            </span>

            {/* Link/Icon */}
            <svg className="w-4 h-4 text-white/90 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>

            {/* Chevron wie Language (rotiert wenn Modal offen) */}
            <svg
              className={`w-3 h-3 text-white/90 transition-transform duration-200 ${false ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}

        {/* Language Switcher - kompakt wie in App.jsx */}
        <button
          type="button"
          onClick={() => changeLanguage(language === 'de' ? 'en' : 'de')}
          className="px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors duration-200"
          aria-label={language === 'en' ? 'Switch language' : 'Sprache wechseln'}
        >
          {language?.toUpperCase?.() || 'EN'}
        </button>
      </div>
    </div>
                





    {/* Dashboard View */}
    {showDashboard && (
      <div className="mt-12">
        <Dashboard
          taxDataset={summaryDataset}
          vetTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const vetTotals = ds?.vetTotals || {};
            return {
              swapProfit: Number(vetTotals.disposalNetEUR || 0),
              otherIncome: Number(vetTotals.otherIncomeEUR || 0),
              total: Number(vetTotals.totalTaxableEUR || 0)
            };
          })()}
          vthoTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const vthoTotals = ds?.vthoTotals || {};
            return {
              swapProfit: Number(vthoTotals.disposalNetEUR || 0),
              otherIncome: Number(vthoTotals.otherIncomeEUR || 0),
              total: Number(vthoTotals.totalTaxableEUR || 0)
            };
          })()}
          b3trTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const b3trTotals = ds?.b3trTotals || {};
            return {
              swapProfit: Number(b3trTotals.disposalNetEUR || 0),
              otherIncome: Number(b3trTotals.otherIncomeEUR || 0),
              total: Number(b3trTotals.totalTaxableEUR || 0)
            };
          })()}
          nftTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const nftTotals = ds?.nftTotals || {};
            return Number(nftTotals.disposalEUR || 0); // NFT ist nur §23 EStG
          })()}
          exchangeTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const exchangeTotals = ds?.exchangeTotals || {};
            return {
              swapProfit: Number(exchangeTotals.disposalNetEUR || 0),
              otherIncome: Number(exchangeTotals.otherIncomeEUR || 0),
              total: Number(exchangeTotals.totalTaxableEUR || 0)
            };
          })()}
          liquidityTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const lq = ds?.liquidityTotals || {};

            // Backend-first, fallback auf FE-State (setLiquidityTotalProfit)
            const fallback =
              (typeof liquidityTotalProfit === "object" && liquidityTotalProfit)
                ? liquidityTotalProfit.swapProfit
                : Number(liquidityTotalProfit || 0);

            const n = Number(
              lq.disposalNetEUR ??
              lq.disposalProfitEUR ??
              lq.netProfitEUR ??
              fallback ??
              0
            );

            return { swapProfit: n, otherIncome: 0, total: n };
          })()}
          selectedCountry={selectedCountry}
          selectedYear={finalYear}
        />
      </div>
    )}

    {/* 🚀 Hero-Sektion (Logo/Slogan): per showAnalysisHero aktivierbar; Standard aus für Ergebnisfokus */}
    {showAnalysisHero && (
    <div className={`no-print mt-12 mb-1 transition-all duration-300 ${showDashboard ? 'hidden' : ''}`}>
      {/* 🌐 Web-Version - Hero-Sektion HEADER-BREITE */}
      <div className="relative overflow-hidden print:hidden -mx-6 px-6">
        {/* Hintergrund-Gradient - PERFEKTE VOLLBREITE */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-100 opacity-60"></div>
        
        {/* Subtile Animation - PERFEKTE VOLLBREITE */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 via-transparent to-blue-600/10 animate-pulse"></div>
        
        <div className="relative flex justify-between items-center py-8 px-6 max-w-[1600px] mx-auto">
          {/* Logo-Sektion mit Hover-Effekten */}
          <div className="w-[490px] relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700 scale-110 group-hover:scale-100"></div>
            <img
              src={
                shouldUseMobileBrandHeroLogo()
                  ? V3TAX_MOBILE_LOGO_PATH
                  : desktopLogoCopy
              }
              alt="V3Tax Logo"
              className="relative w-full h-auto object-contain transform scale-[0.95] translate-x-[200px] translate-y-[2px] group-hover:scale-[1.0] group-hover:translate-x-[210px] group-hover:translate-y-[0px] transition-all duration-500 ease-out drop-shadow-lg"
            />
          </div>

          {/* Slogan-Sektion mit krassen Effekten */}
          <div className="w-[620px] relative group">
            {/* Mehrschichtige Hover-Effekte */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-600/30 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-1000 scale-125 group-hover:scale-110"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-blue-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-80 transition-all duration-800 scale-110 group-hover:scale-105"></div>
            
            <img
              src={analysisHeader}
              alt="Experience the power of Web3 - VeChain Blockchain analysis"
              className="relative w-full h-auto object-contain transform scale-[1.10] translate-x-[-100px] translate-y-[20px] group-hover:scale-[1.15] group-hover:translate-x-[-10px] group-hover:translate-y-[15px] transition-all duration-500 ease-out drop-shadow-xl"
            />
          </div>
        </div>
        
        {/* Eleganter Schatten-Übergang */}
        <div className="h-2 bg-gradient-to-b from-transparent via-blue-100/30 to-transparent -mx-6"></div>
        
      </div>

    </div>
    )}

    {/* 🖨️ PDF-Version – Großes, zentriertes Logo */}
    <div id="pdf-header" className="print-only">
      <img src={desktopLogoCopy} alt="V3Tax Logo" className="pdf-logo" />
    </div>

    {/* 🖨️ Kompakter Report-Header direkt vor der ersten Tabelle */}
    <section className="print-only report-header" id="report-header">
      <div className="report-header__top">
        <img id="pdf-export-logo" src={desktopLogoCopy} alt="V3Tax" />
        <div>
          <h1>{language === 'en' ? `V3Tax Tax Report ${ukTaxYearLabelForPdf || finalYear}` : `V3Tax Steuerreport ${ukTaxYearLabelForPdf || finalYear}`}</h1>
          <div className="report-header__subtitle">{pdfVariantTitle}</div>
        </div>
      </div>

      <div className="report-header__grid">
        <div>
          <strong>{language === 'en' ? 'Report' : 'Report'}</strong>
          <div>Set-ID: {datasetId || '—'}</div>
          <div>Report-ID: {datasetId ? `${datasetId}-${finalYear}-${pdfVariantShort}` : `—-${finalYear}-${pdfVariantShort}`}</div>
          <div>{language === 'en' ? 'Created' : 'Erstellt'}: {new Date().toLocaleString()}</div>
        </div>

        <div>
          <strong>{language === 'en' ? 'Parameters' : 'Parameter'}</strong>
          <div>{language === 'en' ? 'Country' : 'Land'}: {selectedCountry || '—'}</div>
          <div>{language === 'en' ? 'Method' : 'Methode'}: {(() => {
            const ccUsed = String(backendData?.meta?.countryCode || selectedCountry || "DE").toUpperCase();
            const methodUsed = backendData?.meta?.costBasisMethod || (ccUsed === "IT" ? "LIFO" : ccUsed === "UK" || ccUsed === "GB" ? "UK_S104_POOLING" : "FIFO");
            return methodUsed;
          })()}</div>
          <div>{language === 'en' ? 'Currency' : 'Währung'}: {reportCurrencyLabel || (() => {
            const ccUsed = String(backendData?.meta?.countryCode || selectedCountry || "DE").toUpperCase();
            if (ccUsed === 'US' || ccUsed === 'USA') return 'USD';
            if (ccUsed === 'UK' || ccUsed === 'GB') return 'GBP';
            return 'EUR';
          })()}</div>
          <div>{language === 'en' ? 'Pricing methodology' : 'Kursmethodik'}: {priceSourceLabel}</div>
          {pricingMethodologyNote ? (
            <div className="report-pricing-note" style={{ fontSize: '0.85em', opacity: 0.85, marginTop: 4 }}>
              {pricingMethodologyNote}
            </div>
          ) : null}
        </div>

        <div>
          <strong>{language === 'en' ? 'Wallets' : 'Wallets'}</strong>
          <div>{language === 'en' ? 'Main:' : 'Haupt:'}</div>
          <ul className="report-wallets">
            {(walletSet?.mainAddresses || walletSet?.addresses || []).map(a => (
              <li key={`main-${a}`}><code>{a}</code></li>
            ))}
          </ul>

          {walletSet?.delegateAddresses?.length > 0 && (
            <>
              <div>Delegates:</div>
              <ul className="report-wallets">
                {walletSet.delegateAddresses.map(a => (
                  <li key={`delegate-${a}`}><code>{a}</code></li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      <div className="report-header__note">
        {language === 'en'
          ? 'Note: Line items may be rounded for readability; totals are based on unrounded calculation values and rounded only at the end.'
          : 'Hinweis: Einzelwerte können aus Darstellungsgründen gerundet sein; Summen basieren auf ungerundeten Rechenwerten und werden erst am Ende gerundet.'}
        {isUkCountryForPdf && (
          <>
            {' '}
            {language === 'en'
              ? 'This report covers VeWorld/Vechain activity only. Section 104 pooling with same-day and 30-day matching.'
              : 'Dieser Report deckt nur VeWorld/Vechain-Aktivität ab. Section-104-Pooling mit Same-Day- und 30-Day-Matching.'}
          </>
        )}
      </div>

    </section>

    {/* Optionales Wasserzeichen bei unverbindlichem Export */}
    {!isPurchased && <div className="print-only draft-watermark">ENTWURF</div>}

    {/* ✅ PDF Dashboard (Seite 1) – nur im Druck sichtbar */}
    <div id="pdf-dashboard" className="print-only">
      <div className="pdf-dashboard-print">
        <Dashboard
          pdfMode={true}
          taxDataset={summaryDataset}
          vetTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const vetTotals = ds?.vetTotals || {};
            return {
              swapProfit: Number(vetTotals.disposalNetEUR || 0),
              otherIncome: Number(vetTotals.otherIncomeEUR || 0),
              total: Number(vetTotals.totalTaxableEUR || 0),
            };
          })()}
          vthoTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const vthoTotals = ds?.vthoTotals || {};
            return {
              swapProfit: Number(vthoTotals.disposalNetEUR || 0),
              otherIncome: Number(vthoTotals.otherIncomeEUR || 0),
              total: Number(vthoTotals.totalTaxableEUR || 0),
            };
          })()}
          b3trTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const b3trTotals = ds?.b3trTotals || {};
            return {
              swapProfit: Number(b3trTotals.disposalNetEUR || 0),
              otherIncome: Number(b3trTotals.otherIncomeEUR || 0),
              total: Number(b3trTotals.totalTaxableEUR || 0),
            };
          })()}
          nftTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const nftTotals = ds?.nftTotals || {};
            return Number(nftTotals.disposalEUR || 0); // NFT ist nur §23 EStG
          })()}
          exchangeTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const exchangeTotals = ds?.exchangeTotals || {};
            return {
              swapProfit: Number(exchangeTotals.disposalNetEUR || 0),
              otherIncome: Number(exchangeTotals.otherIncomeEUR || 0),
              total: Number(exchangeTotals.totalTaxableEUR || 0),
            };
          })()}
          liquidityTotalProfit={(() => {
            const ds = backendData?.analysisResult?.dataset || backendData?.dataset;
            const lq = ds?.liquidityTotals || {};

            // Backend-first, fallback auf FE-State (setLiquidityTotalProfit)
            const fallback =
              (typeof liquidityTotalProfit === "object" && liquidityTotalProfit)
                ? liquidityTotalProfit.swapProfit
                : Number(liquidityTotalProfit || 0);

            const n = Number(
              lq.disposalNetEUR ??
              lq.disposalProfitEUR ??
              lq.netProfitEUR ??
              fallback ??
              0
            );

            return { swapProfit: n, otherIncome: 0, total: n };
          })()}
          selectedCountry={selectedCountry}
          selectedYear={finalYear}
        />

        {/* 📄 Hinweis zur steuerlichen Verwendung / Legal Disclaimer (nur im PDF) */}
        <div className="pdf-legal-disclaimer">
          {String(selectedCountry || '').toUpperCase() === 'US' || language === 'en' ? (
            <>
              <div className="pdf-legal-disclaimer__title">Legal disclaimer</div>
              <div className="pdf-legal-disclaimer__text">
                This report is provided for informational record-keeping purposes and does not constitute legal or tax advice. Tax treatment may depend on the user's individual circumstances and applicable reporting requirements.
                {String(selectedCountry || '').toUpperCase() === 'US' ? (
                  <>
                    <br />
                    Cost basis is maintained separately for each wallet or account. Historical lots are assigned to the wallet or account in which they originated. Users who previously applied a different cross-wallet method should review their opening allocation.
                  </>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <div className="pdf-legal-disclaimer__title">Hinweis zur steuerlichen Verwendung</div>
              <div className="pdf-legal-disclaimer__text">
                Dieser Bericht dient ausschließlich der privaten Vorbereitung auf Ihre persönliche Steuererklärung und ersetzt keine steuerliche Beratung im Sinne des § 3 StBerG (Steuerberatungsgesetz).
                <br />
                Die Berechnungen (z. B. FIFO-Gewinnermittlung, Klassifizierungen, Haltefristen) beruhen auf den in der Anwendung gesetzten Parametern, den vorliegenden Transaktionsdaten sowie öffentlich verfügbaren Kursquellen.
                <br />
                Jegliche Haftung für Vollständigkeit, Richtigkeit oder steuerliche Anwendbarkeit im Einzelfall wird ausgeschlossen. Bitte lassen Sie die Angaben vor Verwendung in einer Steuererklärung durch eine Steuerberaterin oder einen Steuerberater bzw. das zuständige Finanzamt prüfen.
              </div>
            </>
          )}
        </div>

      </div>
    </div>

    {/* Sticky Edit Bar: Save & Recompute */}
    {isEditMode && (
      <div className="fixed bottom-4 left-1/2 z-50 w-[min(920px,calc(100vw-32px))] -translate-x-1/2 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-2xl backdrop-blur no-print">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">
              {language === "en" ? "Manual edit mode" : "Manueller Bearbeitungsmodus"}
            </div>
            <div className="text-xs text-slate-500">
              {language === "en"
                ? "Changes apply only after saving. Standard recalculation keeps saved manual edits."
                : "Änderungen gelten erst nach Speichern. Standard-Neuberechnung behält gespeicherte manuelle Korrekturen."}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={cancelManualEdits}
              disabled={isSavingManualOverrides || isResettingManualOverrides}
            >
              {language === "en" ? "Cancel" : "Abbrechen"}
            </button>

            {savedManualOverrideCount > 0 && (
              <button
                type="button"
                className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={resetAllManualOverridesAndRecalculate}
                disabled={isSavingManualOverrides || isResettingManualOverrides}
              >
                {isResettingManualOverrides
                  ? language === "en"
                    ? "Resetting…"
                    : "Zurücksetzen…"
                  : language === "en"
                    ? `Reset all saved (${savedManualOverrideCount})`
                    : `Alle gespeicherten zurücksetzen (${savedManualOverrideCount})`}
              </button>
            )}

            <button
              type="button"
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={saveManualOverridesAndRecalculate}
              disabled={isSavingManualOverrides || isResettingManualOverrides || !hasPendingManualOverrides}
            >
              {isSavingManualOverrides
                ? language === "en"
                  ? "Saving & recalculating…"
                  : "Speichern & Neuberechnung…"
                : language === "en"
                  ? `Save & recalculate (${Object.keys(pendingManualOverrides).length})`
                  : `Speichern & neu berechnen (${Object.keys(pendingManualOverrides).length})`}
            </button>
          </div>
        </div>
      </div>
    )}

{!showDashboard && (
<div className={analysisShell}>
<div className={analysisContent}>
<div className={analysisStack}>
<div className="w-full min-w-0 pt-1 lg:pt-2">
  <TotalIncomeSummary {...totalIncomeSummaryProps} />
</div>


{/* 🔍 Balance Verifier Multi-Token - Erste Tabelle nach Set-ID/Steuerjahr */}
{/* ✅ FIX G: Container mit min-height für sichtbare Darstellung */}
{/* Balance Verifier: Hooks oben in AnalysisApp; hier nur JSX (renderBalanceVerifierPanel) */}
<div className="print:hidden min-h-[260px] w-full min-w-0">
  {renderBalanceVerifierPanel()}
</div>

{/* Legacy BalanceVerifier (Einzel-Token) entfernt: nur noch BalanceVerifierMulti oben */}

{/* ✅ Hier korrekt eingefügt – keine Schleife! */}

      {/* 🚀 NEU: Multi-Wallet Rewards Overview - Konsolidierte Übersicht aller Rewards */}
      <div className="print:hidden w-full min-w-0">
        <MultiVthoOverview
          datasetId={datasetId}
          year={finalYear}
          walletSet={walletSet}
          dataset={backendData?.analysisResult?.dataset || backendData?.dataset}
          balanceVerifier={
            backendData?.analysisResult?.balanceVerifier ||
            backendData?.balanceVerifier
          }
          selectedCountry={selectedCountry}
          language={language}
        />
      </div>
      
      {/* 🧪 Preview Banner */}
      {!isPreviewMode && !showDashboard && savedManualOverrideCount > 0 && !isEditMode && (
        <div className="print:hidden mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-semibold">
                {manualOverrideActiveLabel(savedManualOverrideCount, language)}
              </div>
              <div className="mt-1 text-xs text-amber-800/90">
                {language === "en"
                  ? "Standard recalculation (Sync + Calculate Set) keeps these corrections. Use reset only if you want pure auto-classification again."
                  : "Standard-Neuberechnung (Sync + Set berechnen) behält diese Korrekturen. Reset nur, wenn du wieder reine Auto-Klassifizierung willst."}
              </div>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-100 disabled:opacity-50"
              onClick={resetAllManualOverridesAndRecalculate}
              disabled={isAnalyzing || isResettingManualOverrides || isSavingManualOverrides}
            >
              {isResettingManualOverrides
                ? language === "en"
                  ? "Resetting & recalculating…"
                  : "Zurücksetzen & neu berechnen…"
                : language === "en"
                  ? "Reset manual edits & recalculate"
                  : "Manuelle Änderungen zurücksetzen & neu berechnen"}
            </button>
          </div>
        </div>
      )}

      {isPreviewMode && (
        <div className="print:hidden mb-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <span className="font-semibold">{language === 'en' ? 'Preview mode:' : 'Vorschau-Modus:'}</span>{' '}
          {language === 'en' 
            ? 'Sensitive EUR calculations are hidden. Buy to unlock full tax tables + PDF/CSV export.'
            : 'Sensible EUR-Berechnungen sind ausgeblendet. Kaufe, um vollständige Steuertabellen + PDF/CSV-Export freizuschalten.'
          }
        </div>
      )}
      
      {/* 🚀 NEU: Exchange Imports Table */}
      <section
        className={cn(
          "w-full min-w-0",
          pdfSectionClass(hasExchangeData, "report-section--exchangeimports"),
          isFirstCalc("exchange") && "report-section--first",
          !hasExchangeData && "pdf-skip-empty"
        )}
      >
        <ExchangeImportsTable
          datasetId={datasetId}
          year={finalYear}
          isPreviewMode={isPreviewMode}
          previewMaskedTotals={backendData?.preview?.maskedTotals?.sections?.exchange}
          exchangeImports={exchangeImports}
          exchangeTotals={exchangeTotals}
          exchangeTaxRows={exchangeTaxRows}
          selectedCountry={selectedCountry}
          pdfExportMode={false}
          onDataReady={setExchangeExportData}
        />
      </section>

      {/* ✅ ENTFERNT: VetVthoTransactionTable - Ersetzt durch RawTransfersTable */}
      {/* <VetVthoTransactionTable
  datasetId={datasetId}
  walletAddress={walletAddress} // 🚀 NEU: Wallet-Adresse übergeben
  isEditMode={isEditMode}
  // 🚀 NEU: Verwende Backend-Daten wenn verfügbar (klassifizierte Daten)
  transactions={backendData?.dataset?.vetVthoData || vetVthoData}
  salePaymentTransactions={(backendData?.dataset?.vetVthoData || vetVthoData).filter(tx => 
    ["sale", "sale_payment"].includes((tx.classification || "").toLowerCase())
  )}
  onEditChange={() => {}} // 🎯 VEREINFACHT: Keine State-Überschreibung mehr
  onEditDetected={handleEditDetected}
  manualClassifications={manualClassifications}
  onClassificationChange={handleClassificationChange}
  year={finalYear}
  nftMints={allMatchedNfts}
  pdfExportMode={false}
  onDataReady={setVetVthoExportData}
  loadFromSnapshot={loadFromSnapshot} // 🚀 NEU: Einheitlicher Modus
  onDataLoaded={(loadedData) => {
    // 🚫 BLOCKIERE alle API-Aufrufe wenn Backend-Daten oder Snapshot geladen wurde
    if (backendData || loadFromSnapshot) {
            return;
    }
    
    
    // 🔧 WICHTIG: Prüfe ob die State-Variablen bereits Swap-Klassifizierungen haben
    const stateHasSwapClassifications = vetVthoData.some(tx => 
      tx.classification === "swap_in" || tx.classification === "swap_out"
    );
    
    // 🔧 WICHTIG: Wende Swap-Klassifizierungen immer an, wenn globalSwapClassificationMap verfügbar ist
    const swapMapReady = !!window.globalSwapClassificationMap;

    const parsed = loadedData.map(tx => {
      const safeDate = tx.date && tx.date !== "--" ? tx.date : "2025-01-01";
      const fallbackParsed = parseDateToTimestamp(safeDate);
      const fallbackRobust = robustParseDateToTimestamp(safeDate);
      const finalTimestamp =
        typeof tx.timestamp === "number" && tx.timestamp > 1000000000
          ? tx.timestamp
          : !isNaN(fallbackParsed) && fallbackParsed > 1000000000
            ? fallbackParsed
            : fallbackRobust;

      const directionLower = (tx.direction || tx.type || "").toLowerCase();
      const type = directionLower === "empfangen"
        ? "received"
        : directionLower === "gesendet"
          ? "sent"
          : tx.type;

      const sender = type === "received" ? tx.sender || "0xunknown" : walletAddress;
      const recipient = type === "sent" ? tx.recipient || "0xunknown" : walletAddress;

      const hash = tx.hash || tx.txid || tx.key || "unknown-hash";
      
      // 🔧 WICHTIG: Wende Swap-Klassifizierungen direkt an
      let classification = tx.classification || "";
      if (swapMapReady) {
        const swapClassification = window.getSwapClassification(hash, tx.token, tx.from || tx.sender, tx.to || tx.recipient);
        
        
        if (swapClassification) {
          classification = swapClassification;
        } else {
          // 🔧 DEBUG: Nur bei ersten 3 Fehlern loggen um Spam zu vermeiden
          if (window.vetVthoHashNotFoundCount === undefined) window.vetVthoHashNotFoundCount = 0;
          if (window.vetVthoHashNotFoundCount < 3) {
                        window.vetVthoHashNotFoundCount++;
          }
        }
      }

return {
  ...tx,
  token: sanitizeTokenName(tx.token),
  amount: (() => {
    const a = tx.amount_display ?? tx.amountDisplay ?? tx.amount;
    const n = Number(a);
    return Number.isFinite(n) ? n : 0;
  })(), // 🟢 WICHTIG FÜR FIFO! ✅ FIX: amount_display bevorzugen (human-readable)
  timestamp: finalTimestamp || 0,
  hash,
  type,
  sender,
  recipient,
  classification,
};
    });



    const parsedHashes = new Set(parsed.map(tx => tx.hash));

    setVetVthoData(prev => {
      const prevByHash = new Map(prev.map(tx => [tx.hash, tx]));

      // 🔧 WICHTIG: Behalte Swap-Klassifizierungen aus der ersten Ausführung
      const existingSwapClassifications = new Map();
      prev.forEach(tx => {
        if (tx.classification && ["swap_in", "swap_out"].includes(tx.classification)) {
          existingSwapClassifications.set(tx.hash, tx.classification);
        }
      });
      
      

      // 🛡️ Sammle sale_payment Transaktionen aus den ursprünglichen Daten
const existingSalePayments = prev.filter(tx => 
  ["sale", "sale_payment"].includes((tx.classification || "").toLowerCase())
);



const referencedMintHashes = new Set(
  parsed
    .filter(tx => tx.classification === "sale_payment" && tx.relatedMintHash)
    .map(tx => tx.relatedMintHash)
);

const additionalMintsFromSales = [...referencedMintHashes]
  .filter(hash => !parsedHashes.has(hash))
  .map(hash => prevByHash.get(hash))
  .filter(Boolean);

// 🛡️ Kombiniere parsed Daten mit sale_payment Transaktionen (Duplikate vermeiden)
const parsedHashesSet = new Set(parsed.map(tx => tx.hash));
const additionalMintsHashesSet = new Set(additionalMintsFromSales.map(tx => tx.hash));

const uniqueExistingSalePayments = existingSalePayments.filter(tx => 
  !parsedHashesSet.has(tx.hash) && !additionalMintsHashesSet.has(tx.hash)
);

// 🔧 WICHTIG: Wende Swap-Klassifizierungen auf die neuen Daten an
const parsedWithSwapClassifications = parsed.map(tx => {
  const existingSwapClassification = existingSwapClassifications.get(tx.hash);
  
  if (existingSwapClassification) {
    // 🔍 DEBUG: Swap-Klassifizierung beibehalten Log entfernt
    return {
      ...tx,
      classification: existingSwapClassification
    };
  }
  return tx;
});

const final = [...parsedWithSwapClassifications, ...additionalMintsFromSales, ...uniqueExistingSalePayments];


      // ✅ Wichtiger Teil für FIFO-Recalculate
      const vetOnly = final.filter(tx => sanitizeTokenName(tx.token) === "VET");
      const vthoOnly = final.filter(tx => sanitizeTokenName(tx.token) === "VTHO");

      setVetTxs(vetOnly);
      setVthoTxs(vthoOnly);

      return final.map(tx => {
        const enriched = prevByHash.get(tx.hash);
        
        // 🛡️ sale_payment Klassifizierung NICHT überschreiben!
        const currentClassification = tx.classification || "";
        const enrichedClassification = enriched?.classification || "";
        
        let finalClassification = currentClassification;
        
        if (["sale", "sale_payment"].includes(currentClassification.toLowerCase())) {
          // sale/sale_payment NICHT überschreiben
          finalClassification = currentClassification;
        } else if (currentClassification.toLowerCase() === "stargate_staking_rewards") {
          // Stargate_Staking_rewards NICHT überschreiben
          finalClassification = currentClassification;
        } else if (enrichedClassification?.startsWith("NFT_Stargate_")) {
          // Stargate-Klassifizierung beibehalten
          finalClassification = enrichedClassification;
        } else if (enrichedClassification && !currentClassification) {
          // Nur wenn keine aktuelle Klassifizierung vorhanden ist
          finalClassification = enrichedClassification;
        }
        
        return {
          ...tx,
          matchedInflows: enriched?.matchedInflows || tx.matchedInflows || [],
          classification: finalClassification,
        };
      });
      
      // 🔍 DEBUG: Überprüfe sale_payment in VetVthoTransactionTable onDataLoaded
      const salePaymentsInFinal = final.filter(tx => 
        ["sale", "sale_payment"].includes((tx.classification || "").toLowerCase())
      );
            
      if (salePaymentsInFinal.length > 0) {
                console.table(salePaymentsInFinal.map(tx => ({
          hash: tx.hash?.slice(0, 8) + "…",
          token: tx.token,
          classification: tx.classification,
          type: tx.type,
          amount: tx.amount,
          timestamp: tx.timestamp
        })));
      }
      
      // 🛡️ SCHUTZ: Markiere Swap-Klassifizierungen als angewendet
      window.vetVthoSwapClassificationsApplied = true;
          });
  }}
/> */}

      {/* ✅ NEU: Unified Raw Transfers Table */}
      <section
        className={cn(
          "w-full min-w-0",
          pdfSectionClass(hasRawTransfers, "report-section--rawtransfers"),
          // Anlage 2: Rawtransfers ist die erste und einzige gedruckte Tabelle -> immer als first markieren
          "report-section--first",
          !hasRawTransfers && "pdf-skip-empty"
        )}
      >
        <RawTransfersTable
          year={finalYear}
          selectedCountry={selectedCountry}
          vetVthoTxs={rawVetVthoTxs}
          vip180Txs={rawVip180Txs}
          manualClassifications={manualClassifications}
          isEditMode={isEditMode}
          isPreviewMode={isPreviewMode}
          onToggleEditMode={toggleEditMode}
          pendingOverrideCount={Object.keys(pendingManualOverrides || {}).length}
          savedOverrideCount={savedManualOverrideCount}
          onClassificationChange={handleClassificationChange}
          onEditDetected={handleEditDetected}
          pdfExportMode={false}
          pdfExportVariant={taxReportPdfVariant}
        />
      </section>


<section className={cn("w-full min-w-0", pdfSectionClass(hasVetTax), "report-section--lazy-paint", isFirstCalc("vet") && "report-section--first", !hasVetTax && "pdf-skip-empty")}>
  <VetTaxTable
    datasetId={datasetId}
    year={finalYear}
    country={selectedCountry}
    isPreviewMode={isPreviewMode}
    previewMaskedTotals={backendData?.preview?.maskedTotals?.sections?.vet}
    previewMaskedTotalEUR={backendData?.preview?.maskedTotals?.sections?.vet?.total}
    vetDataFromProps={vetTaxDataFromPropsForTable}
    vetTotalsFromProps={vetTaxTotalsFromPropsForTable}
    onTotalProfitCalculated={setVetTotalProfit}
    onDataReady={onVetTaxTableDataReady}
    enableExportCallback={exportCallbacksEnabled}
    pdfExportMode={false}
    isEditMode={isEditMode}
    selectedCountry={selectedCountry}
    loadFromSnapshot={loadFromSnapshot}
    key={`vet-${datasetId ?? "preview"}-${finalYear}`}
  />
</section>

<section className={cn("w-full min-w-0", pdfSectionClass(hasVthoTax), "report-section--lazy-paint", isFirstCalc("vtho") && "report-section--first", !hasVthoTax && "pdf-skip-empty")}>
  <VthoTaxTable
    datasetId={datasetId}
    year={finalYear}
    country={selectedCountry}
    isPreviewMode={isPreviewMode}
    previewMaskedTotals={backendData?.preview?.maskedTotals?.sections?.vtho}
    previewMaskedTotalEUR={backendData?.preview?.maskedTotals?.sections?.vtho?.total}
    key={`vtho-${datasetId ?? "preview"}-${finalYear}`}
    vthoDataFromProps={vthoTaxDataFromPropsForTable}
    vthoTotalsFromProps={vthoTaxTotalsFromPropsForTable}
    onTotalProfitCalculated={setVthoTotalProfit}
    onDataReady={onVthoTaxTableDataReady}
    enableExportCallback={exportCallbacksEnabled}
    pdfExportMode={false}
    isEditMode={isEditMode}
    nonTaxableInflows={nonTaxableInflows}
    onNonTaxableInflowChange={handleNonTaxableInflowChange}
    selectedCountry={selectedCountry}
    loadFromSnapshot={loadFromSnapshot}
  />
</section>

{/* ✅ ENTFERNT: Vip180TokenTable - Ersetzt durch RawTransfersTable */}
{/* <section className="report-section">
  <Vip180TokenTable
    datasetId={datasetId}
    walletAddress={walletAddress} // 🚀 NEU: Wallet-Adresse übergeben
    veDelegateWallet={veDelegateWallet} // 🚀 NEU: Delegate-Wallet übergeben
    year={finalYear} // 🚀 NEU: Verwende finalYear für Konsistenz
    selectedCountry={selectedCountry} // 🚀 NEU: Verwende selectedCountry aus App.jsx
    // 🚀 NEU: Nur noch anzeigen - Backend-Daten verwenden
    transactions={backendData?.dataset?.vip180Data || vip180Data}
    swaps={backendData?.dataset?.swaps || detectedSwaps}
    loadFromSnapshot={loadFromSnapshot} // 🚀 NEU: Blockt interne Fetches/FE-Logik
    pdfExportMode={false}
    onDataReady={setVip180Export} // ✅ CSV-Export aktivieren
  />
</section> */}

<section className={cn("w-full min-w-0", pdfSectionClass(hasB3trTax), "report-section--lazy-paint", isFirstCalc("b3tr") && "report-section--first", !hasB3trTax && "pdf-skip-empty")}>
  <B3trTaxTable
    datasetId={datasetId}
    year={finalYear}
    isPreviewMode={isPreviewMode}
    previewMaskedTotals={backendData?.preview?.maskedTotals?.sections?.b3tr}
    previewMaskedTotalEUR={backendData?.preview?.maskedTotals?.sections?.b3tr?.total}
    key={`b3tr-${datasetId ?? "preview"}-${finalYear}`}
    // Backend first, sonst leer
    b3trDataFromProps={b3trTaxDataFromPropsForTable}
    b3trTotalsFromProps={b3trTaxTotalsFromPropsForTable}
    onTotalProfitCalculated={setB3trTotalProfit}
    onDataReady={onB3trTaxTableDataReady}
    enableExportCallback={exportCallbacksEnabled}
    pdfExportMode={false}
    isEditMode={isEditMode}
    selectedCountry={selectedCountry}
  />
</section>

<section className={cn("w-full min-w-0", pdfSectionClass(hasLiquidityTax), "report-section--lazy-paint", isFirstCalc("liquidity") && "report-section--first", !hasLiquidityTax && "pdf-skip-empty")}>
  <LiquidityTaxTable
    year={finalYear}
    selectedCountry={selectedCountry}
    isPreviewMode={isPreviewMode}
    previewMaskedTotals={backendData?.preview?.maskedTotals?.sections?.liquidity}
    previewMaskedTotalEUR={backendData?.preview?.maskedTotals?.sections?.liquidity?.total}
    liquidityDataFromProps={backendData?.dataset?.liquidityTaxRows || backendData?.analysisResult?.dataset?.liquidityTaxRows || []}
    liquidityTotalsFromProps={backendData?.dataset?.liquidityTotals || backendData?.analysisResult?.dataset?.liquidityTotals || {}}
    onTotalProfitCalculated={setLiquidityTotalProfit}
    onDataReady={onLiquidityTaxTableDataReady}
    enableExportCallback={exportCallbacksEnabled}
    pdfExportMode={false}
    isEditMode={isEditMode}
  />
</section>

<section className={cn("w-full min-w-0", pdfSectionClass(hasNftTax), "report-section--lazy-paint", isFirstCalc("nft") && "report-section--first", !hasNftTax && "pdf-skip-empty")}>
  <NftTaxTable
    datasetId={datasetId}
    year={finalYear}
    selectedCountry={selectedCountry}
    isPreviewMode={isPreviewMode}
    previewMaskedTotals={backendData?.preview?.maskedTotals?.sections?.nft}
    previewMaskedTotalEUR={backendData?.preview?.maskedTotals?.sections?.nft?.total}
    isEditMode={isEditMode}
    nftDataFromProps={summaryDataset?.nftTaxRows || []}
    nftAcquisitionRowsFromProps={
      (summaryDataset?.nftAcquisitionRows?.length
        ? summaryDataset.nftAcquisitionRows
        : backendData?.analysisResult?.dataset?.nftAcquisitionRows) || []
    }
    nftTotalsFromProps={summaryDataset?.nftTotals || {}}
    nonTaxableSales={nonTaxableSales}
    onNonTaxableSaleChange={handleNonTaxableSaleChange}
    onManualNftDataChange={handleManualNftDataChange}
    manualNftData={manualNftData}
    onSaveNftTransformFmv={handleSaveNftTransformFmv}
    onDataReady={onNftTaxTableDataReady}
    enableExportCallback={exportCallbacksEnabled}
    loadFromSnapshot={loadFromSnapshot}
    pdfExportMode={false}
    key={`nft-${datasetId ?? "preview"}-${finalYear}`}
  />
</section>

</div>
</div>
</div>
)}

{/* ExportAllSection - Immer sichtbar, auch im Dashboard-Modus */}
{/* Container für ExportAllSection mit Hinweis */}
<div className="relative">
  {/* Export Hint links neben der ExportAllSection */}
  {showExportHint && (
    <div className="export-hint-container absolute -left-4 top-0 transform -translate-x-full bg-blue-600 text-white px-5 py-4 rounded-xl shadow-2xl z-50 w-80 mr-4">
      <div className="flex items-start gap-3">
        <div className="text-2xl">💡</div>
        <div className="flex-1">
          <div className="font-semibold text-base mb-2">
            {language === 'en' ? 'Export section reached!' : 'Export-Bereich erreicht!'}
          </div>
          <div className="text-sm opacity-90 leading-relaxed">
            {language === 'en' 
              ? 'Here you can find all export options. Choose between different CSV files or download the complete PDF report.'
              : 'Hier findest du alle Export-Optionen. Wähle zwischen verschiedenen CSV-Dateien oder lade den kompletten PDF-Report herunter.'
            }
          </div>
        </div>
      </div>
      {/* Pfeil zeigt zur Export-Section (nach rechts) */}
      <div className="absolute right-0 top-6 transform translate-x-2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-blue-600"></div>
    </div>
  )}

<ExportAllSection
  vetData={vetTaxTableData}
  vthoData={vthoTaxTableData}
  b3trData={b3trTaxTableData}
  nftData={nftTaxTableData}
  liquidityData={liquidityTaxTableData}
  vip180Data={exportPrepEnabled ? vip180ExportComputed : []}
  vetVthoData={exportPrepEnabled ? vetVthoExportComputed : []}
  walletSet={walletSet}
  onExportPrepNeeded={enableExportPrep}
  deferExportPrep={deferExportPrep}
  exchangeData={
    exchangeExportData.length > 0
      ? exchangeExportData
      : (
          backendData?.analysisResult?.dataset?.exchangeImports ||
          backendData?.dataset?.exchangeImports ||
          []
        )
  }
  exchangeTaxRows={
    backendData?.analysisResult?.dataset?.exchangeTaxRows ||
    backendData?.dataset?.exchangeTaxRows ||
    []
  }
  taxDataset={resolveAnalysisDataset(backendData)}
  selectedCountry={selectedCountry}
  year={finalYear}
  onPrintVariant={handlePrintVariantChange}
  isPreviewMode={isPreviewMode}
/>

</div>

        {/* Footer - Immer am Ende, nach ExportAllSection */}
        <div className="mt-2 sm:mt-3 lg:mt-5 print:mt-0">
          <Footer
            onOpenSupport={() => {
              setHelpActiveSection("support");
              setShowHelpModal(true);
            }}
          />
        </div>

{/* Navigation Buttons - Hoch/Runter */}
{showScrollToTop && (
  <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-50">
    {/* Nach oben Button */}
    <button
      onClick={() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110"
      title={language === 'en' ? 'Back to top' : 'Nach oben'}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>
    
    {/* Nach unten zum Export Button */}
    <button
      onClick={() => {
        document.querySelector('#pdf-export-content')?.scrollIntoView({behavior: 'smooth', block: 'end'});
      }}
      className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110"
      title={language === 'en' ? 'To export section' : 'Zum Export-Bereich'}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    </button>
  </div>
)}

    </AnalysisCrashBoundary>
    </div>
    ) : null}
    </div>
  </div>

  <HelpSupportModal
    open={showHelpModal}
    onClose={handleHelpModalClose}
    language={language}
    initialSection={helpActiveSection}
    initialCategory={supportForm.category}
  />

  <LegalNoticesModal
    open={showLegalModal}
    onClose={() => setShowLegalModal(false)}
    language={language}
  />

  <MyWalletsDashboard
    isOpen={showMyWalletsDashboard}
    onClose={() => setShowMyWalletsDashboard(false)}
    onOpenSupport={(onSupportClose) => {
      setShowHelpModal(true);
      setHelpActiveSection("support");
      setSupportForm((prev) => ({
        ...prev,
        category: "CSV Import",
      }));
      window._supportModalCloseHandler = () => {
        setShowHelpModal(false);
        onSupportClose?.();
        window._supportModalCloseHandler = null;
      };
    }}
    userWallet={walletAddress}
  />

  <ToastContainer
    position="top-right"
    autoClose={3000}
    hideProgressBar={false}
    newestOnTop={false}
    closeOnClick
    rtl={false}
    pauseOnFocusLoss
    draggable
    pauseOnHover
    theme="light"
  />

  {isTaxReportPrintMounted && uiPhase === "ready" && backendData && (
    <TaxReportPrint
      language={language}
      pdfVariant={taxReportPdfVariant}
      datasetId={datasetId}
      finalYear={finalYear}
      selectedCountry={selectedCountry}
      walletSet={walletSet}
      backendData={backendData}
      isPurchased={isPurchased}
      manualClassifications={manualClassifications}
      nonTaxableSales={nonTaxableSales}
      manualNftData={manualNftData}
      handleNonTaxableSaleChange={handleNonTaxableSaleChange}
      handleManualNftDataChange={handleManualNftDataChange}
      rawVetVthoTxs={rawVetVthoTxs}
      rawVip180Txs={rawVip180Txs}
      loadFromSnapshot={loadFromSnapshot}
    />
  )}
  </>
);
}

export default App;
